import React, { useEffect, useState, useMemo, useRef, Suspense } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import Header from '../components/layout/Header';
import Modal from '../components/common/Modal';
import ConfirmModal from '../components/common/ConfirmModal';
import AddressAutocomplete from '../components/common/AddressAutocomplete';
import AddProviderModal from '../components/common/AddProviderModal';
import { useAuth } from '../context/AuthContext';
import { useMobile } from '../context/MobileContext';
import useOCR from '../hooks/useOCR';

var CaseCommunicationTab = React.lazy(function() { return import('../components/CaseCommunicationTab'); });
var CaseSigningPanel = React.lazy(function() { return import('../components/CaseSigningPanel'); });
var CaseCivilBillingTab = React.lazy(function() { return import('../components/CaseCivilBillingTab'); });
import {
  getCase,
  getCaseBilling, addBillingItem, updateBillingItem, deleteBillingItem,
  getWcBilling, addWcItem, updateWcItem, deleteWcItem,
  renameDocument,
  updateCase,
  addCaseNote,
  updateCaseNote,
  addTreatment,
  getAttorneys,
  updateClient,
  uploadCaseDocument,
  upsertCaseFieldNote,
  getCustomLists,
  getGeneratedDocument,
  deleteCase,
  getStaff,
  getCivilDetails,
  saveCivilDetails,
  getCareProviders,
  createCareProvider,
  softDeleteDocument,
  restoreDocument,
  permanentDeleteDocument,
  purgeDocumentTrash,
  getDirectApiUrl,
  addConsultation,
  patchConsultation,
  deleteConsultation,
} from '../utils/api';
import { formatCurrency, formatDate, timeAgo } from '../utils/helpers';
import { ArrowLeft, Save, Send, Plus, MessageSquare, Stethoscope, FileText, UploadCloud, X, Trash2, ChevronDown, Eye, EyeOff, Printer, Bold, Italic, Palette, Pencil, Search, AlertTriangle, Calendar, ClipboardList, Phone, Mail, RotateCcw, Archive, CheckCircle, Clock, XCircle, Scan, Sparkles, ChevronRight } from 'lucide-react';
import toast from 'react-hot-toast';

const DEFAULT_STATUSES = ['intake','active','pending','closed','settled','disengage'];

const CLAIMANT_FIELDS = [
  'name','address','mailing_address','city','state','zip','county','phone','phone_alt','email','ssn',
  'date_of_birth','sex','language',
];

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC',
];

const DOCUMENT_CATEGORIES = [
  { value: 'medical_records', label: 'Medical Records' },
  { value: 'billing', label: 'Billing' },
  { value: 'imaging', label: 'Imaging' },
  { value: 'pharmacy', label: 'Pharmacy' },
  { value: 'correspondence', label: 'Correspondence' },
  { value: 'forms', label: 'Forms' },
  { value: 'other', label: 'Other' },
];


const FIELD_NOTE_SPECS = [
  { source_field: 'claimant_notes', label: 'Claimant Notes', get: (s) => s?.form?.description || '' },
  { source_field: 'insurance_notes', label: 'Insurance Notes', get: (s) => s?.ins?.notes || '' },
  { source_field: 'legal_notes', label: 'Legal Notes', get: (s) => s?.form?.attorney_notes_status || '' },
  { source_field: 'benefit_notes', label: 'Benefit Notes', get: (s) => s?.form?.benefit_notes || '' },
  { source_field: 'employer_notes', label: 'Employer Notes', get: (s) => s?.emp?.notes || '' },
  { source_field: 'diagnoses_notes', label: 'Notes / Diagnoses', get: (s) => s?.form?.notes_diagnoses || '' },
];

// Phone formatting: (XXX) XXX-XXXX
function formatPhoneValue(raw) {
  const digits = (raw || '').replace(/\D/g, '').slice(0, 10);
  if (digits.length === 0) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0,3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`;
}

function formatSSNValue(raw) {
  const digits = (raw || '').replace(/\D/g, '').slice(0, 9);
  if (digits.length === 0) return '';
  if (digits.length <= 3) return digits;
  if (digits.length <= 5) return `${digits.slice(0,3)}-${digits.slice(3)}`;
  return `${digits.slice(0,3)}-${digits.slice(3,5)}-${digits.slice(5)}`;
}

function isPhoneField(field) {
  return /phone|fax/i.test(field || '');
}

function FieldInput({ label, field, value, onChange, type, options, checkbox, required, ssnRevealed, onToggleSSN, labelWidth }) {
  if (checkbox) {
    return (
      <label style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'0.82rem', color:'var(--text-secondary)', cursor:'pointer' }}>
        <input type="checkbox" checked={!!value} onChange={(e) => onChange(field, e.target.checked)} />
        {label}
      </label>
    );
  }

  const phone = isPhoneField(field);
  const isSSN = field === 'ssn';

  // SSN masking: show ***-**-XXXX unless revealed
  const ssnDigits = isSSN && value ? String(value).replace(/\D/g,'') : '';
  const displayVal = isSSN && !ssnRevealed && ssnDigits
    ? `***-**-${ssnDigits.slice(-4) || '****'}`
    : (phone ? formatPhoneValue(value) : (value ?? ''));

  return (
    <div style={{ display:'flex', alignItems:'center', gap:'6px', marginBottom:'6px' }}>
      <label style={{ fontSize:'0.75rem', color:'var(--text-muted)', minWidth: (labelWidth || 90), textAlign:'right', flexShrink:0 }}>
        {label}{required && <span style={{ color:'var(--status-red)', marginLeft:'2px' }}>*</span>}
      </label>
      {options ? (
        <select
          className="form-select"
          style={{ padding:'5px 8px', fontSize:'0.82rem', flex:1 }}
          value={value ?? ''}
          onChange={(e) => onChange(field, e.target.value)}
        >
          <option value="">—</option>
          {options.map((o) => {
            const val = o?.value ?? o;
            const lab = o?.label ?? o;
            return <option key={val} value={val}>{lab}</option>;
          })}
        </select>
      ) : isSSN ? (
        <div style={{ display:'flex', alignItems:'center', gap:'4px', flex:1, minWidth:0 }}>
          <input
            className="form-input"
            type="text"
            value={ssnRevealed ? formatSSNValue(value) : displayVal}
            readOnly={!ssnRevealed}
            placeholder="___-__-____"
            maxLength={11}
            title={ssnRevealed ? 'SSN visible — auto-hides in 15 seconds' : 'Click to reveal SSN'}
            onChange={(e) => {
              if (ssnRevealed) onChange(field, formatSSNValue(e.target.value));
            }}
            onClick={() => { if (!ssnRevealed && value && onToggleSSN) onToggleSSN(); }}
            style={{
              padding:'5px 8px', fontSize:'0.82rem', flex:1, minWidth:0,
              cursor: !ssnRevealed && value ? 'pointer' : 'text',
              color: !ssnRevealed && value ? 'var(--text-muted)' : 'var(--text-primary)',
            }}
          />
        </div>
      ) : (
        <input
          className="form-input"
          type={type || 'text'}
          style={{ padding:'5px 8px', fontSize:'0.82rem', flex:1 }}
          value={phone ? formatPhoneValue(value) : (value ?? '')}
          placeholder={phone ? '(___) ___-____' : undefined}
          onChange={(e) => {
            if (phone) {
              onChange(field, formatPhoneValue(e.target.value));
            } else {
              onChange(field, e.target.value);
            }
          }}
        />
      )}
    </div>
  );
}

function splitName(fullName = '') {
  const s = String(fullName || '').trim();
  if (!s) return { first_name: '', last_name: '' };
  const parts = s.split(/\s+/);
  if (parts.length === 1) return { first_name: parts[0], last_name: '' };
  return { first_name: parts.slice(0, -1).join(' '), last_name: parts[parts.length - 1] };
}

// Format date values to YYYY-MM-DD for input[type="date"]
function toDateInput(val) {
  if (!val) return '';
  const d = new Date(val);
  if (isNaN(d.getTime())) return '';
  return d.toISOString().split('T')[0];
}

function buildFormFromCase(data) {
  const claimant = data?.clients || data?.claimant || {};

  // Extract assigned_attorney as plain _id (may be populated object)
  const attyRaw = data?.assigned_attorney;
  const attyId = typeof attyRaw === 'object' && attyRaw !== null ? (attyRaw._id || '') : (attyRaw || '');

  // IMPORTANT: Do NOT spread ...data here — the case object contains enriched sub-tables
  // (consultations, treatments, documents, notes, relationships, clients, history, field_notes)
  // that must NEVER be sent back through the flat-field save path. Spreading them caused
  // handleSave to silently delete+reinsert all sub-table data on every Save press.
  return {
    // ── Flat case fields only ──
    case_type:              data?.case_type              ?? '',
    status:                 data?.status                 ?? '',
    date_of_incident:       toDateInput(data?.date_of_incident ?? claimant?.date_of_injury),
    claim_number:           data?.claim_number           ?? claimant?.claim_number ?? '',
    dwc_number:             data?.dwc_number             ?? claimant?.dwc_number   ?? '',
    demand_amount:          data?.demand_amount          ?? '',
    settlement_amount:      data?.settlement_amount      ?? '',
    attorney_notes_status:  data?.attorney_notes_status  ?? '',
    assist:                 Array.isArray(data?.assist) ? data.assist : (data?.assist ? [data.assist] : []),
    claim_status:           data?.claim_status           ?? '',
    claim_type:             data?.claim_type             ?? '',
    referred_by:            data?.referred_by            ?? '',
    af_fee:                 data?.af_fee                 ?? '',
    screener:               data?.screener               ?? '',
    consult_date:           toDateInput(data?.consult_date),
    ref_date:               toDateInput(data?.ref_date),
    lor_faxed:              toDateInput(data?.lor_faxed),
    initial_billing_done:   data?.initial_billing_done   ?? false,
    benefit_type:           data?.benefit_type           ?? '',
    work_status_dwc73:      data?.work_status_dwc73      ?? '',
    aww:                    data?.aww                    ?? '',
    benefit_rate_percent:   data?.benefit_rate_percent   ?? '',
    actual_benefit_amount:  data?.actual_benefit_amount  ?? '',
    attorney_fees:          data?.attorney_fees          ?? '',
    rec_benefits:           data?.rec_benefits           ?? '',
    certifications:         data?.certifications         ?? '',
    benefit_notes:          data?.benefit_notes          ?? '',
    body_parts:             Array.isArray(data?.body_parts)   ? data.body_parts   : (data?.body_parts   ? [data.body_parts]   : []),
    mechanism_of_injury:    data?.mechanism_of_injury    ?? '',
    compensable:            Array.isArray(data?.compensable)  ? data.compensable  : (data?.compensable  ? [data.compensable]  : []),
    disputes:               Array.isArray(data?.disputes)     ? data.disputes     : (data?.disputes     ? [data.disputes]     : []),
    description:            data?.description            ?? claimant?.notes ?? '',
    notes_diagnoses:        data?.notes_diagnoses        ?? '',
    injury_summary:         data?.injury_summary         ?? '',
    symptoms:               data?.symptoms               ?? '',
    date_of_death:          toDateInput(data?.date_of_death),
    cause_of_death:         data?.cause_of_death         ?? '',
    deceased_notes:         data?.deceased_notes         ?? '',
    is_deceased:            data?.is_deceased            ?? false,
    beneficiary_name:         data?.beneficiary_name         ?? data?.beneficiary?.name         ?? '',
    beneficiary_relationship: data?.beneficiary_relationship ?? data?.beneficiary?.relationship ?? '',
    beneficiary_phone:        data?.beneficiary_phone        ?? data?.beneficiary?.phone        ?? '',
    beneficiary_email:        data?.beneficiary_email        ?? data?.beneficiary?.email        ?? '',
    beneficiary_address:      data?.beneficiary_address      ?? data?.beneficiary?.address      ?? '',
    sol_date:               toDateInput(data?.sol_date),
    sol_years:              data?.sol_years              ?? 2,
    sol_is_minor:           data?.sol_is_minor           ?? data?.is_minor ?? false,
    // ── Claimant / client fields (read from clients sub-object, NOT sent back to server) ──
    assigned_attorney:      attyId,
    name:                   claimant?.name || `${claimant?.first_name || ''} ${claimant?.last_name || ''}`.trim() || data?.client_name || '',
    address:                claimant?.address          ?? data?.address          ?? '',
    mailing_address:        claimant?.mailing_address  ?? data?.mailing_address  ?? '',
    city:                   claimant?.city             ?? '',
    state:                  claimant?.state            ?? '',
    zip:                    claimant?.zip              ?? '',
    county:                 claimant?.county           ?? data?.county           ?? '',
    phone:                  claimant?.phone            ?? data?.phone            ?? '',
    phone_alt:              claimant?.phone_alt        ?? data?.phone_alt        ?? '',
    email:                  claimant?.email            ?? data?.email            ?? '',
    ssn:                    claimant?.ssn              ?? data?.ssn              ?? '',
    sex:                    claimant?.sex              ?? data?.sex              ?? '',
    language: (() => {
      const l = claimant?.language ?? data?.language;
      if (Array.isArray(l)) return l;
      return l ? [l] : [];
    })(),
    comm_pref:              Array.isArray(claimant?.comm_pref ?? data?.comm_pref)
                              ? (claimant?.comm_pref ?? data?.comm_pref) : [],
    date_of_birth:          toDateInput(claimant?.date_of_birth ?? data?.date_of_birth),
  };
}


function normalizeInsuranceForm(insurance = {}) {
  const src = (insurance && typeof insurance === 'object') ? insurance : {};
  return {
    ...src,
    pi: {
      ...(src.pi || {}),
      client_insurance: {
        carrier: '',
        policy_number: '',
        claim_number: '',
        adjuster_name: '',
        adjuster_phone: '',
        adjuster_email: '',
        um_uim_coverage: '',
        pip_medpay_info: '',
        notes: '',
        ...((src.pi && src.pi.client_insurance) || {}),
      },
      at_fault_insurance: {
        carrier: '',
        policy_number: '',
        claim_number: '',
        adjuster_name: '',
        adjuster_phone: '',
        adjuster_email: '',
        coverage_limits: '',
        reservation_rights_notes: '',
        denial_notes: '',
        notes: '',
        ...((src.pi && src.pi.at_fault_insurance) || {}),
      }
    }
  };
}

function pickCaseFieldsForUpdate(form) {
  // Whitelist: only send actual Case schema fields
  const CASE_FIELDS = [
    'case_type', 'status', 'date_of_incident', 'claim_number', 'dwc_number',
    'demand_amount', 'settlement_amount', 'assigned_attorney', 'attorney_name',
    'assist', 'claim_status', 'claim_type', 'referred_by', 'af_fee', 'screener',
    'consult_date', 'lor_faxed', 'initial_billing_done', 'ref_date', 'attorney_notes_status',
    'benefit_type', 'work_status_dwc73', 'aww', 'benefit_rate_percent',
    'actual_benefit_amount', 'attorney_fees', 'rec_benefits', 'certifications', 'benefit_notes',
    'body_parts', 'mechanism_of_injury', 'compensable', 'disputes',
    'description', 'notes_diagnoses',
    'injury_summary', 'symptoms',
    'date_of_death', 'cause_of_death', 'deceased_notes', 'is_deceased',
    'beneficiary_name', 'beneficiary_relationship', 'beneficiary_phone',
    'beneficiary_email', 'beneficiary_address',
    'sol_date', 'sol_years', 'sol_is_minor',
  ];
  const payload = {};
  CASE_FIELDS.forEach(k => {
    if (form[k] !== undefined) {
      // Send empty string dates as null for Mongoose
      if (['date_of_incident','consult_date','lor_faxed','date_of_death','sol_date'].includes(k) && !form[k]) {
        payload[k] = null;
      } else {
        payload[k] = form[k];
      }
    }
  });
  return payload;
}

function displayUser(u) {
  if (!u) return '—';
  const name = `${u.first_name || ''} ${u.last_name || ''}`.trim();
  return name || u.email || '—';
}

function OcrDetectedInfo({ entities }) {
  if (!entities) return null;
  var hasAny = entities.clientFound ||
    entities.names.length > 0 ||
    entities.facilities.length > 0 ||
    entities.dates.length > 0;
  if (!hasAny) return null;
  return (
    <div style={{ borderTop:'1px solid var(--zeus-border)', padding:'10px 14px 12px', background:'rgba(255,255,255,0.02)' }}>
      <div style={{ fontSize:'0.68rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em', color:'var(--text-muted)', marginBottom:8 }}>
        Detected in document
      </div>
      {entities.clientFound && (
        <div style={{ display:'flex', flexWrap:'wrap', gap:'5px', marginBottom:6 }}>
          <span style={{ fontSize:'0.65rem', fontWeight:700, color:'var(--text-muted)', width:'70px', paddingTop:'2px' }}>Client</span>
          <span style={{ fontSize:'0.75rem', padding:'2px 8px', borderRadius:'10px', fontWeight:600, background:'rgba(34,197,94,0.12)', color:'#22c55e' }}>
            {entities.clientName}
          </span>
        </div>
      )}
      {entities.facilities.length > 0 && (
        <div style={{ display:'flex', flexWrap:'wrap', gap:'5px', marginBottom:6, alignItems:'flex-start' }}>
          <span style={{ fontSize:'0.65rem', fontWeight:700, color:'var(--text-muted)', width:'70px', paddingTop:'3px', flexShrink:0 }}>Facility</span>
          <div style={{ display:'flex', flexWrap:'wrap', gap:'4px', flex:1 }}>
            {entities.facilities.map(function(f, i) {
              return <span key={i} style={{ fontSize:'0.72rem', padding:'2px 8px', borderRadius:'10px', background:'rgba(59,130,246,0.1)', color:'#60a5fa', border:'1px solid rgba(59,130,246,0.2)', maxWidth:'200px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={f}>{f}</span>;
            })}
          </div>
        </div>
      )}
      {entities.names.length > 0 && (
        <div style={{ display:'flex', flexWrap:'wrap', gap:'5px', marginBottom:6, alignItems:'flex-start' }}>
          <span style={{ fontSize:'0.65rem', fontWeight:700, color:'var(--text-muted)', width:'70px', paddingTop:'3px', flexShrink:0 }}>Names</span>
          <div style={{ display:'flex', flexWrap:'wrap', gap:'4px', flex:1 }}>
            {entities.names.map(function(n, i) {
              return <span key={i} style={{ fontSize:'0.72rem', padding:'2px 8px', borderRadius:'10px', background:'rgba(139,92,246,0.1)', color:'#a78bfa', border:'1px solid rgba(139,92,246,0.2)' }}>{n}</span>;
            })}
          </div>
        </div>
      )}
      {entities.dates.length > 0 && (
        <div style={{ display:'flex', flexWrap:'wrap', gap:'5px', alignItems:'flex-start' }}>
          <span style={{ fontSize:'0.65rem', fontWeight:700, color:'var(--text-muted)', width:'70px', paddingTop:'3px', flexShrink:0 }}>Dates</span>
          <div style={{ display:'flex', flexWrap:'wrap', gap:'4px', flex:1 }}>
            {entities.dates.map(function(d, i) {
              return <span key={i} style={{ fontSize:'0.72rem', padding:'2px 8px', borderRadius:'10px', background:'rgba(212,175,55,0.08)', color:'var(--zeus-gold)', border:'1px solid rgba(212,175,55,0.2)' }}>{d}</span>;
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// Find documents likely associated with a consultation entry
// Matches on provider name or appointment type within the doc name
function findConsultDocs(activeDocs, entry) {
  if (!activeDocs || !activeDocs.length) return [];
  const provider = (entry.provider_name || '').toLowerCase();
  const apptType = (entry.appointment_type || '').toLowerCase();
  return activeDocs.filter(d => {
    const name = (d.name || d.filename || '').toLowerCase();
    if (provider && provider.length > 2 && name.includes(provider.split(' ')[0])) return true;
    if (apptType && apptType.length > 3 && name.includes(apptType.split(' ')[0])) return true;
    return false;
  });
}

export default function CaseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSuperAdmin = user?.access_level === 'super_admin'
    || user?.role === 'super_admin'
    || user?.role === 'owner';
  const isAdmin = isSuperAdmin
    || ['admin'].includes(user?.access_level || '')
    || ['admin'].includes(user?.role || '');
  // OCR — gated by plan. Checks has_ocr flag from plan_templates (server-resolved),
  // with a slug fallback for legacy plans that predate the flag.
  const orgPlan = user?.organization?.subscription_plan || 'trial';
  const OCR_SLUGS = ['elite', 'enterprise', 'early_bird'];
  const ocrEnabled = user?.organization?.has_ocr === true || OCR_SLUGS.includes(orgPlan);
  const ocr = useOCR();
  const [ocrDismissed, setOcrDismissed] = useState(false);

  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(
    location?.state?.tab === 'treatment' ? 'treatment'
    : location?.state?.tab === 'billing'  ? 'billing'
    : 'employer_case'
  );

  const [noteText, setNoteText] = useState('');
  const [noteType, setNoteType] = useState('General');
  const [noteColor, setNoteColor] = useState('');
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [ssnRevealed, setSSNRevealed]   = useState(false);
  const [ssnCountdown, setSsnCountdown] = useState(0);   // seconds remaining while revealed
  const ssnTimerRef = useRef(null);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const noteRef = useRef(null);
  const colorPickerRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);
  const [showNotePreview, setShowNotePreview] = useState(false);
  const [noteDateFilter, setNoteDateFilter] = useState('all');
  const [noteSearch, setNoteSearch] = useState('');
  const [historyDateFilter, setHistoryDateFilter] = useState('all');
  const [historyTypeFilter, setHistoryTypeFilter] = useState('all');

  // Delete
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteReason,  setDeleteReason]  = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);

  const [showTreatment, setShowTreatment] = useState(false);
  const [editingTreatIdx, setEditingTreatIdx] = useState(null);
  const [treatFilter, setTreatFilter] = useState('all');
  const [treatForm, setTreatForm] = useState({
    provider_name:'', provider_type:'', phone:'', fax:'', email:'', address:'',
    treating_status:'current',
    treatment_start:'', treatment_end:'', next_appointment:'',
    description:'', cost:'', notes:'',
    records_requested:false, records_requested_date:'', records_received_date:'',
    bills_requested:false, bills_requested_date:'', bills_received_date:'',
    outstanding_records:'', outstanding_bills:'', gap_notes:'',
  });
  const emptyTreatForm = {
    provider_name:'', provider_type:'', phone:'', fax:'', email:'', address:'',
    treating_status:'current',
    treatment_start:'', treatment_end:'', next_appointment:'',
    description:'', cost:'', notes:'',
    records_requested:false, records_requested_date:'', records_received_date:'',
    bills_requested:false, bills_requested_date:'', bills_received_date:'',
    outstanding_records:'', outstanding_bills:'', gap_notes:'',
  };

  const [showUpload, setShowUpload] = useState(false);
  const [docUploading, setDocUploading] = useState(false);
  const [docForm, setDocForm] = useState({ name: '', category: 'medical_records' });
  const [renamingDocId, setRenamingDocId] = useState(null);
  const [renameValue,   setRenameValue]   = useState('');
  const [docFile, setDocFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const [form, setForm] = useState({});
  const [insForm, setInsForm] = useState({});
  const [isDirty, setIsDirty] = useState(false);
  const [empForm, setEmpForm] = useState({});
  const [ptdForm, setPtdForm] = useState({});
  const [rtdForm, setRtdForm] = useState({});
  const [benForm, setBenForm] = useState({});

  const [attorneys, setAttorneys] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [providers, setProviders] = useState([]);

  // Referred By searchable dropdown
  const [refByOpen, setRefByOpen] = useState(false);
  const [refBySearch, setRefBySearch] = useState('');
  const refByRef = useRef(null);
  const refByInputRef = useRef(null);

  // Quick add provider modal
  const [showQuickProvider,   setShowQuickProvider]   = useState(false);
  const [quickProviderContext, setQuickProviderContext] = useState('treatment'); // 'treatment' | 'referred_by'
  const [quickProviderForm, setQuickProviderForm] = useState({ name:'', provider_type:'', phone:'', fax:'', email:'', address:'', city:'', state:'', zip:'' });
  const [quickProviderTarget, setQuickProviderTarget] = useState('referred_by'); // 'referred_by' | 'treatment'

  // Treatment provider search dropdown
  const [treatProvOpen, setTreatProvOpen] = useState(false);
  const [treatProvSearch, setTreatProvSearch] = useState('');
  const [showProviderDetails, setShowProviderDetails] = useState(false);
  const treatProvRef = useRef(null);
  const treatProvInputRef = useRef(null);

  // WC billing modal — provider search
  const [wcProvOpen, setWcProvOpen] = useState(false);
  const [wcProvSearch, setWcProvSearch] = useState('');
  const wcProvRef = useRef(null);
  const wcProvInputRef = useRef(null);

  // Custom configurable lists from org settings
  const [customLists, setCustomLists] = useState({
    body_parts: [],
    af_fee_options: ['25','33','40','Other'],
    legal_status_options: ['Open','Pending','Closed','Denied','Disputed','Disengage'],
    legal_type_options: ['WC','Indemnity','Medical Only','Third Party','Death','DOL'],
    case_status_options: DEFAULT_STATUSES,
    treatment_type_options: ['Initial Consultation','Follow-Up','MRI','Physical Therapy'],
  });

  // Body parts multi-select
  const [showBodyParts, setShowBodyParts] = useState(false);
  const [showCompensable, setShowCompensable] = useState(false);
  const [showDisputes, setShowDisputes] = useState(false);
  const [showLanguage, setShowLanguage] = useState(false);
  const [showCommPref, setShowCommPref] = useState(false);
  const bodyPartsRef = useRef(null);
  const compensableRef = useRef(null);
  const disputesRef = useRef(null);
  const languageRef = useRef(null);
  const commPrefRef = useRef(null);
  const [showDocTrash, setShowDocTrash] = useState(false);

  // Relationships

  // Treatment tab state
  const [ptOrderForm, setPtOrderForm] = useState({ provider_name:'', provider_type:'', total_sessions:6, treatment_type:'Physical Therapy' });
  const [showPtOrder, setShowPtOrder] = useState(false);
  const [consultUploadIdx, setConsultUploadIdx] = useState(null);
  const consultFileRef = useRef(null);

  const fieldNoteSnapshotRef = useRef({});

  useEffect(() => { loadCase(); }, [id]);

  // Load custom lists independently so they stay fresh even if user edited
  // Settings in another tab. Re-fetches on window focus so changes appear immediately.
  const loadCustomLists = async () => {
    try {
      const lists = await getCustomLists();
      if (lists && typeof lists === 'object') {
        setCustomLists(prev => ({ ...prev, ...lists }));
      }
    } catch (err) {
      console.warn('[CaseDetail] Failed to load custom lists:', err?.message);
    }
  };

  useEffect(() => {
    loadCustomLists();
    const onFocus = () => loadCustomLists();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []); // eslint-disable-line

  const loadCase = async () => {
    try {
      setLoading(true);
      const data = await getCase(id);

      setC(data);
      // Pre-load billing if the tab was already active
      if (activeTab === 'billing') loadBilling(data.id || id);
      setForm(buildFormFromCase(data));

      setInsForm(normalizeInsuranceForm(data?.insurance || {}));
      setEmpForm(data?.employer || {});
      setPtdForm(data?.preceding_td || {});
      setRtdForm(data?.referred_td || {});
      setBenForm(data?.beneficiary || {});

      const tmpState = {
        form: buildFormFromCase(data),
        ins: data?.insurance || {},
        emp: data?.employer || {}
      };

      const initial = {};
      FIELD_NOTE_SPECS.forEach(spec => {
        initial[spec.source_field] = String(spec.get(tmpState) ?? '');
      });
      fieldNoteSnapshotRef.current = initial;

      try {
        const list = await getAttorneys();
        setAttorneys(Array.isArray(list) ? list : []);
      } catch {
        setAttorneys([]);
      }

      try {
        const sList = await getStaff();
        setStaffList(Array.isArray(sList) ? sList : []);
      } catch {
        setStaffList([]);
      }

      // custom lists loaded separately via its own useEffect

      try {
        const pRes = await getCareProviders();
        setProviders(Array.isArray(pRes) ? pRes : (pRes?.providers || []));
      } catch { setProviders([]); }
    } catch (err) {
      console.error(err);
      setC(null);
    } finally {
      setLoading(false);
    }
  };

  const makeHandler = (setter) => (field, value) => { setter(prev => ({ ...prev, [field]: value })); setIsDirty(true); };

  const handlers = useMemo(() => ({
    form: makeHandler(setForm),
    ins: makeHandler(setInsForm),
    emp: makeHandler(setEmpForm),
    ptd: makeHandler(setPtdForm),
    rtd: makeHandler(setRtdForm),
    ben: makeHandler(setBenForm),
  }), []);

  const states = {
    form,
    ins: insForm,
    emp: empForm,
    ptd: ptdForm,
    rtd: rtdForm,
    ben: benForm,
  };

  // Click-outside handler for all dropdowns
  useEffect(() => {
    const handleClick = (e) => {
      if (refByRef.current && !refByRef.current.contains(e.target)) setRefByOpen(false);
      if (treatProvRef.current && !treatProvRef.current.contains(e.target)) setTreatProvOpen(false);
      if (wcProvRef.current && !wcProvRef.current.contains(e.target)) setWcProvOpen(false);
      if (bodyPartsRef.current && !bodyPartsRef.current.contains(e.target)) setShowBodyParts(false);
      if (compensableRef.current && !compensableRef.current.contains(e.target)) setShowCompensable(false);
      if (disputesRef.current && !disputesRef.current.contains(e.target)) setShowDisputes(false);
      if (languageRef.current && !languageRef.current.contains(e.target)) setShowLanguage(false);
      if (commPrefRef.current && !commPrefRef.current.contains(e.target)) setShowCommPref(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // ── Statute of Limitations auto-calculation ──
  const solLayoutRef = useRef(null);

  // Recalculate SOL date when DOI or claim_type changes, using org sol_rules
  useEffect(() => {
    const solRules = customLists?.sol_rules || {};
    const claimType = form.claim_type || '';
    const rule = solRules[claimType];
    const years = rule?.years ?? 2;

    // Auto-update years when claim type changes
    if (solLayoutRef.current !== null && solLayoutRef.current !== claimType) {
      setForm(prev => ({ ...prev, sol_years: years }));
    }
    // First load: set default if not set
    if (form.sol_years === undefined || form.sol_years === null) {
      setForm(prev => ({ ...prev, sol_years: years }));
    }
    solLayoutRef.current = claimType;

    if (!form.date_of_incident) return;
    const activeYears = form.sol_years ?? years;
    // Calculate SOL date = DOI + years
    const doi = new Date(form.date_of_incident + 'T00:00:00');
    if (!isNaN(doi.getTime())) {
      const solDate = new Date(doi);
      solDate.setFullYear(solDate.getFullYear() + activeYears);
      const solStr = solDate.toISOString().split('T')[0];
      if (form.sol_date !== solStr) {
        setForm(prev => ({ ...prev, sol_date: solStr }));
      }
    }
  }, [form.date_of_incident, form.sol_years, form.claim_type, customLists]);

  // Auto-calculate minor status from DOB
  useEffect(() => {
    if (!form.date_of_birth) {
      if (form.sol_is_minor) setForm(prev => ({ ...prev, sol_is_minor: false }));
      return;
    }
    const dob = new Date(form.date_of_birth + 'T00:00:00');
    if (isNaN(dob.getTime())) return;
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const m = today.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
    const isMinorNow = age < 18;
    if (form.sol_is_minor !== isMinorNow) {
      setForm(prev => ({ ...prev, sol_is_minor: isMinorNow }));
    }
  }, [form.date_of_birth]);

  const handleQuickAddProvider = async () => {
    if (!quickProviderForm.name.trim()) return toast.error('Provider name required');
    try {
      const newP = await createCareProvider(quickProviderForm);
      setProviders(prev => [...prev, newP].sort((a,b) => a.name.localeCompare(b.name)));
      if (quickProviderTarget === 'treatment') {
        // Auto-fill treatment form with provider details
        setTreatForm(prev => ({
          ...prev,
          provider_name: newP.name || quickProviderForm.name.trim(),
          provider_type: newP.provider_type || quickProviderForm.provider_type || '',
          phone: newP.phone || quickProviderForm.phone || '',
          fax: newP.fax || quickProviderForm.fax || '',
          email: newP.email || quickProviderForm.email || '',
          address: [newP.address, newP.city, newP.state, newP.zip].filter(Boolean).join(', ') || quickProviderForm.address || '',
        }));
        setTreatProvOpen(false);
        setTreatProvSearch('');
      } else {
        setForm(prev => ({ ...prev, referred_by: quickProviderForm.name.trim() }));
        setRefByOpen(false);
        setRefBySearch('');
      }
      setShowQuickProvider(false);
      setQuickProviderForm({ name:'', provider_type:'', phone:'', fax:'', email:'', address:'', city:'', state:'', zip:'' });
      toast.success('Provider added');
    } catch { toast.error('Failed to add provider'); }
  };

  const renderField = ({ label, field, obj='form', type='text', options, checkbox, required, labelWidth }) => {
    // Autocomplete for claimant address — fills city/state/zip automatically
    if (field === 'address' && obj === 'form' && !options && !checkbox) {
      return (
        <div style={{ display:'flex', alignItems:'center', gap:'6px', marginBottom:'6px' }}>
          <label style={{ fontSize:'0.75rem', color:'var(--text-muted)', minWidth: labelWidth || '90px', textAlign:'right', flexShrink:0 }}>
            {label}
          </label>
          <AddressAutocomplete
            value={form.address ?? ''}
            onChange={(v) => setForm(prev => ({ ...prev, address: v }))}
            onPlaceSelect={({ street, city, state, zip }) => setForm(prev => ({
              ...prev,
              address: street,
              city:    city  || prev.city,
              state:   state || prev.state,
              zip:     zip   || prev.zip,
            }))}
            placeholder="Street address"
            className="form-input"
            style={{ padding:'5px 8px', fontSize:'0.82rem', flex:1 }}
          />
        </div>
      );
    }
    return (
    <FieldInput
      label={label}
      field={field}
      value={states[obj]?.[field]}
      onChange={handlers[obj]}
      type={type}
      options={options}
      checkbox={checkbox}
      required={required}
      labelWidth={labelWidth}
      ssnRevealed={ssnRevealed}
      onToggleSSN={() => {
        if (ssnRevealed) {
          // Manual hide — cancel timer immediately
          if (ssnTimerRef.current) clearInterval(ssnTimerRef.current);
          ssnTimerRef.current = null;
          setSSNRevealed(false);
          setSsnCountdown(0);
        } else {
          // Reveal — start 15-second auto-hide countdown
          setSSNRevealed(true);
          setSsnCountdown(15);
          if (ssnTimerRef.current) clearInterval(ssnTimerRef.current);
          ssnTimerRef.current = setInterval(() => {
            setSsnCountdown(prev => {
              if (prev <= 1) {
                clearInterval(ssnTimerRef.current);
                ssnTimerRef.current = null;
                setSSNRevealed(false);
                setSsnCountdown(0);
                return 0;
              }
              return prev - 1;
            });
          }, 1000);
        }
      }}
    />
    );
  };

  const SectionTitle = ({ children }) => (
    <div style={{
      fontSize:'0.78rem',
      fontWeight:700,
      textTransform:'uppercase',
      letterSpacing:'0.06em',
      color:'var(--zeus-gold)',
      padding:'8px 0 6px',
      borderBottom:'1px solid var(--zeus-border)',
      marginBottom:'8px'
    }}>
      {children}
    </div>
  );

  const CityStateZipRow = ({ obj }) => {
    const st = states[obj] || {};
    const h = handlers[obj];
    return (
      <div style={{ display:'flex', alignItems:'center', gap:'6px', marginBottom:'6px' }}>
        <label style={{ fontSize:'0.75rem', color:'var(--text-muted)', minWidth:'90px', textAlign:'right', flexShrink:0 }}>City/St/Zip</label>
        <input className="form-input" style={{ padding:'5px 8px', fontSize:'0.82rem', flex:2, minWidth:0 }}
          placeholder="City"
          value={st.city || ''} onChange={(e) => h('city', e.target.value)} />
        <select className="form-select" style={{ padding:'5px 4px', fontSize:'0.82rem', width:'62px', flexShrink:0 }}
          value={st.state || ''} onChange={(e) => h('state', e.target.value)}>
          <option value="">—</option>
          {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <input className="form-input" style={{ padding:'5px 8px', fontSize:'0.82rem', width:'72px', flexShrink:0 }}
          placeholder="00000" maxLength={5}
          value={st.zip || ''} onChange={(e) => h('zip', e.target.value.replace(/\D/g,'').slice(0,5))} />
      </div>
    );
  };

  const attorneyOptions = (attorneys || []).map(a => ({
    value: a._id,
    label: `${a.first_name || ''} ${a.last_name || ''}`.trim() || a.email || 'Attorney'
  }));

  const staffOptions = (staffList || []).map(s => {
    const name = `${s.first_name || ''} ${s.last_name || ''}`.trim() || s.email || 'Staff';
    return { value: name, label: name };
  });

  // Assist multi-select (up to 2)
  const [showAssistDropdown, setShowAssistDropdown] = useState(false);
  const assistRef = useRef(null);
  const assistValues = Array.isArray(form.assist) ? form.assist : (form.assist ? [form.assist] : []);

  useEffect(() => {
    const handler = (e) => {
      if (assistRef.current && !assistRef.current.contains(e.target)) setShowAssistDropdown(false);
    };
    if (showAssistDropdown) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showAssistDropdown]);

  // Color picker click-outside
  useEffect(() => {
    const handler = (e) => {
      if (colorPickerRef.current && !colorPickerRef.current.contains(e.target)) setShowColorPicker(false);
    };
    if (showColorPicker) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showColorPicker]);

  // Documents
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const openUpload = () => {
    setDocForm({ name: '', category: 'medical_records' });
    setDocFile(null);
    setIsDragging(false);
    ocr.reset();
    setOcrDismissed(false);
    setAppliedOcrIds(new Set());
    setShowUpload(true);
  };
  const closeUpload = () => { setShowUpload(false); setIsDragging(false); ocr.reset(); setOcrDismissed(false); setAppliedOcrIds(new Set()); };

  const onPickFile = (file) => {
    if (!file) return;
    setDocFile(file);
    setDocForm(prev => ({
      ...prev,
      name: prev.name?.trim() ? prev.name : (file.name || '').replace(/\.[^.]+$/, '')
    }));
    // Auto-run OCR on image/PDF files for Elite plan orgs
    if (ocrEnabled && (file.type.startsWith('image/') || file.type === 'application/pdf')) {
      const clientName = [c?.client_first_name, c?.client_last_name].filter(Boolean).join(' ')
                      || c?.client_name || '';
      ocr.runOCR(file, {
        consultations: c?.consultations || [],
        treatments:    c?.treatments    || [],
        clientName,
      });
      setOcrDismissed(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer?.files?.[0];
    onPickFile(file);
  };

  const doUpload = async () => {
    if (!docFile) return toast.error('Please choose a file.');
    if (!docForm.name?.trim()) return toast.error('Please enter a document name.');
    try {
      setDocUploading(true);
      const fd = new FormData();
      fd.append('file', docFile);
      fd.append('name', docForm.name.trim());
      fd.append('category', docForm.category || 'other');
      await uploadCaseDocument(id, fd);
      toast.success('Uploaded');
      closeUpload();
      await loadCase();
    } catch (err) {
      console.error(err);
      toast.error(err?.message || 'Upload failed');
    } finally {
      setDocUploading(false);
    }
  };

  // Track which OCR suggestions have been applied (by id)
  const [appliedOcrIds, setAppliedOcrIds] = useState(new Set());
  // Record viewer — shown when user clicks "View Record" on a completed consultation
  const [viewRecordEntry, setViewRecordEntry] = useState(null);

  // ── Billing Tab state ──────────────────────────────────────────────────────
  const [billing,       setBilling]      = useState({ medical_bills:[], liens:[], costs:[], disbursements:[] });
  const [billingTab,    setBillingTab]   = useState('medical_bills'); // mini-tab inside billing
  const [billingLoaded, setBillingLoaded] = useState(false);
  const [billingModal,  setBillingModal]  = useState(null); // { type, item|null }
  const [billingForm,   setBillingForm]   = useState({});
  const [billingSaving, setBillingSaving] = useState(false);

  // ── WC Billing state ───────────────────────────────────────────────────────
  const [wcBilling,       setWcBilling]      = useState({ benefit_payments:[], medical_charges:[], case_expenses:[], liens:[], billing_flags:[] });
  const [wcLoaded,        setWcLoaded]       = useState(false);

  // ── Civil case data ──
  const [civilDetails, setCivilDetails] = useState({
    civil_subtype: 'general', defendant_name: '', defendant_address: '', defendant_city: '', defendant_state: '', defendant_zip: '',
    defendant_phone: '', defendant_email: '', defendant_attorney: '',
    opposing_counsel_name: '', opposing_counsel_firm: '', opposing_counsel_phone: '', opposing_counsel_email: '', opposing_counsel_address: '',
    court_name: '', court_division: '', judge_name: '', court_case_number: '', filing_date: '', hearing_date: '',
    cause_of_action: '', damages_claimed: '', damages_description: '',
  });
  const [civilLoaded, setCivilLoaded] = useState(false);
  const [wcBenefitTab,    setWcBenefitTab]   = useState('payments'); // payments | suspensions | disputes | rate_changes
  const [wcModal,         setWcModal]        = useState(null); // { type, item|null }
  const [wcForm,          setWcForm]         = useState({});
  const [wcSaving,        setWcSaving]       = useState(false);
  // Confirmation modal state — single reusable confirm
  const [confirmState, setConfirmState] = useState(null); // { title, message, detail, onConfirm, label }

  // Apply an OCR suggestion to case data
  const applyOcrSuggestion = async (suggestion) => {
    try {
      const { action } = suggestion;
      if (action.consultationIdx !== undefined) {
        await updateConsultation(action.consultationIdx, { [action.field]: action.value });
      } else if (action.treatmentIdx !== undefined) {
        const updated = (c.treatments || []).map((t, i) =>
          cleanTreatmentForSave(i === action.treatmentIdx ? { ...t, [action.field]: action.value } : t)
        );
        const res = await updateCase(id, { treatments: updated });
        const saved = res?.treatments || updated;
        setC(prev => ({ ...prev, treatments: saved.map(t => ({ ...t, _id: t.id || t._id })) }));
      }
      setAppliedOcrIds(prev => new Set([...prev, suggestion.id]));
      toast.success('Applied');
    } catch (err) {
      toast.error('Failed to apply update');
    }
  };

  const handleDocClick = async (doc) => {
    if (!doc) return;

    // Generated document (from Letters & Forms) — open in full preview tab
    if (doc.url && doc.url.includes('/templates/documents/generated/')) {
      const genId = doc.url.split('/').pop().split('?')[0];
      const token = localStorage.getItem('zeus_token') || '';
      const previewUrl = getDirectApiUrl(`/templates/documents/generated/${genId}?format=preview&token=${encodeURIComponent(token)}`);
      window.open(previewUrl, '_blank');
      return;
    }

    // Regular uploaded file — show in preview modal
    // Use the authenticated file-serve endpoint instead of the raw /uploads/ path.
    // The raw path resolves against the frontend domain and returns the React app.
    const caseDocId = doc.id || doc._id;
    const token = localStorage.getItem('zeus_token') || '';
    const serveUrl  = caseDocId
      ? getDirectApiUrl('/cases/' + id + '/documents/' + caseDocId + '/file?token=' + encodeURIComponent(token))
      : (doc.url ? getDirectApiUrl(doc.url) : null);
    setPreviewDoc({ ...doc, url: serveUrl });
    setPreviewHtml('');
  };

  // Document trash handlers
  const handleSoftDelete = async (docId) => {
    askConfirm(
      'Move to Trash',
      'Move this document to the trash? You can restore it within 30 days.',
      null,
      async () => {
        setConfirmState(null);
        try {
          await softDeleteDocument(id, docId);
          setC(prev => ({
            ...prev,
            documents: (prev.documents || []).map(d => d._id === docId ? { ...d, deleted_at: new Date().toISOString() } : d),
          }));
          toast.success('Moved to trash');
        } catch { toast.error('Failed to delete'); }
      },
      'Move to Trash'
    );
  };

  const handleRestoreDoc = async (docId) => {
    try {
      await restoreDocument(id, docId);
      setC(prev => ({
        ...prev,
        documents: (prev.documents || []).map(d => d._id === docId ? { ...d, deleted_at: null } : d),
      }));
      toast.success('Document restored');
    } catch { toast.error('Failed to restore'); }
  };

  const handlePermanentDelete = async (docId) => {
    askConfirm(
      'Permanently Delete Document',
      'This will permanently remove the document. This cannot be undone.',
      null,
      async () => {
        setConfirmState(null);
        try {
          await permanentDeleteDocument(id, docId);
          setC(prev => ({
            ...prev,
            documents: (prev.documents || []).filter(d => d._id !== docId),
          }));
          toast.success('Permanently deleted');
        } catch { toast.error('Failed to delete'); }
      },
      'Delete Permanently'
    );
  };

  // Auto-purge trash on load (documents older than 30 days)
  useEffect(() => {
    if (id) purgeDocumentTrash(id).catch(() => {});
  }, [id]);

  // Computed: active and trashed documents
  const activeDocs = useMemo(() => (c?.documents || []).filter(d => !d.deleted_at), [c?.documents]);
  const trashedDocs = useMemo(() => {
    return (c?.documents || []).filter(d => d.deleted_at);
  }, [c?.documents]);

  const handleAddNote = async () => {
    const content = noteRef.current ? noteRef.current.innerHTML.replace(/<div><br><\/div>/g, '\n').replace(/<div>/g, '\n').replace(/<\/div>/g, '').replace(/<br\s*\/?>/g, '\n').trim() : noteText.trim();
    if (!content || content === '<br>') return;
    try {
      if (editingNoteId) {
        await updateCaseNote(id, editingNoteId, content, noteType, noteColor);
        toast.success('Note updated');
      } else {
        await addCaseNote(id, content, noteType, noteColor);
        toast.success('Note added');
      }
      setNoteText('');
      if (noteRef.current) noteRef.current.innerHTML = '';
      setNoteType('General');
      setNoteColor('');
      setEditingNoteId(null);
      setShowNoteModal(false);
      await loadCase();
    } catch (err) {
      toast.error(err?.message || 'Failed');
    }
  };

  const openNewNote = () => {
    setEditingNoteId(null);
    setNoteText('');
    setNoteType('General');
    setNoteColor('');
    setShowNoteModal(true);
    setTimeout(() => {
      if (noteRef.current) { noteRef.current.innerHTML = ''; noteRef.current.focus(); }
    }, 100);
  };

  const startEditNote = (note) => {
    setEditingNoteId(note._id);
    setNoteText(note.content || '');
    setNoteType(note.note_type || 'General');
    setNoteColor(note.color || '');
    setShowNoteModal(true);
    setTimeout(() => {
      if (noteRef.current) {
        noteRef.current.innerHTML = (note.content || '').replace(/\n/g, '<br>');
        noteRef.current.focus();
      }
    }, 100);
  };

  const cancelEditNote = () => {
    setEditingNoteId(null);
    setNoteText('');
    setNoteType('General');
    setNoteColor('');
    setShowNoteModal(false);
    if (noteRef.current) noteRef.current.innerHTML = '';
  };

  const insertFormatting = (cmd) => {
    const el = noteRef.current;
    if (!el) return;
    el.focus();
    document.execCommand(cmd === 'b' ? 'bold' : 'italic', false, null);
  };

  // Strip a treatment object down to only the fields the DB accepts.
  // c.treatments has enriched fields (created_by as user object, etc.) that
  // must never be sent raw back to the server.
  const cleanTreatmentForSave = (t) => ({
    provider_name:        t.provider_name        || '',
    provider_type:        t.provider_type        || '',
    phone:                t.phone                || '',
    fax:                  t.fax                  || '',
    email:                t.email                || '',
    address:              t.address              || '',
    treating_status:      ['current','former'].includes(t.treating_status) ? t.treating_status : 'current',
    treatment_start:      t.treatment_start      || null,
    treatment_end:        t.treatment_end        || null,
    next_appointment:     t.next_appointment     || null,
    description:          t.description          || '',
    cost:                 parseFloat(t.cost)     || 0,
    notes:                t.notes                || '',
    records_requested:    !!t.records_requested,
    records_requested_date:  t.records_requested_date  || null,
    records_received_date:   t.records_received_date   || null,
    bills_requested:      !!t.bills_requested,
    bills_requested_date:    t.bills_requested_date    || null,
    bills_received_date:     t.bills_received_date     || null,
    outstanding_records:  t.outstanding_records  || '',
    outstanding_bills:    t.outstanding_bills    || '',
    gap_notes:            t.gap_notes            || '',
    is_active:            t.is_active !== false,
    // Preserve DB id if present so server can upsert rather than blind re-insert
    ...(t.id  ? { id:  t.id  } : {}),
    ...(t._id ? { _id: t._id } : {}),
  });

  const handleAddTreatment = async (e) => {
    if (e) e.preventDefault();
    if (!treatForm.provider_name?.trim()) return toast.error('Provider name required');
    const payload = {
      ...treatForm,
      cost: parseFloat(treatForm.cost) || 0,
    };
    try {
      if (editingTreatIdx !== null) {
        // Edit existing treatment
        const updated = (c.treatments || []).map((t, i) =>
          i === editingTreatIdx ? cleanTreatmentForSave({ ...t, ...payload }) : cleanTreatmentForSave(t)
        );
        const res = await updateCase(id, { treatments: updated });
        const saved = res?.treatments || updated;
        setC(prev => ({ ...prev, treatments: saved.map(t => ({ ...t, _id: t.id || t._id })) }));
        toast.success('Treatment updated');
      } else {
        // Add new treatment via dedicated POST endpoint
        const t = await addTreatment(id, payload);
        // Append to local state using the server-confirmed row
        setC(prev => ({ ...prev, treatments: [...(prev.treatments || []), { ...t, _id: t.id || t._id }] }));

        // Auto-create a case_medical_bills row for PI, or wc_medical_charges row for WC
        try {
          if (!isPI) {
            // WC: add to Medical Authorization & Payment Tracking
            const alreadyCharged = (wcBilling.medical_charges || []).some(
              r => r.provider_name === payload.provider_name
            );
            if (!alreadyCharged && payload.provider_name?.trim()) {
              const newCharge = await addWcItem('medical_charges', {
                case_id:       id,
                provider_name: payload.provider_name.trim(),
                specialty:     payload.provider_type || '',
                billed:        0,
                paid:          0,
                auth_status:   'pending',
                ur_status:     '',
                notes:         '',
              });
              setWcBilling(prev => ({
                ...prev,
                medical_charges: [...prev.medical_charges, { ...newCharge, _id: newCharge.id }],
              }));
            }
          } else {
            // PI: add to Medical Bills
            const alreadyBilled = (billing.medical_bills || []).some(
              b => b.provider_name === payload.provider_name
            );
            if (!alreadyBilled && payload.provider_name?.trim()) {
              const newBill = await addBillingItem('medical_bills', {
                case_id:       id,
                provider_name: payload.provider_name.trim(),
                provider_type: payload.provider_type || '',
                billed:        0,
                adjusted:      0,
                paid:          0,
                status:        'open',
                notes:         '',
              });
              setBilling(prev => ({
                ...prev,
                medical_bills: [...prev.medical_bills, { ...newBill, _id: newBill.id }],
              }));
            }
          }
        } catch (billErr) {
          console.warn('[auto-bill] failed to create billing row:', billErr?.message);
        }

        toast.success('Treatment added');
      }
      setShowTreatment(false);
      setTreatForm({ ...emptyTreatForm });
      setEditingTreatIdx(null);
    } catch (err) {
      toast.error(err?.message || 'Failed');
    }
  };

  const openEditTreatment = (idx) => {
    const t = c.treatments[idx];
    setEditingTreatIdx(idx);
    setTreatForm({
      provider_name: t.provider_name || '',
      provider_type: t.provider_type || '',
      phone: t.phone || '',
      fax: t.fax || '',
      email: t.email || '',
      address: t.address || '',
      treating_status: t.treating_status || 'current',
      treatment_start: toDateInput(t.treatment_start),
      treatment_end: toDateInput(t.treatment_end),
      next_appointment: toDateInput(t.next_appointment),
      description: t.description || '',
      cost: t.cost || '',
      notes: t.notes || '',
      records_requested: t.records_requested || false,
      records_requested_date: toDateInput(t.records_requested_date),
      records_received_date: toDateInput(t.records_received_date),
      bills_requested: t.bills_requested || false,
      bills_requested_date: toDateInput(t.bills_requested_date),
      bills_received_date: toDateInput(t.bills_received_date),
      outstanding_records: t.outstanding_records || '',
      outstanding_bills: t.outstanding_bills || '',
      gap_notes: t.gap_notes || '',
    });
    setTreatProvOpen(false);
    setTreatProvSearch('');
    setShowProviderDetails(false);
    setShowTreatment(true);
  };

  const handleRemoveTreatment = (idx) => {
    const treatEntry = (c?.treatments || [])[idx];
    const tLabel = treatEntry?.provider_name || 'this provider';
    askConfirm(
      'Remove Treating Provider',
      "This will remove the provider from this case's treatment list.",
      tLabel,
      async () => {
        setConfirmState(null);
        const updated = (c.treatments || [])
          .filter((_, i) => i !== idx)
          .map(cleanTreatmentForSave);
        try {
          const res = await updateCase(id, { treatments: updated });
          const saved = res?.treatments || updated;
          setC(prev => ({ ...prev, treatments: saved.map(t => ({ ...t, _id: t.id || t._id })) }));
          toast.success('Provider removed');
        } catch (err) {
          toast.error(err?.message || 'Failed to remove');
        }
      },
      'Remove Provider'
    );
  };

  const treatmentGaps = useMemo(() => {
    const treats = (c?.treatments || []).filter(t => t.treatment_start || t.treatment_end);
    if (treats.length < 2) return [];
    const sorted = [...treats].sort((a, b) => new Date(a.treatment_end || a.treatment_start) - new Date(b.treatment_end || b.treatment_start));
    const gaps = [];
    for (let i = 1; i < sorted.length; i++) {
      const prevEnd = new Date(sorted[i-1].treatment_end || sorted[i-1].treatment_start);
      const nextStart = new Date(sorted[i].treatment_start || sorted[i].treatment_end);
      const diffDays = Math.round((nextStart - prevEnd) / (1000*60*60*24));
      if (diffDays > 14) {
        gaps.push({ from: sorted[i-1].provider_name, to: sorted[i].provider_name, days: diffDays, fromDate: prevEnd, toDate: nextStart });
      }
    }
    return gaps;
  }, [c?.treatments]);

  // ─── Treatment Tab: Appointments ───
  // Derive isPI the same way as the render (layoutMap with fallback) so data loading
  // always matches which billing layout actually displays.
  const billingLayoutMap = customLists.legal_type_layout_map || {};
  const billingCaseTypeCode = String(form.claim_type || '').toUpperCase();
  const billingTypeLayout = (billingLayoutMap[form.claim_type] || (billingCaseTypeCode === 'PI' ? 'pi' : billingCaseTypeCode === 'CIVIL' ? 'civil' : 'wc')).toLowerCase();
  const billingIsPI = billingTypeLayout === 'pi';
  const billingIsCivil = billingTypeLayout === 'civil';

  // Auto-load civil details when case is civil
  useEffect(() => {
    if (billingIsCivil && !civilLoaded && id && c?.id) {
      (async () => {
        try {
          const data = await getCivilDetails(id);
          if (data) setCivilDetails(prev => ({ ...prev, ...data }));
          setCivilLoaded(true);
        } catch { setCivilLoaded(true); }
      })();
    }
  // eslint-disable-next-line
  }, [billingIsCivil, civilLoaded, id, c?.id]);

  // Auto-load PI billing when billing tab becomes active for a PI case
  useEffect(() => {
    if (activeTab === 'billing' && billingIsPI && !billingLoaded && id && c?.id) {
      loadBilling(id);
    }
  // eslint-disable-next-line
  }, [activeTab, billingIsPI, billingLoaded, id, c?.id]);

  // Auto-load WC billing when billing tab becomes active for a WC case
  useEffect(() => {
    if (activeTab === 'billing' && !billingIsPI && !wcLoaded && id && c?.id) {
      loadWcBilling(id, c?.treatments || []);
    }
  // eslint-disable-next-line
  }, [activeTab, billingIsPI, wcLoaded, id, c?.id, c?.treatments?.length]);

  // ── WC Billing helpers ────────────────────────────────────────────────────
  const loadWcBilling = async (cid, treatmentsOverride) => {
    if (wcLoaded) return;
    try {
      const data = await getWcBilling(cid || id);
      const charges = data.medical_charges || [];

      // Auto-sync any treating providers not yet in wc_medical_charges
      const treatments = treatmentsOverride || c?.treatments || [];
      const missingProviders = treatments.filter(t =>
        t.provider_name?.trim() &&
        !charges.some(ch => ch.provider_name === t.provider_name)
      );

      let finalCharges = charges;
      if (missingProviders.length > 0) {
        const created = await Promise.all(
          missingProviders.map(t =>
            addWcItem('medical_charges', {
              case_id:       cid || id,
              provider_name: t.provider_name.trim(),
              specialty:     t.provider_type || '',
              billed:        0,
              paid:          0,
              auth_status:   'pending',
              ur_status:     '',
              notes:         '',
            }).catch(err => { console.warn('[wc auto-sync] failed:', err?.message); return null; })
          )
        );
        finalCharges = [...charges, ...created.filter(Boolean).map(r => ({ ...r, _id: r.id || r._id }))];
      }

      setWcBilling({
        benefit_payments: data.benefit_payments || [],
        medical_charges:  finalCharges,
        case_expenses:    data.case_expenses     || [],
        liens:            data.liens             || [],
        billing_flags:    data.billing_flags     || [],
      });
      setWcLoaded(true);
    } catch (e) { console.error('[wc billing load]', e); }
  };

  const openWcModal = (type, item = null) => {
    const defaults = {
      benefit_payments: { case_id:id, payment_date:'', benefit_type:'TTD', benefit_label:'Temporary Total Disability', period_start:'', period_end:'', rate:0, paid:0, owed:0, status:'paid', notes:'' },
      medical_charges:  { case_id:id, provider_name:'', specialty:'', dos_start:'', dos_end:'', billed:0, paid:0, auth_status:'pending', ur_status:'', notes:'' },
      case_expenses:    { case_id:id, expense_date:'', expense_type:'', vendor:'', amount:0, recovery_status:'recoverable', notes:'' },
      liens:            { case_id:id, title:'', amount:0, negotiated:0, status:'open', notes:'' },
      billing_flags:    { case_id:id, title:'', flag_type:'', description:'', resolved:false },
    };
    setWcForm(item ? { ...item } : { ...defaults[type] });
    setWcModal({ type, item });
    setWcProvOpen(false);
    setWcProvSearch('');
  };

  const saveWcItem = async () => {
    if (!wcModal) return;
    setWcSaving(true);
    const { type, item } = wcModal;
    // Strip empty-string dates — PostgreSQL rejects '' for DATE columns
    const WC_DATE_FIELDS = ['dos_start','dos_end','payment_date','period_start','period_end','expense_date'];
    const cleaned = { ...wcForm };
    WC_DATE_FIELDS.forEach(f => { if (cleaned[f] === '') delete cleaned[f]; });
    try {
      if (item) {
        const updated = await updateWcItem(type, item._id || item.id, cleaned);
        setWcBilling(prev => ({ ...prev, [type]: prev[type].map(r => (r._id||r.id)===(item._id||item.id) ? {...updated,_id:updated.id} : r) }));
      } else {
        const created = await addWcItem(type, { ...cleaned, case_id: id });
        setWcBilling(prev => ({ ...prev, [type]: [...prev[type], {...created,_id:created.id}] }));
      }
      setWcModal(null);
    } catch (e) { console.error('[wc save]', e); }
    finally { setWcSaving(false); }
  };

  const deleteWc = async (type, rid) => {
    try {
      await deleteWcItem(type, rid);
      setWcBilling(prev => ({ ...prev, [type]: prev[type].filter(r => (r._id||r.id) !== rid) }));
    } catch (e) { console.error('[wc delete]', e); }
  };

  // ── Billing helpers ───────────────────────────────────────────────────────
  const loadBilling = async (cid, force = false) => {
    if (billingLoaded && !force) return;
    try {
      const data = await getCaseBilling(cid || id);
      const existingBills = data.medical_bills || [];

      // Auto-sync treating providers that don't have a billing row yet
      const treatingProviders = (c?.treatments || []).filter(t => t.provider_name?.trim());
      const toCreate = treatingProviders.filter(t =>
        !existingBills.some(b => b.provider_name === t.provider_name)
      );

      let allBills = [...existingBills];
      if (toCreate.length > 0) {
        const created = await Promise.all(
          toCreate.map(t =>
            addBillingItem('medical_bills', {
              case_id: cid || id, provider_name: t.provider_name.trim(),
              provider_type: t.provider_type || '', billed: 0, adjusted: 0, paid: 0,
              status: 'open', notes: '',
            }).catch(() => null)
          )
        );
        allBills = [...existingBills, ...created.filter(Boolean).map(r => ({ ...r, _id: r.id }))];
      }

      setBilling({
        medical_bills:  allBills,
        liens:          data.liens          || [],
        costs:          data.costs          || [],
        disbursements:  data.disbursements  || [],
      });
      setBillingLoaded(true);
    } catch (e) { console.error('[billing load]', e); }
  };

  const openBillingModal = (type, item = null) => {
    const defaults = {
      medical_bills:  { case_id: id, provider_name:'', provider_type:'', billed:0, adjusted:0, paid:0, status:'open', notes:'' },
      liens:          { case_id: id, title:'', lienholder:'', amount:0, negotiated:0, status:'open', notes:'' },
      costs:          { case_id: id, description:'', amount:0, recoverable:true, notes:'' },
      disbursements:  { case_id: id, payee:'', category:'client', amount:0, paid_date:'', status:'pending', notes:'' },
    };
    setBillingForm(item ? { ...item } : { ...defaults[type] });
    setBillingModal({ type, item });
  };

  const saveBillingItem = async () => {
    if (!billingModal) return;
    setBillingSaving(true);
    const { type, item } = billingModal;
    try {
      if (item) {
        const updated = await updateBillingItem(type, item._id || item.id, billingForm);
        setBilling(prev => ({ ...prev, [type]: prev[type].map(r => (r._id || r.id) === (item._id || item.id) ? { ...updated, _id: updated.id } : r) }));
      } else {
        const created = await addBillingItem(type, { ...billingForm, case_id: id });
        setBilling(prev => ({ ...prev, [type]: [...prev[type], { ...created, _id: created.id }] }));
      }
      setBillingModal(null);
    } catch (e) { console.error('[billing save]', e); }
    finally { setBillingSaving(false); }
  };

  const deleteBilling = async (type, rid) => {
    try {
      await deleteBillingItem(type, rid);
      setBilling(prev => ({ ...prev, [type]: prev[type].filter(r => (r._id || r.id) !== rid) }));
    } catch (e) { console.error('[billing delete]', e); }
  };

  const fmtMoney = (n) => {
    const num = parseFloat(n) || 0;
    return '$' + num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

    const consultations = c?.consultations || [];
  const DEFAULT_TREATMENT_TYPES = ['Initial Consultation','Follow-Up','MRI','Physical Therapy'];
  const treatmentTypes = customLists.treatment_type_options?.length ? customLists.treatment_type_options : DEFAULT_TREATMENT_TYPES;

  // ── Consultation helpers — use direct row endpoints, NOT delete+reinsert ──────

  // Update a single field (or set of fields) on an existing consultation row.
  // Falls back to full-array save only when the row has no id yet (brand-new unsaved row).
  const updateConsultation = async (idx, changes, silent) => {
    const entry = consultations[idx];
    const rowId = entry?.id || entry?._id;

    // Optimistic UI update first
    const updated = consultations.map((c, i) => i === idx ? { ...c, ...changes } : c);
    setC(prev => ({ ...prev, consultations: updated }));

    try {
      if (rowId) {
        // Row exists in DB — PATCH just the changed fields directly
        const saved = await patchConsultation(id, rowId, changes);
        // Sync back server-confirmed values for this row only
        setC(prev => ({
          ...prev,
          consultations: prev.consultations.map((c, i) =>
            i === idx ? { ...c, ...saved, _id: saved.id || saved._id } : c
          ),
        }));
      } else {
        // Row not yet in DB — POST the full row
        const full = { ...entry, ...changes };
        const saved = await addConsultation(id, full);
        setC(prev => ({
          ...prev,
          consultations: prev.consultations.map((c, i) =>
            i === idx ? { ...c, ...saved, _id: saved.id } : c
          ),
        }));
      }
      // Show completion notification when treatment is marked done
      if (changes.completed === true && !silent) {
        const provider = entry ? (entry.provider_name || entry.appointment_type || 'Appointment') : 'Appointment';
        toast.success(provider + ' — treatment complete. Record saved to Documents tab.', { duration: 5000, icon: '' });
      }
    } catch (err) {
      console.error('[updateConsultation]', err);
      toast.error(err?.message || 'Failed to save');
      // Revert optimistic update on error
      setC(prev => ({ ...prev, consultations: consultations }));
    }
  };

  const removeConsultation = (idx) => {
    const entry = consultations[idx];
    const label = entry?.provider_name || entry?.appointment_type || 'this consultation';
    askConfirm(
      'Remove Consultation',
      'This will remove the consultation entry from the Treatment tab.',
      label,
      async () => {
        const rowId = entry?.id || entry?._id;
        setC(prev => ({ ...prev, consultations: prev.consultations.filter((_, i) => i !== idx) }));
        try {
          if (rowId) await deleteConsultation(id, rowId);
          toast.success('Removed');
          setConfirmState(null);
        } catch (err) {
          toast.error(err?.message || 'Failed to remove');
          setC(prev => ({ ...prev, consultations: consultations }));
        }
      },
      'Remove'
    );
  };

  const removePTGroup = (groupId) => {
    const toDelete = consultations.filter(c => c.therapy_group === groupId);
    const providerName = toDelete[0]?.provider_name || 'this treatment order';
    askConfirm(
      'Remove Treatment Order',
      `Remove all ${toDelete.length} session(s) in this order?`,
      providerName,
      async () => {
        setC(prev => ({ ...prev, consultations: prev.consultations.filter(c => c.therapy_group !== groupId) }));
        try {
          await Promise.all(toDelete.map(c => (c.id || c._id) ? deleteConsultation(id, c.id || c._id) : Promise.resolve()));
          toast.success('Order removed');
          setConfirmState(null);
        } catch (err) {
          toast.error(err?.message || 'Failed to remove');
          setC(prev => ({ ...prev, consultations: consultations }));
        }
      },
      'Remove Order'
    );
  };

  const addTreatmentEntry = async (treatmentType) => {
    const isPT = treatmentType.toLowerCase().includes('physical therapy');
    if (isPT) {
      // Auto-select the therapy provider if one exists in current treating providers
      const therapyProvider = (c.treatments || []).find(t =>
        (t.treating_status || 'current') === 'current' &&
        (t.provider_type || '').toLowerCase().includes('therapy')
      );
      setShowPtOrder(true);
      setPtOrderForm(prev => ({
        ...prev,
        treatment_type: treatmentType,
        provider_name: therapyProvider ? therapyProvider.provider_name : prev.provider_name,
        provider_type: therapyProvider ? therapyProvider.provider_type : prev.provider_type,
      }));
      return;
    }
    try {
      const newRow = {
        provider_name: '', provider_type: '',
        appointment_type: treatmentType,
        status: 'scheduled', appointment_date: null,
        reschedule_date: null, reschedule_status: '',
        reschedule2_date: null, reschedule2_status: '',
        record_uploaded: false, completed: false,
        session_label: '', therapy_group: '', total_sessions: null, notes: '',
      };
      const saved = await addConsultation(id, newRow);
      setC(prev => ({ ...prev, consultations: [...(prev.consultations || []), { ...saved, _id: saved.id }] }));
      toast.success(`${treatmentType} added`);
    } catch (err) {
      toast.error(err?.message || 'Failed to add');
    }
  };

  const addPTOrder = async () => {
    if (!ptOrderForm.provider_name?.trim()) return toast.error('Provider name required');
    if (!ptOrderForm.total_sessions || ptOrderForm.total_sessions < 1) return toast.error('Enter number of sessions');
    const groupId = 'pt_' + Date.now();
    const sessions = [];
    for (let i = 1; i <= ptOrderForm.total_sessions; i++) {
      sessions.push({
        provider_name: ptOrderForm.provider_name, provider_type: ptOrderForm.provider_type || 'Therapy',
        appointment_type: ptOrderForm.treatment_type || 'Physical Therapy',
        status: 'scheduled', appointment_date: '',
        reschedule_date: '', reschedule_status: '',
        reschedule2_date: '', reschedule2_status: '',
        record_uploaded: false, completed: false,
        session_label: `${i} of ${ptOrderForm.total_sessions}`,
        therapy_group: groupId, total_sessions: ptOrderForm.total_sessions, notes: '',
      });
    }
    try {
      const savedSessions = await Promise.all(
        sessions.map(s => addConsultation(id, s))
      );
      setC(prev => ({ ...prev, consultations: [...(prev.consultations || []), ...savedSessions.map(s => ({ ...s, _id: s.id }))] }));
      setPtOrderForm({ provider_name:'', provider_type:'', total_sessions:6, treatment_type:'Physical Therapy' });
      setShowPtOrder(false);
      toast.success(`${ptOrderForm.total_sessions} sessions added`);
    } catch (err) {
      toast.error(err?.message || 'Failed to add sessions');
    }
  };

  const handleConsultUpload = async (idx, file) => {
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('name', `${consultations[idx].provider_name || 'Treatment'} - ${consultations[idx].session_label || consultations[idx].appointment_type} Record`);
      formData.append('category', 'medical_records');
      await uploadCaseDocument(id, formData);
      await updateConsultation(idx, { record_uploaded: true, completed: true });
      toast.success('Record uploaded & marked complete');
    } catch { toast.error('Upload failed'); }
  };

  // Helper: date string for input
  const toDateVal = (d) => {
    if (!d) return '';
    if (typeof d === 'string') return d.substring(0, 10);
    return new Date(d).toISOString().split('T')[0];
  };

  // ─── End Treatment Tab helpers ───

  const askConfirm = (title, message, detail, onConfirm, label = 'Delete') => {
    setConfirmState({ title, message, detail, onConfirm, label });
  };

  const handleSave = async () => {
    // Validate required fields
    if (!form.name?.trim()) return toast.error('Claimant Name is required');
    if (!form.date_of_birth) return toast.error('DOB is required');
    if (!form.claim_type?.trim()) return toast.error('Type is required');
    setSaving(true);
    try {
      const claimant = c?.claimant || c?.clients || null;
      const claimantId = claimant?._id;

      if (claimantId) {
        const { first_name, last_name } = splitName(form.name);

        await updateClient(claimantId, {
          name: form.name ?? '',
          first_name: first_name ?? '',
          last_name: last_name ?? '',
          address: form.address ?? '',
          mailing_address: form.mailing_address ?? '',
          city: form.city ?? '',
          state: form.state ?? '',
          zip: form.zip ?? '',
          county: form.county ?? '',
          phone: form.phone ?? '',
          phone_alt: form.phone_alt ?? '',
          email: form.email ?? '',
          ssn: form.ssn ?? '',
          sex: form.sex ?? '',
          language: Array.isArray(form.language) ? form.language : (form.language ? [form.language] : []),
          comm_pref: Array.isArray(form.comm_pref) ? form.comm_pref : [],
          date_of_birth: form.date_of_birth || null,
          date_of_injury: form.date_of_incident || null,
          claim_number: form.claim_number ?? '',
          dwc_number: form.dwc_number ?? '',
          notes: form.description ?? '',
        });
      }

      // Compute claimant field changes for history tracking
      const oldClaimant = c?.clients || c?.claimant || {};
      const CLAIMANT_TRACK = {
        name: 'Claimant Name',
        address: 'Claimant Address',
        mailing_address: 'Mailing Address',
        city: 'City',
        state: 'State',
        zip: 'Zip',
        county: 'County',
        phone: 'Phone',
        phone_alt: 'Alt Phone',
        email: 'Email',
        ssn: 'SSN',
        sex: 'Sex',
        language: 'Language',
      };
      const clientHistory = [];
      const oldName = oldClaimant?.name || `${oldClaimant?.first_name || ''} ${oldClaimant?.last_name || ''}`.trim();
      if ((form.name || '') !== (oldName || '')) {
        clientHistory.push({ action: 'Field Changed', description: `Field Claimant Name changed from "${oldName || '(empty)'}" to "${form.name || '(empty)'}"` });
      }
      Object.entries(CLAIMANT_TRACK).forEach(([k, label]) => {
        if (k === 'name') return; // already handled
        const oldVal = String(oldClaimant?.[k] ?? '');
        const newVal = String(form[k] ?? '');
        if (oldVal !== newVal) {
          clientHistory.push({ action: 'Field Changed', description: `Field ${label} changed from "${oldVal || '(empty)'}" to "${newVal || '(empty)'}"` });
        }
      });

      await updateCase(id, {
        ...pickCaseFieldsForUpdate(form),
        client_name: form.name || '',
        insurance: normalizeInsuranceForm(insForm),
        employer: empForm,
        preceding_td: ptdForm,
        referred_td: rtdForm,
        _extra_history: clientHistory,
      });

      // Check for notes field changes and create Notes tab entries
      const currentState = { form, ins: insForm, emp: empForm };
      const before = fieldNoteSnapshotRef.current || {};
      const notesChanges = FIELD_NOTE_SPECS
        .map(spec => {
          const nowVal = String(spec.get(currentState) ?? '').trim();
          const prevVal = String(before[spec.source_field] ?? '').trim();
          return { ...spec, nowVal, prevVal, changed: nowVal !== prevVal };
        })
        .filter(x => x.changed && x.nowVal);

      for (const ch of notesChanges) {
        await addCaseNote(id, `[${ch.label}] ${ch.nowVal}`);
        await upsertCaseFieldNote(id, ch.source_field, ch.nowVal);
      }

      // Update snapshot
      const newSnap = { ...before };
      notesChanges.forEach(ch => { newSnap[ch.source_field] = ch.nowVal; });
      fieldNoteSnapshotRef.current = newSnap;

      // Save civil details if this is a civil case
      if (billingIsCivil && civilLoaded) {
        try { await saveCivilDetails(id, civilDetails); } catch (e) { console.warn('Civil details save:', e.message); }
      }

      toast.success('Case saved');
      await loadCase();
    } catch (err) {
      console.error(err);
      toast.error(err?.message || 'Save failed');
    } finally {
      setSaving(false);
      setIsDirty(false);
    }
  };

  const handleDiscard = () => {
    if (!c) return;
    setForm(buildFormFromCase(c));
    setInsForm(normalizeInsuranceForm(c?.insurance || {}));
    setIsDirty(false);
    toast('Changes discarded', { icon: '↩' });
  };

  const handleDeleteCase = async () => {
    setDeleting(true);
    try {
      const res = await deleteCase(id, deleteReason, isSuperAdmin ? deleteConfirm.trim() : undefined);
      if (res.deleted) {
        toast.success('Case permanently deleted');
        navigate('/cases');
      } else if (res.pending) {
        toast.success('Deletion request sent to admin for approval');
        setShowDeleteConfirm(false);
      }
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Failed';
      toast.error(msg);
    } finally {
      setDeleting(false);
    }
  };

  const mergedNotes = useMemo(() => {
    // Combine inline case notes + field notes (from textarea areas)
    const caseNotes = Array.isArray(c?.notes) ? [...c.notes] : [];
    const fieldNotes = Array.isArray(c?.field_notes) ? c.field_notes.map(fn => ({
      ...fn,
      _id: fn._id,
      content: fn.content,
      note_type: fn.note_type || fn.source_field?.replace(/_/g, ' ') || 'Field Note',
      is_field_note: true,
      created_by: fn.created_by,
      updated_by: fn.updated_by,
      createdAt: fn.createdAt,
      updatedAt: fn.updatedAt,
    })) : [];
    const all = [...caseNotes, ...fieldNotes];
    all.sort((a, b) => {
      const da = new Date(a.updatedAt || a.createdAt || 0).getTime();
      const db = new Date(b.updatedAt || b.createdAt || 0).getTime();
      return db - da;
    });
    return all;
  }, [c]);

  if (loading) return <div className="loading-spinner"><div className="spinner" /></div>;
  if (!c) return <div className="empty-state"><h3>Case not found</h3></div>;

  const caseTypeCode = String(form.claim_type || '').toUpperCase();
  // Determine layout from the legal_type_layout_map (set in Settings > Custom Lists)
  const layoutMap = customLists.legal_type_layout_map || {};
  const typeLayout = (layoutMap[form.claim_type] || (caseTypeCode === 'PI' ? 'pi' : caseTypeCode === 'CIVIL' ? 'civil' : 'wc')).toLowerCase();
  const isPI = typeLayout === 'pi';
  const isCivil = typeLayout === 'civil';
  const insuranceTitle = isCivil ? 'Court Information' : isPI ? 'Insurance Information' : 'Workers Compensation Carrier Insurance';
  const claimantRefLabel = isCivil ? 'File #' : isPI ? 'PI Ref' : 'DWC';
  const lorLabel = isPI ? 'LOR sent' : 'LOR faxed';

  // SOL Warning Badge
  const getSOLBadge = () => {
    // Case is closed — SOL is no longer relevant
    if ((form.status || '').toLowerCase() === 'closed') {
      return { color: '#64748b', bg: 'rgba(100,116,139,0.12)', label: 'CLOSED', days: null };
    }
    if (!form.sol_date) return null;
    const sol = new Date(form.sol_date + 'T00:00:00');
    const today = new Date();
    today.setHours(0,0,0,0);
    const diffMs = sol.getTime() - today.getTime();
    const daysLeft = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    if (daysLeft < 0) return { color: '#7c3aed', bg: 'rgba(124,58,237,0.12)', label: 'PAST DUE', days: daysLeft };
    if (daysLeft <= 30) return { color: '#ef4444', bg: 'rgba(239,68,68,0.12)', label: 'CRITICAL', days: daysLeft };
    if (daysLeft <= 90) return { color: '#f97316', bg: 'rgba(249,115,22,0.12)', label: 'URGENT', days: daysLeft };
    if (daysLeft <= 180) return { color: '#eab308', bg: 'rgba(234,179,8,0.12)', label: 'WATCH', days: daysLeft };
    return { color: '#22c55e', bg: 'rgba(34,197,94,0.12)', label: 'SAFE', days: daysLeft };
  };
  const solBadge = getSOLBadge();

  // SOL Rule display: "2 Years, PI, TX"
  const getSolRuleDisplay = () => {
    const solRules = customLists?.sol_rules || {};
    const claimType = form.claim_type || '';
    const rule = solRules[claimType];
    const layoutMap = customLists?.legal_type_layout_map || {};
    const layout = (layoutMap[claimType] || 'wc').toUpperCase();
    const years = rule?.years ?? form.sol_years ?? 2;
    const state = rule?.state ?? 'TX';
    return `${years} Year${years !== 1 ? 's' : ''}, ${layout}, ${state}`;
  };

  const piClientIns = insForm?.pi?.client_insurance || {};
  const piAtFaultIns = insForm?.pi?.at_fault_insurance || {};
  const setPIInsField = (section, field, value) => {
    setInsForm(prev => ({
      ...prev,
      pi: {
        ...(prev?.pi || {}),
        [section]: {
          ...(((prev?.pi || {})[section]) || {}),
          [field]: value
        }
      }
    }));
  };

  return (
    <>
    <div style={{ display:'flex', flexDirection:'column', height:'100vh' }}>
      <Header title={<>{c.case_number}{c.is_minor && <span style={{ marginLeft:'10px', padding:'2px 10px', borderRadius:'10px', fontSize:'0.7rem', fontWeight:700, background:'rgba(249,115,22,0.15)', color:'#f97316', verticalAlign:'middle' }}>Minor</span>}{solBadge && <span style={{ marginLeft:'10px', display:'inline-flex', alignItems:'center', gap:'5px', padding:'2px 10px', borderRadius:'10px', fontSize:'0.7rem', fontWeight:700, background: solBadge.bg, color: solBadge.color, verticalAlign:'middle' }}>SOL: <span style={{ width:'8px', height:'8px', borderRadius:'50%', background: solBadge.color, display:'inline-block' }} />{solBadge.label}{solBadge.days !== null ? (solBadge.days < 0 ? ` ${Math.abs(solBadge.days)}d past` : ` ${solBadge.days}d left`) : ''}</span>}</>}>
        <Link to="/cases" className="btn btn-ghost btn-sm"><ArrowLeft size={16} /> Back</Link>

        <select
          className="form-select"
          value={form.status || ''}
          onChange={(e) => setForm(prev => ({...prev, status: e.target.value}))}
          style={{ width:'auto', padding:'6px 10px', fontSize:'0.82rem' }}
        >
          {(customLists.case_status_options || DEFAULT_STATUSES).map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
        </select>

        <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
          <Save size={14} /> {saving ? 'Saving...' : 'Save'}
        </button>

        <button
          className="btn btn-sm"
          onClick={() => { setDeleteReason(''); setShowDeleteConfirm(true); }}
          style={{ background:'transparent', border:'1px solid var(--status-red)', color:'var(--status-red)' }}
          title={isAdmin ? 'Delete case' : 'Request deletion'}
        >
          <Trash2 size={14} /> {isAdmin ? 'Delete' : 'Request Delete'}
        </button>
      </Header>

      <div style={{ flex:1, overflowY:'auto' }}>
      <div className="app-content" style={{ padding:'16px 20px' }}>
        {/* ═══════════════════ TOP HALF — 4 columns ═══════════════════ */}
        <div className="case-detail-header" style={{
          display:'grid',
          gridTemplateColumns: isCivil ? '1fr 1fr 1fr 1fr' : isPI ? '1fr 1.2fr 1fr' : '1.15fr 0.95fr 0.95fr 0.95fr',
          gap:'12px',
          marginBottom:'16px',
          overflowX: isPI ? 'visible' : 'auto',
          minWidth: 0,
        }}>

          {/* ── CLAIMANT ── */}
          <div className="card" style={{ padding:'14px' }}>
            <SectionTitle>Claimant</SectionTitle>
            {renderField({ label:"Name", field:"name", obj:"form", required:true })}
            {renderField({ label:"Address", field:"address", obj:"form" })}
            {renderField({ label:"Mailing", field:"mailing_address", obj:"form" })}

            {/* City / State / Zip */}
            <CityStateZipRow obj="form" />

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'4px' }}>
              {renderField({ label:"Phone", field:"phone", obj:"form" })}
              {renderField({ label:"Alt#", field:"phone_alt", obj:"form" })}
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'4px' }}>
              {renderField({ label:"SSN", field:"ssn", obj:"form" })}
              {renderField({ label:"County", field:"county", obj:"form" })}
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'4px' }}>
              {renderField({ label:"DOB", field:"date_of_birth", obj:"form", type:"date" })}
              {renderField({ label:"DOI", field:"date_of_incident", obj:"form", type:"date" })}
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'4px' }}>
              {renderField({ label:"Claim", field:"claim_number", obj:"form" })}
              {renderField({ label:"Sex", field:"sex", obj:"form", options:['Male','Female','Other'] })}
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'4px' }}>
              {renderField({ label:claimantRefLabel, field:"dwc_number", obj:"form" })}
              {/* Language Multi-Select */}
              <div style={{ display:'flex', alignItems:'flex-start', gap:'6px', marginBottom:'6px' }}>
                <label style={{ fontSize:'0.75rem', color:'var(--text-muted)', minWidth:'90px', textAlign:'right', flexShrink:0, paddingTop:'6px' }}>Language</label>
                <div ref={languageRef} style={{ flex:1, position:'relative' }}>
                  <div onClick={() => setShowLanguage(!showLanguage)} style={{
                    padding:'4px 8px', fontSize:'0.82rem', minHeight:'30px',
                    background:'var(--zeus-navy-light)', border:'1px solid var(--zeus-border)',
                    borderRadius:'var(--radius-sm)', cursor:'pointer',
                    display:'flex', flexWrap:'wrap', gap:'3px', alignItems:'center',
                  }}>
                    {(form.language || []).length === 0 ? (
                      <span style={{ color:'var(--text-muted)', fontSize:'0.82rem' }}>Select...</span>
                    ) : (form.language || []).map(l => (
                      <span key={l} style={{
                        background:'var(--zeus-gold-dim)', color:'var(--zeus-gold)', padding:'1px 7px',
                        borderRadius:'10px', fontSize:'0.68rem', fontWeight:600,
                        display:'inline-flex', alignItems:'center', gap:'3px',
                      }}>
                        {l}
                        <X size={9} style={{ cursor:'pointer' }} onClick={(e) => {
                          e.stopPropagation();
                          setForm(prev => ({...prev, language: (prev.language || []).filter(x => x !== l)}));
                        }} />
                      </span>
                    ))}
                    <ChevronDown size={12} style={{ marginLeft:'auto', color:'var(--text-muted)', flexShrink:0 }} />
                  </div>
                  {showLanguage && (
                    <div style={{
                      position:'absolute', top:'100%', left:0, right:0, zIndex:20,
                      background:'var(--zeus-navy)', border:'1px solid var(--zeus-border)',
                      borderRadius:'var(--radius-sm)', maxHeight:'160px', overflowY:'auto',
                      boxShadow:'0 4px 20px rgba(0,0,0,0.3)',
                    }}>
                      {(customLists.language_options || ['English','Spanish','Other']).map(opt => {
                        const sel = (form.language || []).includes(opt);
                        return (
                          <div key={opt} onClick={() => {
                            setForm(prev => ({
                              ...prev,
                              language: sel ? (prev.language || []).filter(x => x !== opt) : [...(prev.language || []), opt],
                            }));
                          }} style={{
                            padding:'4px 10px', cursor:'pointer', fontSize:'0.82rem',
                            background: sel ? 'var(--zeus-gold-dim)' : 'transparent',
                            color: sel ? 'var(--zeus-gold)' : 'var(--text-primary)',
                            display:'flex', alignItems:'center', gap:'6px',
                          }}>
                            <input type="checkbox" checked={sel} readOnly style={{ accentColor:'var(--zeus-gold)' }} />
                            {opt}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {renderField({ label:"E-mail", field:"email", obj:"form" })}

            {/* Communication Preference Multi-Select */}
            <div style={{ display:'flex', alignItems:'flex-start', gap:'6px', marginBottom:'6px' }}>
              <label style={{ fontSize:'0.75rem', color:'var(--text-muted)', minWidth:'90px', textAlign:'right', flexShrink:0, paddingTop:'6px' }}>Comm Pref</label>
              <div ref={commPrefRef} style={{ flex:1, position:'relative' }}>
                <div onClick={() => setShowCommPref(!showCommPref)} style={{
                  padding:'4px 8px', fontSize:'0.82rem', minHeight:'30px',
                  background:'var(--zeus-navy-light)', border:'1px solid var(--zeus-border)',
                  borderRadius:'var(--radius-sm)', cursor:'pointer',
                  display:'flex', flexWrap:'wrap', gap:'3px', alignItems:'center',
                }}>
                  {(form.comm_pref || []).length === 0 ? (
                    <span style={{ color:'var(--text-muted)', fontSize:'0.82rem' }}>Select...</span>
                  ) : (form.comm_pref || []).map(cp => (
                    <span key={cp} style={{
                      background:'rgba(59,130,246,0.12)', color:'#3b82f6', padding:'1px 7px',
                      borderRadius:'10px', fontSize:'0.68rem', fontWeight:600,
                      display:'inline-flex', alignItems:'center', gap:'3px',
                    }}>
                      {cp}
                      <X size={9} style={{ cursor:'pointer' }} onClick={(e) => {
                        e.stopPropagation();
                        setForm(prev => ({...prev, comm_pref: (prev.comm_pref || []).filter(x => x !== cp)}));
                      }} />
                    </span>
                  ))}
                  <ChevronDown size={12} style={{ marginLeft:'auto', color:'var(--text-muted)', flexShrink:0 }} />
                </div>
                {showCommPref && (
                  <div style={{
                    position:'absolute', top:'100%', left:0, right:0, zIndex:20,
                    background:'var(--zeus-navy)', border:'1px solid var(--zeus-border)',
                    borderRadius:'var(--radius-sm)', maxHeight:'160px', overflowY:'auto',
                    boxShadow:'0 4px 20px rgba(0,0,0,0.3)',
                  }}>
                    {(customLists.comm_pref_options || ['Mail Only','E-mail','Phone Call','Text Message']).map(opt => {
                      const sel = (form.comm_pref || []).includes(opt);
                      return (
                        <div key={opt} onClick={() => {
                          setForm(prev => ({
                            ...prev,
                            comm_pref: sel ? (prev.comm_pref || []).filter(x => x !== opt) : [...(prev.comm_pref || []), opt],
                          }));
                        }} style={{
                          padding:'4px 10px', cursor:'pointer', fontSize:'0.82rem',
                          background: sel ? 'rgba(59,130,246,0.08)' : 'transparent',
                          color: sel ? '#3b82f6' : 'var(--text-primary)',
                          display:'flex', alignItems:'center', gap:'6px',
                        }}>
                          <input type="checkbox" checked={sel} readOnly style={{ accentColor:'#3b82f6' }} />
                          {opt}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div style={{ marginTop:'6px', display:'flex', alignItems:'center', justifyContent:'flex-end' }}>
              <label style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'0.82rem', color:'var(--text-secondary)', cursor:'pointer' }}>
                <input
                  type="checkbox"
                  checked={form.is_deceased || false}
                  onChange={(e) => setForm(prev => ({...prev, is_deceased: e.target.checked}))}
                  style={{ accentColor:'#ef4444', width:'15px', height:'15px' }}
                />
                Is this a Deceased Case?
              </label>
            </div>
          </div>

          {/* ── INSURANCE / DEFENDANT ── */}
          {isCivil ? (
            <div className="card" style={{ padding:'14px' }}>
              <SectionTitle>Defendant</SectionTitle>
              <div style={{ marginBottom:'4px' }}>
                <FieldInput label="Name" field="defendant_name" value={civilDetails.defendant_name || ''} onChange={(f,v) => setCivilDetails(p => ({...p, [f]: v}))} />
                <FieldInput label="Address" field="defendant_address" value={civilDetails.defendant_address || ''} onChange={(f,v) => setCivilDetails(p => ({...p, [f]: v}))} />
                <CityStateZipRow prefix="defendant_" obj={civilDetails} setObj={(f,v) => setCivilDetails(p => ({...p, [f]: v}))} />
                <div className="claimant-pair-row" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'4px' }}>
                  <FieldInput label="Phone" field="defendant_phone" value={civilDetails.defendant_phone || ''} onChange={(f,v) => setCivilDetails(p => ({...p, [f]: v}))} />
                  <FieldInput label="Email" field="defendant_email" value={civilDetails.defendant_email || ''} onChange={(f,v) => setCivilDetails(p => ({...p, [f]: v}))} />
                </div>
                <FieldInput label="Attorney" field="defendant_attorney" value={civilDetails.defendant_attorney || ''} onChange={(f,v) => setCivilDetails(p => ({...p, [f]: v}))} />
              </div>
            </div>
          ) : (
          <div className="card" style={{ padding: isPI ? '14px' : '10px' }}>
            <SectionTitle>{insuranceTitle}</SectionTitle>

            {!isPI ? (
              <>
                {renderField({ label:"Insurance", labelWidth:"72px", field:"insurance_company", obj:"ins" })}
                {renderField({ label:"Provider Network", labelWidth:"72px", field:"network", obj:"ins" })}
                {renderField({ label:"Address", field:"address", obj:"ins" })}
                {renderField({ label:"City/St/Zip", field:"city_state_zip", obj:"ins" })}
                {renderField({ label:"Phone", field:"phone", obj:"ins" })}
                {renderField({ label:"Fax", field:"fax", obj:"ins" })}

                <div style={{ margin:'8px 0 4px', borderTop:'1px solid var(--zeus-border)', paddingTop:'8px' }}>
                  {renderField({ label:"Adjuster", labelWidth:"72px", field:"adjuster_name", obj:"ins" })}
                  {renderField({ label:"Email", labelWidth:"72px", field:"adjuster_email", obj:"ins" })}
                  {renderField({ label:"Phone/Ext", labelWidth:"72px", field:"adjuster_phone", obj:"ins" })}
                  {renderField({ label:"Fax", field:"adjuster_fax", obj:"ins" })}
                </div>

              </>
            ) : (
              <div className="medical-info-grid" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px', alignItems:'start' }}>
                <div>
                  <div style={{ fontSize:'0.72rem', color:'var(--text-muted)', marginBottom:'6px', fontWeight:600 }}>Client Insurance</div>
                  <FieldInput label="Carrier" labelWidth={68} field="carrier" value={piClientIns.carrier || ''} onChange={(f,v)=>setPIInsField('client_insurance', f, v)} />
                  <FieldInput label="Policy #" labelWidth={68} field="policy_number" value={piClientIns.policy_number || ''} onChange={(f,v)=>setPIInsField('client_insurance', f, v)} />
                  <FieldInput label="Claim #" labelWidth={68} field="claim_number" value={piClientIns.claim_number || ''} onChange={(f,v)=>setPIInsField('client_insurance', f, v)} />
                  <FieldInput label="Adjuster" labelWidth={68} field="adjuster_name" value={piClientIns.adjuster_name || ''} onChange={(f,v)=>setPIInsField('client_insurance', f, v)} />
                  <FieldInput label="Adj Phone" labelWidth={68} field="adjuster_phone" value={piClientIns.adjuster_phone || ''} onChange={(f,v)=>setPIInsField('client_insurance', f, v)} />
                  <FieldInput label="Adj Email" labelWidth={68} field="adjuster_email" value={piClientIns.adjuster_email || ''} onChange={(f,v)=>setPIInsField('client_insurance', f, v)} />
                  <FieldInput label="UM/UIM" labelWidth={68} field="um_uim_coverage" value={piClientIns.um_uim_coverage || ''} onChange={(f,v)=>setPIInsField('client_insurance', f, v)} />
                  <FieldInput label="PIP/MedPay" labelWidth={68} field="pip_medpay_info" value={piClientIns.pip_medpay_info || ''} onChange={(f,v)=>setPIInsField('client_insurance', f, v)} />
                  <div style={{ marginTop:'4px' }}>
                    <label style={{ fontSize:'0.72rem', color:'var(--text-muted)' }}>Notes</label>
                    <textarea className="form-textarea" style={{ minHeight:'56px', fontSize:'0.82rem', padding:'6px 8px' }}
                      value={piClientIns.notes || ''}
                      onChange={(e)=>setPIInsField('client_insurance','notes',e.target.value)} />
                  </div>
                </div>

                <div>
                  <div style={{ fontSize:'0.72rem', color:'var(--text-muted)', marginBottom:'6px', fontWeight:600 }}>
                    Defendant / At-Fault Insurance
                  </div>
                  <FieldInput label="Carrier" labelWidth={68} field="carrier" value={piAtFaultIns.carrier || ''} onChange={(f,v)=>setPIInsField('at_fault_insurance', f, v)} />
                  <FieldInput label="Policy #" labelWidth={68} field="policy_number" value={piAtFaultIns.policy_number || ''} onChange={(f,v)=>setPIInsField('at_fault_insurance', f, v)} />
                  <FieldInput label="Claim #" labelWidth={68} field="claim_number" value={piAtFaultIns.claim_number || ''} onChange={(f,v)=>setPIInsField('at_fault_insurance', f, v)} />
                  <FieldInput label="Adjuster" labelWidth={68} field="adjuster_name" value={piAtFaultIns.adjuster_name || ''} onChange={(f,v)=>setPIInsField('at_fault_insurance', f, v)} />
                  <FieldInput label="Adj Phone" labelWidth={68} field="adjuster_phone" value={piAtFaultIns.adjuster_phone || ''} onChange={(f,v)=>setPIInsField('at_fault_insurance', f, v)} />
                  <FieldInput label="Adj Email" labelWidth={68} field="adjuster_email" value={piAtFaultIns.adjuster_email || ''} onChange={(f,v)=>setPIInsField('at_fault_insurance', f, v)} />
                  <FieldInput label="Limits" labelWidth={68} field="coverage_limits" value={piAtFaultIns.coverage_limits || ''} onChange={(f,v)=>setPIInsField('at_fault_insurance', f, v)} />
                  <div style={{ marginTop:'4px' }}>
                    <label style={{ fontSize:'0.72rem', color:'var(--text-muted)' }}>Reservation / Denial Notes</label>
                    <textarea className="form-textarea" style={{ minHeight:'56px', fontSize:'0.82rem', padding:'6px 8px' }}
                      value={(piAtFaultIns.reservation_rights_notes || '')}
                      onChange={(e)=>setPIInsField('at_fault_insurance','reservation_rights_notes', e.target.value)} />
                  </div>
                </div>
              </div>
            )}
          </div>
          )}

          {/* ── OPPOSING COUNSEL (CIVIL ONLY) ── */}
          {isCivil && (
            <div className="card" style={{ padding:'14px' }}>
              <SectionTitle>Opposing Counsel</SectionTitle>
              <FieldInput label="Name" field="opposing_counsel_name" value={civilDetails.opposing_counsel_name || ''} onChange={(f,v) => setCivilDetails(p => ({...p, [f]: v}))} />
              <FieldInput label="Firm" field="opposing_counsel_firm" value={civilDetails.opposing_counsel_firm || ''} onChange={(f,v) => setCivilDetails(p => ({...p, [f]: v}))} />
              <FieldInput label="Phone" field="opposing_counsel_phone" value={civilDetails.opposing_counsel_phone || ''} onChange={(f,v) => setCivilDetails(p => ({...p, [f]: v}))} />
              <FieldInput label="Email" field="opposing_counsel_email" value={civilDetails.opposing_counsel_email || ''} onChange={(f,v) => setCivilDetails(p => ({...p, [f]: v}))} />
              <FieldInput label="Address" field="opposing_counsel_address" value={civilDetails.opposing_counsel_address || ''} onChange={(f,v) => setCivilDetails(p => ({...p, [f]: v}))} />
            </div>
          )}

          {/* ── COURT INFO (CIVIL ONLY) ── */}
          {isCivil && (
            <div className="card" style={{ padding:'14px' }}>
              <SectionTitle>Court Information</SectionTitle>
              <FieldInput label="Court" field="court_name" value={civilDetails.court_name || ''} onChange={(f,v) => setCivilDetails(p => ({...p, [f]: v}))} />
              <FieldInput label="Division" field="court_division" value={civilDetails.court_division || ''} onChange={(f,v) => setCivilDetails(p => ({...p, [f]: v}))} />
              <FieldInput label="Judge" field="judge_name" value={civilDetails.judge_name || ''} onChange={(f,v) => setCivilDetails(p => ({...p, [f]: v}))} />
              <FieldInput label="Court Case #" field="court_case_number" value={civilDetails.court_case_number || ''} onChange={(f,v) => setCivilDetails(p => ({...p, [f]: v}))} />
              <div className="claimant-pair-row" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'4px' }}>
                <FieldInput label="Filed" field="filing_date" value={civilDetails.filing_date || ''} onChange={(f,v) => setCivilDetails(p => ({...p, [f]: v}))} type="date" />
                <FieldInput label="Hearing" field="hearing_date" value={civilDetails.hearing_date || ''} onChange={(f,v) => setCivilDetails(p => ({...p, [f]: v}))} type="date" />
              </div>
              <FieldInput label="Subtype" field="civil_subtype" value={civilDetails.civil_subtype || 'general'} onChange={(f,v) => setCivilDetails(p => ({...p, [f]: v}))} options={['general','contract','property','family','employment','landlord_tenant']} />
              <FieldInput label="Cause" field="cause_of_action" value={civilDetails.cause_of_action || ''} onChange={(f,v) => setCivilDetails(p => ({...p, [f]: v}))} />
              <FieldInput label="Damages $" field="damages_claimed" value={civilDetails.damages_claimed || ''} onChange={(f,v) => setCivilDetails(p => ({...p, [f]: v}))} type="number" />
            </div>
          )}

          {/* ── BENEFIT INFORMATION (WC/DOL ONLY) ── */}
          {!isPI && !isCivil && (
            <div className="card" style={{ padding:'10px' }}>
              <SectionTitle>Benefit Information</SectionTitle>
              {renderField({ label:"Benefit Type", labelWidth:"80px", field:"benefit_type", obj:"form", options: customLists.benefit_type_options || ['TIBs','IIBs','SIBs','Lifetime Income','Death Benefits'] })}
              {renderField({ label:"Work Status", labelWidth:"80px", field:"work_status_dwc73", obj:"form", options:['Off Work','Light Duty','Full Duty Modified','Full Duty','MMI'] })}
              {renderField({ label:"AWW", labelWidth:"80px", field:"aww", obj:"form", type:"number" })}
              {renderField({ label:"70% of AWW", labelWidth:"80px", field:"benefit_rate_percent", obj:"form", type:"number" })}
              {renderField({ label:"Actual Amt.", labelWidth:"80px", field:"actual_benefit_amount", obj:"form", type:"number" })}
              {renderField({ label:"Attorney Fees", labelWidth:"80px", field:"attorney_fees", obj:"form", type:"number" })}
              {renderField({ label:"Benefits", labelWidth:"80px", field:"rec_benefits", obj:"form", options:['Yes','No'] })}

              <div style={{ marginTop:'8px' }}>
                <label style={{ fontSize:'0.72rem', color:'var(--text-muted)' }}>Certifications: MMI, IR, EOI, DIS, RTW</label>
                <textarea
                  className="form-textarea"
                  style={{ minHeight:'50px', fontSize:'0.82rem', padding:'6px 8px' }}
                  value={form.certifications || ''}
                  onChange={(e) => setForm(prev => ({...prev, certifications: e.target.value}))}
                />
              </div>

            </div>
          )}

          {/* ── LEGAL INFORMATION ── */}
          <div className="card" style={{ padding:'14px' }}>
            <SectionTitle>Legal Information</SectionTitle>

            <FieldInput
              label="Attorney"
              field="assigned_attorney"
              value={form.assigned_attorney || ''}
              onChange={handlers.form}
              options={attorneyOptions}
            />

            {/* Assist — multi-select up to 2 staff */}
            <div style={{ display:'flex', alignItems:'flex-start', gap:'6px', marginBottom:'6px' }}>
              <label style={{ fontSize:'0.75rem', color:'var(--text-muted)', minWidth:'90px', textAlign:'right', flexShrink:0, paddingTop:'7px' }}>Assist</label>
              <div ref={assistRef} style={{ flex:1, position:'relative' }}>
                <div
                  onClick={() => setShowAssistDropdown(!showAssistDropdown)}
                  style={{
                    padding:'5px 8px', fontSize:'0.82rem', cursor:'pointer',
                    background:'var(--zeus-navy-light)', border:'1px solid var(--zeus-border)',
                    borderRadius:'var(--radius-sm)', minHeight:'30px',
                    display:'flex', flexWrap:'wrap', gap:'4px', alignItems:'center',
                  }}
                >
                  {assistValues.length === 0 && <span style={{ color:'var(--text-muted)', fontSize:'0.82rem' }}>Select up to 2...</span>}
                  {assistValues.map((v, i) => (
                    <span key={i} style={{
                      display:'inline-flex', alignItems:'center', gap:'4px',
                      padding:'1px 8px', background:'var(--zeus-gold-dim)',
                      borderRadius:'3px', fontSize:'0.78rem', color:'var(--text-primary)',
                    }}>
                      {v}
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          const updated = assistValues.filter((_, idx) => idx !== i);
                          setForm(prev => ({ ...prev, assist: updated }));
                        }}
                        style={{ cursor:'pointer', color:'var(--text-muted)', fontWeight:700, fontSize:'0.82rem', lineHeight:1 }}
                      >×</span>
                    </span>
                  ))}
                </div>
                {showAssistDropdown && (
                  <div style={{
                    position:'absolute', top:'100%', left:0, right:0, marginTop:'2px',
                    background:'var(--zeus-navy)', border:'1px solid var(--zeus-border)',
                    borderRadius:'var(--radius-sm)', maxHeight:'150px', overflowY:'auto', zIndex:50,
                    boxShadow:'0 4px 16px rgba(0,0,0,0.3)',
                  }}>
                    {staffOptions.filter(s => !assistValues.includes(s.value)).map(s => (
                      <div
                        key={s.value}
                        onClick={() => {
                          if (assistValues.length < 2) {
                            const updated = [...assistValues, s.value];
                            setForm(prev => ({ ...prev, assist: updated }));
                            if (updated.length >= 2) setShowAssistDropdown(false);
                          }
                        }}
                        style={{
                          padding:'6px 10px', fontSize:'0.82rem', cursor: assistValues.length >= 2 ? 'not-allowed' : 'pointer',
                          color: assistValues.length >= 2 ? 'var(--text-muted)' : 'var(--text-primary)',
                          transition:'background 0.1s',
                        }}
                        onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                        onMouseOut={(e) => e.currentTarget.style.background = ''}
                      >
                        {s.label}
                      </div>
                    ))}
                    {staffOptions.filter(s => !assistValues.includes(s.value)).length === 0 && (
                      <div style={{ padding:'8px 10px', fontSize:'0.82rem', color:'var(--text-muted)' }}>No more staff available</div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'4px' }}>
              {renderField({ label:"Status", field:"claim_status", obj:"form", options: customLists.legal_status_options })}
              {/* Type — locked once set; editable only on first save */}
              {c?.claim_type ? (
                <div style={{ display:'flex', alignItems:'center', gap:'6px', marginBottom:'6px' }}>
                  <label style={{ fontSize:'0.75rem', color:'var(--text-muted)', minWidth:'90px', textAlign:'right', flexShrink:0 }}>
                    Type <span style={{ color:'var(--zeus-gold)' }}>*</span>
                  </label>
                  <div style={{ flex:1, display:'flex', alignItems:'center', gap:'8px' }}>
                    <span style={{
                      padding:'5px 10px', fontSize:'0.82rem', fontWeight:700,
                      background:'var(--zeus-navy-light)', border:'1px solid var(--zeus-border)',
                      borderRadius:'var(--radius-sm)', color:'var(--text-primary)',
                    }}>
                      {c.claim_type}
                    </span>
                    <span style={{ fontSize:'0.7rem', color:'var(--text-muted)', display:'flex', alignItems:'center', gap:'4px' }}>
                      <span style={{ fontSize:'0.65rem' }}>&#128274;</span>
                    </span>
                  </div>
                </div>
              ) : (
                renderField({ label:"Type", field:"claim_type", obj:"form", options: customLists.legal_type_options, required:true })
              )}
            </div>
            {/* Referred By — searchable provider dropdown */}
            <div style={{ display:'flex', alignItems:'center', gap:'6px', marginBottom:'6px' }}>
              <label style={{ fontSize:'0.75rem', color:'var(--text-muted)', minWidth:'90px', textAlign:'right', flexShrink:0 }}>Referred by</label>
              <div ref={refByRef} style={{ flex:1, position:'relative' }}>
                <div
                  onClick={() => { setRefByOpen(true); setRefBySearch(''); setTimeout(() => refByInputRef.current?.focus(), 50); }}
                  style={{
                    display:'flex', alignItems:'center', gap:'6px',
                    padding:'5px 8px', fontSize:'0.82rem',
                    border:'1px solid var(--zeus-border)', borderRadius:'var(--radius-sm)',
                    background:'var(--zeus-navy-light)', cursor:'pointer', minHeight:'30px',
                  }}
                >
                  <Search size={13} style={{ color:'var(--text-muted)', flexShrink:0 }} />
                  {refByOpen ? (
                    <input
                      ref={refByInputRef}
                      className="form-input"
                      style={{ border:'none', outline:'none', background:'transparent', padding:0, fontSize:'0.82rem', flex:1, color:'var(--text-primary)' }}
                      placeholder="Search providers..."
                      value={refBySearch}
                      onChange={(e) => setRefBySearch(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Escape') setRefByOpen(false); }}
                    />
                  ) : (
                    <span style={{ flex:1, color: form.referred_by ? 'var(--text-primary)' : 'var(--text-muted)', fontSize:'0.82rem' }}>
                      {form.referred_by || 'Select provider...'}
                    </span>
                  )}
                  <ChevronDown size={14} style={{ color:'var(--text-muted)', flexShrink:0, transform: refByOpen ? 'rotate(180deg)' : 'none', transition:'transform 0.2s' }} />
                </div>

                {refByOpen && (
                  <div style={{
                    position:'absolute', top:'100%', left:0, right:0, marginTop:'2px',
                    background:'var(--zeus-navy)', border:'1px solid var(--zeus-border)',
                    borderRadius:'var(--radius-md)', boxShadow:'0 8px 24px rgba(0,0,0,0.4)',
                    zIndex:80, maxHeight:'220px', overflowY:'auto',
                  }}>
                    {/* Clear option */}
                    {form.referred_by && (
                      <div
                        onClick={() => { setForm(prev => ({ ...prev, referred_by: '' })); setRefByOpen(false); setRefBySearch(''); }}
                        style={{ padding:'6px 12px', fontSize:'0.8rem', color:'var(--text-muted)', cursor:'pointer', borderBottom:'1px solid var(--zeus-border)', fontStyle:'italic' }}
                        onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                        onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                      >
                        — Clear —
                      </div>
                    )}
                    {/* Provider list */}
                    {providers
                      .filter(p => {
                        const q = refBySearch.toLowerCase();
                        if (!q) return true;
                        return p.name?.toLowerCase().includes(q) || (p.provider_type || '').toLowerCase().includes(q) || (p.specialty || '').toLowerCase().includes(q);
                      })
                      .map(p => (
                        <div
                          key={p._id}
                          onClick={() => { setForm(prev => ({ ...prev, referred_by: p.name })); setRefByOpen(false); setRefBySearch(''); }}
                          style={{
                            padding:'7px 12px', cursor:'pointer', fontSize:'0.82rem',
                            borderBottom:'1px solid rgba(255,255,255,0.03)',
                            background: form.referred_by === p.name ? 'var(--zeus-gold-dim)' : 'transparent',
                          }}
                          onMouseOver={(e) => { if (form.referred_by !== p.name) e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
                          onMouseOut={(e) => { if (form.referred_by !== p.name) e.currentTarget.style.background = 'transparent'; }}
                        >
                          <div style={{ color:'var(--text-primary)', fontWeight: form.referred_by === p.name ? 600 : 400 }}>{p.name}</div>
                          {(p.provider_type || p.specialty) && (
                            <div style={{ fontSize:'0.72rem', color:'var(--text-muted)', marginTop:'1px' }}>
                              {p.provider_type}{p.specialty ? ` · ${p.specialty}` : ''}
                            </div>
                          )}
                        </div>
                      ))
                    }
                    {providers.filter(p => { const q = refBySearch.toLowerCase(); return !q || p.name?.toLowerCase().includes(q) || (p.provider_type || '').toLowerCase().includes(q); }).length === 0 && (
                      <div style={{ padding:'12px', textAlign:'center', color:'var(--text-muted)', fontSize:'0.82rem' }}>
                        No providers found
                      </div>
                    )}
                    {/* Add New button */}
                    <div
                      onClick={() => {
                        setQuickProviderForm(prev => ({ ...prev, name: refBySearch || '' }));
                        setQuickProviderTarget('referred_by');
                        setShowQuickProvider(true);
                      }}
                      style={{
                        padding:'8px 12px', cursor:'pointer', fontSize:'0.8rem',
                        borderTop:'1px solid var(--zeus-border)', color:'var(--zeus-gold)',
                        display:'flex', alignItems:'center', gap:'6px', fontWeight:600,
                      }}
                      onMouseOver={(e) => e.currentTarget.style.background = 'rgba(212,168,83,0.08)'}
                      onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                      <Plus size={14} /> Add New Provider
                    </div>
                  </div>
                )}
              </div>
            </div>
            {renderField({ label:"Referred Date", field:"ref_date", obj:"form", type:"date" })}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'4px' }}>
              {renderField({ label:"AF Fee", field:"af_fee", obj:"form", options: customLists.af_fee_options })}
              {renderField({ label:"Screener", field:"screener", obj:"form", options: staffOptions })}
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'4px' }}>
              {renderField({ label:"Consult Date", field:"consult_date", obj:"form", type:"date" })}
              {renderField({ label:lorLabel, field:"lor_faxed", obj:"form", type:"date" })}
            </div>
            <div style={{ display:'flex', justifyContent:'flex-end', marginTop:'4px' }}>
              {renderField({ label:"Initial billing done?", field:"initial_billing_done", obj:"form", checkbox:true })}
            </div>

            <div style={{ marginTop:'8px' }}>
              <label style={{ fontSize:'0.72rem', color:'var(--text-muted)' }}>Attorney Notes and Status</label>
              <textarea
                className="form-textarea"
                style={{ minHeight:'80px', fontSize:'0.82rem', padding:'6px 8px' }}
                value={form.attorney_notes_status || ''}
                onChange={(e) => setForm(prev => ({...prev, attorney_notes_status: e.target.value}))}
              />
            </div>
          </div>
        </div>

        {/* ═══════════════════ TABS ═══════════════════ */}
        <div className="tabs">
          {[
            ...(!isCivil ? [{key:'employer_case', label:'Employer/Case Info'}] : []),
            ...(isCivil ? [{key:'civil_details', label:'Case Details'}] : []),
            {key:'treatments', label:'Medical Info'},
            ...(!isCivil ? [{key:'treatment', label:'Treatment'}] : []),
            ...(form.is_deceased ? [{key:'deceased', label:'Deceased Case'}] : []),
            {key:'notes', label:'Notes'},
            {key:'documents', label:'Documents'},
            {key:'communication', label:'Communication'},
            {key:'billing', label:'Billing'},
            {key:'history', label:'History'},
          ].map(t => (
            <button
              key={t.key}
              className={`tab ${activeTab === t.key ? 'active' : ''}`}
              onClick={() => setActiveTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* ═══════════════════ CIVIL DETAILS TAB ═══════════════════ */}
        {activeTab === 'civil_details' && isCivil && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            {/* Cause of Action */}
            <div className="card">
              <div className="card-header"><span style={{ fontWeight: 700, fontSize: '0.82rem' }}>Cause of Action</span></div>
              <div style={{ padding: '14px' }}>
                <FieldInput label="Subtype" field="civil_subtype" value={civilDetails.civil_subtype || 'general'}
                  onChange={(f,v) => setCivilDetails(p => ({...p, [f]: v}))}
                  options={['general','contract','property','family','employment','landlord_tenant']} />
                <div style={{ marginTop: 8 }}>
                  <label style={{ fontSize:'0.75rem', color:'var(--text-muted)', display:'block', marginBottom:4 }}>Description</label>
                  <textarea className="form-textarea" style={{ minHeight: 120, fontSize:'0.82rem', padding:'8px' }}
                    value={civilDetails.cause_of_action || ''}
                    onChange={e => setCivilDetails(p => ({...p, cause_of_action: e.target.value}))}
                    placeholder="Describe the cause of action, claims, and legal theories..." />
                </div>
              </div>
            </div>

            {/* Damages */}
            <div className="card">
              <div className="card-header"><span style={{ fontWeight: 700, fontSize: '0.82rem' }}>Damages</span></div>
              <div style={{ padding: '14px' }}>
                <FieldInput label="Amount Claimed" field="damages_claimed" value={civilDetails.damages_claimed || ''} type="number"
                  onChange={(f,v) => setCivilDetails(p => ({...p, [f]: v}))} />
                <div style={{ marginTop: 8 }}>
                  <label style={{ fontSize:'0.75rem', color:'var(--text-muted)', display:'block', marginBottom:4 }}>Damages Breakdown</label>
                  <textarea className="form-textarea" style={{ minHeight: 120, fontSize:'0.82rem', padding:'8px' }}
                    value={civilDetails.damages_description || ''}
                    onChange={e => setCivilDetails(p => ({...p, damages_description: e.target.value}))}
                    placeholder="Describe compensatory, punitive, and/or special damages sought..." />
                </div>
              </div>
            </div>

            {/* Court Timeline */}
            <div className="card">
              <div className="card-header"><span style={{ fontWeight: 700, fontSize: '0.82rem' }}>Court Timeline</span></div>
              <div style={{ padding: '14px' }}>
                <FieldInput label="Filing Date" field="filing_date" value={civilDetails.filing_date || ''} type="date"
                  onChange={(f,v) => setCivilDetails(p => ({...p, [f]: v}))} />
                <FieldInput label="Next Hearing" field="hearing_date" value={civilDetails.hearing_date || ''} type="date"
                  onChange={(f,v) => setCivilDetails(p => ({...p, [f]: v}))} />
                <FieldInput label="Court" field="court_name" value={civilDetails.court_name || ''}
                  onChange={(f,v) => setCivilDetails(p => ({...p, [f]: v}))} />
                <FieldInput label="Judge" field="judge_name" value={civilDetails.judge_name || ''}
                  onChange={(f,v) => setCivilDetails(p => ({...p, [f]: v}))} />
                <FieldInput label="Court Case #" field="court_case_number" value={civilDetails.court_case_number || ''}
                  onChange={(f,v) => setCivilDetails(p => ({...p, [f]: v}))} />
              </div>
            </div>

            {/* Defendant Summary */}
            <div className="card">
              <div className="card-header"><span style={{ fontWeight: 700, fontSize: '0.82rem' }}>Defendant</span></div>
              <div style={{ padding: '14px' }}>
                <FieldInput label="Name" field="defendant_name" value={civilDetails.defendant_name || ''}
                  onChange={(f,v) => setCivilDetails(p => ({...p, [f]: v}))} />
                <FieldInput label="Attorney" field="defendant_attorney" value={civilDetails.defendant_attorney || ''}
                  onChange={(f,v) => setCivilDetails(p => ({...p, [f]: v}))} />
                <FieldInput label="Phone" field="defendant_phone" value={civilDetails.defendant_phone || ''}
                  onChange={(f,v) => setCivilDetails(p => ({...p, [f]: v}))} />
                <FieldInput label="Email" field="defendant_email" value={civilDetails.defendant_email || ''}
                  onChange={(f,v) => setCivilDetails(p => ({...p, [f]: v}))} />
              </div>
            </div>
          </div>
        )}

        {/* ═══════════════════ EMPLOYER/CASE INFO TAB ═══════════════════ */}
        {activeTab === 'employer_case' && (
          <>
            <div className="employer-case-grid" style={{ display:'grid', gridTemplateColumns:'1fr 0.8fr 1fr 1fr 1fr', gap:'12px', marginBottom:'12px' }}>
              {/* Employer */}
              <div className="card" style={{ padding:'14px' }}>
                <SectionTitle>Employer</SectionTitle>
                {renderField({ label:"Company", field:"company", obj:"emp" })}
                {renderField({ label:"Address", field:"address", obj:"emp" })}
                <CityStateZipRow obj="emp" />
                {renderField({ label:"Phone", field:"phone", obj:"emp" })}
                <div style={{ marginTop:'6px' }}>
                  <label style={{ fontSize:'0.72rem', color:'var(--text-muted)' }}>Notes</label>
                  <textarea
                    className="form-textarea"
                    style={{ minHeight:'60px', fontSize:'0.82rem', padding:'6px 8px' }}
                    value={empForm.notes || ''}
                    onChange={(e) => setEmpForm(prev => ({...prev, notes: e.target.value}))}
                  />
                </div>
              </div>

              {/* Statute of Limitations — PI only, hidden for WC */}
              {isPI && <div className="card" style={{ padding:'14px' }}>
                <SectionTitle>Statute of Limitations</SectionTitle>

                {/* Warning Badge */}
                {solBadge && (
                  <div style={{
                    display:'flex', alignItems:'center', gap:'8px',
                    padding:'8px 10px', borderRadius:'var(--radius-sm)',
                    background: solBadge.bg, marginBottom:'10px',
                  }}>
                    <span style={{
                      width:'10px', height:'10px', borderRadius:'50%',
                      background: solBadge.color, flexShrink:0,
                    }} />
                    <span style={{ fontSize:'0.78rem', fontWeight:700, color: solBadge.color }}>
                      {solBadge.label}
                    </span>
                    <span style={{ fontSize:'0.72rem', color:'var(--text-muted)', marginLeft:'auto' }}>
                      {solBadge.days < 0
                        ? `${Math.abs(solBadge.days)} day${Math.abs(solBadge.days) !== 1 ? 's' : ''} past`
                        : `${solBadge.days} day${solBadge.days !== 1 ? 's' : ''} left`}
                    </span>
                  </div>
                )}

                {/* SOL Date */}
                <div style={{ display:'flex', alignItems:'center', gap:'6px', marginBottom:'8px' }}>
                  <label style={{ fontSize:'0.75rem', color:'var(--text-muted)', minWidth:'58px', textAlign:'right', flexShrink:0 }}>SOL Date</label>
                  <input
                    className="form-input"
                    type="date"
                    style={{ flex:1, fontSize:'0.82rem', padding:'5px 8px', fontWeight:600,
                      color: solBadge ? solBadge.color : 'var(--text-primary)',
                    }}
                    value={form.sol_date || ''}
                    readOnly
                  />
                </div>

                {/* Rule (from org settings) */}
                <div style={{ display:'flex', alignItems:'center', gap:'6px', marginBottom:'8px' }}>
                  <label style={{ fontSize:'0.75rem', color:'var(--text-muted)', minWidth:'58px', textAlign:'right', flexShrink:0 }}>Rule</label>
                  <span style={{
                    padding:'4px 10px', fontSize:'0.82rem', fontWeight:600,
                    background:'var(--zeus-navy-light)', border:'1px solid var(--zeus-border)',
                    borderRadius:'var(--radius-sm)', color:'var(--text-primary)',
                  }}>
                    {getSolRuleDisplay()}
                  </span>
                </div>

                {/* Minor */}
                <div style={{ display:'flex', alignItems:'center', gap:'6px', marginBottom:'8px' }}>
                  <label style={{ fontSize:'0.75rem', color:'var(--text-muted)', minWidth:'58px', textAlign:'right', flexShrink:0 }}>Minor</label>
                  <span style={{
                    padding:'2px 10px', borderRadius:'10px', fontSize:'0.7rem', fontWeight:700,
                    background: form.sol_is_minor ? 'rgba(249,115,22,0.15)' : 'rgba(34,197,94,0.12)',
                    color: form.sol_is_minor ? '#f97316' : '#22c55e',
                  }}>
                    {form.sol_is_minor ? 'Minor' : 'No'}
                  </span>
                  {form.sol_is_minor && (
                    <span style={{ fontSize:'0.7rem', color:'#f97316', fontWeight:700 }}>Under 18</span>
                  )}
                </div>
              </div>}

            </div>

          </>
        )}

        {/* ═══════════════════ TREATMENT TAB ═══════════════════ */}
        {activeTab === 'treatment' && (
          <div className="treatment-tab-layout" style={{ display:'flex', gap:'16px', alignItems:'flex-start' }}>

            {/* ── LEFT SIDE: Treatment Entries ── */}
            <div style={{ flex:1, display:'flex', flexDirection:'column', gap:'14px', minWidth:0 }}>
              {(() => {
                const currentProviders = (c.treatments || []).filter(t => (t.treating_status || 'current') === 'current');

                // Separate PT grouped sessions from individual entries
                const ptGroups = {};
                const singles = [];
                consultations.forEach((entry, idx) => {
                  if (entry.therapy_group) {
                    if (!ptGroups[entry.therapy_group]) ptGroups[entry.therapy_group] = { sessions: [], type: entry.appointment_type, provider: entry.provider_name, total: entry.total_sessions || 0 };
                    ptGroups[entry.therapy_group].sessions.push({ ...entry, globalIdx: idx });
                  } else {
                    singles.push({ ...entry, globalIdx: idx });
                  }
                });

                // Group singles by appointment_type
                const groupedSingles = {};
                singles.forEach(s => {
                  const key = s.appointment_type || 'Other';
                  if (!groupedSingles[key]) groupedSingles[key] = [];
                  groupedSingles[key].push(s);
                });

                const hasAnything = singles.length > 0 || Object.keys(ptGroups).length > 0;

                /* ── Render a Show/No-Show status row ── */
                const renderStatusRow = (entry, gIdx, dateField, statusField, reschDateField, reschStatusField, level) => {
                  const dateVal = toDateVal(entry[dateField]);
                  const noDate = !dateVal;
                  const statusVal = noDate ? 'not_scheduled' : (entry[statusField] || (level === 0 ? 'scheduled' : ''));
                  // Treat as complete if either flag is set — they can be out of sync
                  const isDone = entry.completed === true || entry.record_uploaded === true;

                  const hasReschedule = statusVal === 'no_show' && reschDateField && !isDone;
                  const isShow = statusVal === 'show';
                  const canUpload = isShow && !entry.record_uploaded && !isDone;

                  return (
                    <div style={{ marginTop: level > 0 ? '6px' : 0, paddingTop: level > 0 ? '6px' : 0, borderTop: level > 0 ? '1px dashed rgba(239,68,68,0.2)' : 'none' }}>
                      {level > 0 && <div style={{ fontSize:'0.65rem', fontWeight:700, color: level === 1 ? '#ef4444' : '#f97316', marginBottom:'4px', textTransform:'uppercase' }}>Rescheduled{level > 1 ? ' Again' : ''}</div>}
                      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr auto', gap:'8px', alignItems:'end' }}>
                        <div>
                          <label style={{ fontSize:'0.65rem', color:'var(--text-muted)', fontWeight:600, display:'block', marginBottom:'2px' }}>{level > 0 ? 'New Date' : 'Appt Date'}</label>
                          {isDone ? (
                            <div className="form-input" style={{ fontSize:'0.8rem', padding:'4px 6px', background:'rgba(255,255,255,0.03)', cursor:'default', opacity:0.7 }}>
                              {dateVal ? new Date(dateVal + 'T00:00:00').toLocaleDateString('en-US', { month:'2-digit', day:'2-digit', year:'numeric' }) : '—'}
                            </div>
                          ) : (
                            <input type="date" className="form-input" style={{ fontSize:'0.8rem', padding:'4px 6px' }}
                              value={dateVal}
                              onChange={(e) => {
                                const updates = { [dateField]: e.target.value };
                                if (level > 0 && e.target.value && !entry[statusField]) updates[statusField] = 'scheduled';
                                if (level === 0 && e.target.value && entry[statusField] === '') updates[statusField] = 'scheduled';
                                updateConsultation(gIdx, updates);
                              }} />
                          )}
                        </div>
                        <div>
                          <label style={{ fontSize:'0.65rem', color:'var(--text-muted)', fontWeight:600, display:'block', marginBottom:'2px' }}>Status</label>
                          {noDate ? (
                            <div style={{
                              padding:'4px 8px', fontSize:'0.8rem', borderRadius:'var(--radius-sm)',
                              background:'rgba(255,255,255,0.04)', border:'1px solid var(--zeus-border)',
                              color:'var(--text-muted)', fontStyle:'italic',
                            }}>
                              Not Scheduled
                            </div>
                          ) : isDone ? (
                            <div className="form-input" style={{ fontSize:'0.8rem', padding:'4px 6px', background:'rgba(255,255,255,0.03)', cursor:'default', opacity:0.7, color: statusVal === 'show' ? '#22c55e' : statusVal === 'no_show' ? '#ef4444' : 'var(--text-muted)' }}>
                              {statusVal === 'show' ? 'Show' : statusVal === 'no_show' ? 'No Show' : statusVal === 'scheduled' ? 'Scheduled' : statusVal || '—'}
                            </div>
                          ) : (
                            <select className="form-select" style={{ fontSize:'0.8rem', padding:'4px 6px' }}
                              value={statusVal} disabled={level > 0 && !dateVal}
                              onChange={(e) => {
                                const updates = { [statusField]: e.target.value };
                                if (e.target.value !== 'no_show' && reschDateField) {
                                  updates[reschDateField] = '';
                                  if (reschStatusField) updates[reschStatusField] = '';
                                }
                                updateConsultation(gIdx, updates);
                              }}>
                              {level > 0 && <option value="">—</option>}
                              <option value="scheduled">Scheduled</option>
                              <option value="show">Show</option>
                              <option value="no_show">No Show</option>
                            </select>
                          )}
                        </div>
                        <div style={{ paddingBottom:'1px' }}>
                          {canUpload && (
                            <>
                              <input type="file" style={{ display:'none' }} id={`upload-${gIdx}-${level}`}
                                onChange={(e) => handleConsultUpload(gIdx, e.target.files[0])} />
                              <button onClick={() => document.getElementById(`upload-${gIdx}-${level}`)?.click()}
                                style={{
                                  display:'flex', alignItems:'center', gap:'4px', padding:'5px 10px',
                                  fontSize:'0.75rem', fontWeight:600, borderRadius:'var(--radius-sm)',
                                  background:'rgba(59,130,246,0.1)', color:'#3b82f6', border:'1px solid rgba(59,130,246,0.15)',
                                  cursor:'pointer', whiteSpace:'nowrap',
                                }}>
                                <UploadCloud size={12} /> Upload Record
                              </button>
                            </>
                          )}
                          {isShow && entry.record_uploaded && (
                            <div style={{ display:'flex', alignItems:'center', gap:'4px' }}>
                              <span style={{ fontSize:'0.72rem', color:'#22c55e', fontWeight:600, display:'flex', alignItems:'center', gap:'3px', whiteSpace:'nowrap' }}>
                                <CheckCircle size={11} /> Complete
                              </span>
                              <button
                                onClick={(e) => { e.stopPropagation(); setViewRecordEntry(entry); }}
                                style={{
                                  display:'flex', alignItems:'center', gap:'3px',
                                  fontSize:'0.65rem', fontWeight:600, padding:'2px 7px',
                                  background:'rgba(59,130,246,0.1)', color:'#60a5fa',
                                  border:'1px solid rgba(59,130,246,0.25)', borderRadius:'8px',
                                  cursor:'pointer', whiteSpace:'nowrap',
                                }}
                                title="View associated document"
                              >
                                <FileText size={10} /> View File
                              </button>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Recursive: if no_show, show reschedule row */}
                      {hasReschedule && !entry.completed && (
                        renderStatusRow(entry, gIdx, reschDateField, reschStatusField,
                          reschStatusField === 'reschedule_status' ? 'reschedule2_date' : null,
                          reschStatusField === 'reschedule_status' ? 'reschedule2_status' : null,
                          level + 1)
                      )}
                    </div>
                  );
                };

                /* ── Render a single appointment card ── */
                const renderAppointmentCard = (entry, compact) => {
                  const gIdx = entry.globalIdx;
                  const isDone = entry.completed === true || entry.record_uploaded === true;

                  // Completed — render as fully static read-only card, zero interactive elements
                  if (isDone) {
                    const dateDisplay = entry.appointment_date
                      ? new Date(entry.appointment_date + 'T00:00:00').toLocaleDateString('en-US', { month:'2-digit', day:'2-digit', year:'numeric' })
                      : '—';
                    const statusDisplay = entry.status === 'show' ? 'Show'
                      : entry.status === 'no_show' ? 'No Show'
                      : entry.status === 'scheduled' ? 'Scheduled'
                      : entry.status || '—';
                    return (
                      <div key={gIdx} style={{
                        padding: compact ? '8px 12px' : '12px 14px', borderRadius:'var(--radius-sm)',
                        border:'1px solid rgba(34,197,94,0.25)', background:'rgba(34,197,94,0.03)',
                      }}>
                        <div style={{ display:'flex', alignItems:'center', gap:'6px', marginBottom:'8px' }}>
                          <CheckCircle size={14} style={{ color:'#22c55e', flexShrink:0 }} />
                          <span style={{ fontSize:'0.8rem', flex:1, color:'var(--text-secondary)', fontWeight:600 }}>
                            {entry.provider_name || '—'}
                          </span>
                          <span style={{ fontSize:'0.62rem', fontWeight:700, color:'#22c55e', padding:'1px 6px', background:'rgba(34,197,94,0.1)', borderRadius:'8px' }}>COMPLETE</span>
                          <button onClick={(e) => { e.stopPropagation(); setViewRecordEntry(entry); }}
                            style={{ display:'flex', alignItems:'center', gap:'3px', fontSize:'0.65rem', fontWeight:600, padding:'1px 7px', background:'rgba(59,130,246,0.1)', color:'#60a5fa', border:'1px solid rgba(59,130,246,0.25)', borderRadius:'8px', cursor:'pointer', whiteSpace:'nowrap' }}>
                            <FileText size={10} /> View File
                          </button>
                        </div>
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr auto', gap:'8px', alignItems:'end' }}>
                          <div>
                            <label style={{ fontSize:'0.65rem', color:'var(--text-muted)', fontWeight:600, display:'block', marginBottom:'2px' }}>Appt Date</label>
                            <div style={{ padding:'4px 6px', fontSize:'0.8rem', color:'var(--text-secondary)', background:'rgba(255,255,255,0.03)', border:'1px solid var(--zeus-border)', borderRadius:'var(--radius-sm)' }}>{dateDisplay}</div>
                          </div>
                          <div>
                            <label style={{ fontSize:'0.65rem', color:'var(--text-muted)', fontWeight:600, display:'block', marginBottom:'2px' }}>Status</label>
                            <div style={{ padding:'4px 6px', fontSize:'0.8rem', fontWeight:600, background:'rgba(255,255,255,0.03)', border:'1px solid var(--zeus-border)', borderRadius:'var(--radius-sm)', color: entry.status === 'show' ? '#22c55e' : entry.status === 'no_show' ? '#ef4444' : 'var(--text-muted)' }}>{statusDisplay}</div>
                          </div>
                          <span style={{ fontSize:'0.72rem', color:'#22c55e', fontWeight:600, display:'flex', alignItems:'center', gap:'3px' }}><CheckCircle size={11} /> Complete</span>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div key={gIdx} style={{
                      padding: compact ? '8px 12px' : '12px 14px', borderRadius:'var(--radius-sm)',
                      border:'1px solid var(--zeus-border)',
                      background:'var(--zeus-navy-light)',
                    }}>

                      {/* Header: icon + provider select + delete */}
                      <div style={{ display:'flex', alignItems:'center', gap:'6px', marginBottom:'8px' }}>
                        {entry.status === 'no_show'
                          ? <XCircle size={14} style={{ color:'#ef4444', flexShrink:0 }} />
                          : <Clock size={14} style={{ color:'var(--zeus-gold)', flexShrink:0 }} />}
                        {compact && entry.session_label && (
                          <span style={{ fontSize:'0.75rem', fontWeight:700, color:'var(--text-primary)', flexShrink:0 }}>{entry.session_label}</span>
                        )}
                        <select className="form-select" style={{ fontSize:'0.8rem', padding:'3px 6px', flex:1, minWidth:0 }}
                          value={entry.provider_name || ''}
                          onChange={(e) => {
                            const p = currentProviders.find(p => p.provider_name === e.target.value);
                            updateConsultation(gIdx, { provider_name: e.target.value, provider_type: p?.provider_type || '' });
                          }}>
                          <option value="">Select provider...</option>
                          {currentProviders.map((p, i) => <option key={i} value={p.provider_name}>{p.provider_name}{p.provider_type ? ` — ${p.provider_type}` : ''}</option>)}
                        </select>
                        <button onClick={() => removeConsultation(gIdx)} style={{ background:'none', border:'none', cursor:'pointer', color:'#ef4444', padding:'2px', opacity:0.6, flexShrink:0 }}>
                          <Trash2 size={12} />
                        </button>
                      </div>

                      {/* Status rows (with recursive reschedule) */}
                      {renderStatusRow(entry, gIdx, 'appointment_date', 'status', 'reschedule_date', 'reschedule_status', 0)}
                    </div>
                  );
                };

                return (
                  <>
                    {!hasAnything && (
                      <div className="card" style={{ padding:'40px 20px', textAlign:'center', color:'var(--text-muted)' }}>
                        <Stethoscope size={28} style={{ opacity:0.3, marginBottom:'8px' }} />
                        <div style={{ fontSize:'0.9rem', marginBottom:'4px' }}>No treatments added yet</div>
                        <div style={{ fontSize:'0.78rem' }}>Select a treatment type from the right to get started.</div>
                      </div>
                    )}

                    {/* Render single-entry groups (Initial Consultation, Follow-Up, MRI, etc.) */}
                    {Object.entries(groupedSingles).map(([type, entries]) => (
                      <div key={type} className="card" style={{ padding:'14px' }}>
                        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'10px' }}>
                          <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                            <Stethoscope size={15} style={{ color:'var(--zeus-gold)' }} />
                            <span style={{ fontWeight:700, fontSize:'0.88rem', color:'var(--text-primary)' }}>{type}</span>
                            <span style={{ fontSize:'0.68rem', color:'var(--text-muted)', padding:'1px 8px', background:'rgba(255,255,255,0.05)', borderRadius:'10px' }}>
                              {entries.filter(e => e.completed).length}/{entries.length}
                            </span>
                          </div>
                          <button onClick={() => addTreatmentEntry(type)}
                            style={{ display:'flex', alignItems:'center', gap:'3px', padding:'3px 8px', fontSize:'0.7rem', fontWeight:600, borderRadius:'var(--radius-sm)', background:'rgba(255,255,255,0.04)', color:'var(--text-muted)', border:'1px solid var(--zeus-border)', cursor:'pointer' }}>
                            <Plus size={11} /> Add
                          </button>
                        </div>
                        <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
                          {entries.map(entry => renderAppointmentCard(entry, false))}
                        </div>
                      </div>
                    ))}

                    {/* Render PT / session-based groups */}
                    {Object.entries(ptGroups).map(([gKey, group]) => {
                      const completedCount = group.sessions.filter(s => s.completed).length;
                      const progress = group.total > 0 ? Math.round((completedCount / group.total) * 100) : 0;
                      return (
                        <div key={gKey} className="card" style={{ padding:0, overflow:'hidden' }}>
                          {/* Group header */}
                          <div style={{
                            display:'flex', alignItems:'center', justifyContent:'space-between',
                            padding:'10px 14px', borderBottom:'1px solid var(--zeus-border)',
                          }}>
                            <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                              <ClipboardList size={15} style={{ color:'#3b82f6' }} />
                              <span style={{ fontWeight:700, fontSize:'0.88rem', color:'var(--text-primary)' }}>{group.type}</span>
                              <span style={{ fontSize:'0.72rem', color:'var(--text-muted)' }}>— {group.provider}</span>
                              <span style={{ fontSize:'0.68rem', fontWeight:700, color:'#3b82f6', padding:'1px 8px', background:'rgba(59,130,246,0.08)', borderRadius:'10px' }}>
                                {completedCount}/{group.total}
                              </span>
                            </div>
                            <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                              <div style={{ width:'80px', height:'5px', background:'rgba(255,255,255,0.08)', borderRadius:'3px', overflow:'hidden' }}>
                                <div style={{ width:`${progress}%`, height:'100%', background: progress === 100 ? '#22c55e' : '#3b82f6', borderRadius:'3px', transition:'width 0.3s' }} />
                              </div>
                              <span style={{ fontSize:'0.68rem', fontWeight:700, color: progress === 100 ? '#22c55e' : '#3b82f6' }}>{progress}%</span>
                              <button onClick={() => removePTGroup(gKey)} style={{ background:'none', border:'none', cursor:'pointer', color:'#ef4444', padding:'2px', opacity:0.6 }}><Trash2 size={12} /></button>
                            </div>
                          </div>
                          {/* Session rows */}
                          <div style={{ padding:'8px 14px', display:'flex', flexDirection:'column', gap:'6px' }}>
                            {group.sessions.map(s => renderAppointmentCard(s, true))}
                          </div>
                        </div>
                      );
                    })}

                    {/* PT / Session Order Form (inline) */}
                    {showPtOrder && (
                      <div className="card" style={{ padding:'14px', border:'1px solid rgba(59,130,246,0.2)' }}>
                        <div style={{ fontSize:'0.75rem', fontWeight:700, color:'#3b82f6', marginBottom:'10px' }}>NEW SESSION ORDER</div>
                        <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr', gap:'10px', alignItems:'end' }}>
                          <div>
                            <label style={{ fontSize:'0.68rem', color:'var(--text-muted)', fontWeight:600, display:'block', marginBottom:'3px' }}>Provider *</label>
                            <select className="form-select" style={{ fontSize:'0.82rem', padding:'5px 8px' }}
                              value={ptOrderForm.provider_name}
                              onChange={(e) => {
                                const p = currentProviders.find(t => t.provider_name === e.target.value);
                                setPtOrderForm(prev => ({ ...prev, provider_name: e.target.value, provider_type: p?.provider_type || '' }));
                              }}>
                              <option value="">Select provider...</option>
                              {currentProviders.map((t, i) => <option key={i} value={t.provider_name}>{t.provider_name}</option>)}
                            </select>
                          </div>
                          <div>
                            <label style={{ fontSize:'0.68rem', color:'var(--text-muted)', fontWeight:600, display:'block', marginBottom:'3px' }}>Sessions</label>
                            <input type="number" className="form-input" style={{ fontSize:'0.82rem', padding:'5px 8px' }}
                              min={1} max={100} value={ptOrderForm.total_sessions}
                              onChange={(e) => setPtOrderForm(prev => ({ ...prev, total_sessions: parseInt(e.target.value) || 1 }))} />
                          </div>
                          <div style={{ display:'flex', gap:'6px' }}>
                            <button onClick={addPTOrder} style={{ flex:1, padding:'6px', fontSize:'0.78rem', fontWeight:600, borderRadius:'var(--radius-sm)', background:'#3b82f6', color:'#fff', border:'none', cursor:'pointer' }}>Create</button>
                            <button onClick={() => setShowPtOrder(false)} style={{ padding:'6px 10px', fontSize:'0.78rem', borderRadius:'var(--radius-sm)', background:'transparent', color:'var(--text-muted)', border:'1px solid var(--zeus-border)', cursor:'pointer' }}>×</button>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>

            {/* ── RIGHT SIDE: Treatment Type Selector ── */}
            <div className="card treatment-type-panel" style={{ width:'240px', flexShrink:0, padding:0, overflow:'hidden' }}>
              <div style={{ padding:'10px 14px', borderBottom:'1px solid var(--zeus-border)', background:'var(--zeus-navy)' }}>
                <span style={{ fontSize:'0.82rem', fontWeight:700, color:'var(--text-primary)' }}>Treatments</span>
              </div>
              <div style={{ display:'flex', flexDirection:'column' }}>
                {treatmentTypes.map((type, i) => {
                  const isPT = type.toLowerCase().includes('therapy');
                  return (
                    <button key={i}
                      onClick={() => addTreatmentEntry(type)}
                      style={{
                        padding:'12px 14px', textAlign:'left', fontSize:'0.84rem', fontWeight:500,
                        color:'var(--text-primary)', background:'transparent',
                        border:'none', borderBottom: i < treatmentTypes.length - 1 ? '1px solid var(--zeus-border)' : 'none',
                        cursor:'pointer', transition:'background 0.15s',
                        display:'flex', alignItems:'center', justifyContent:'space-between',
                      }}
                      onMouseOver={(e) => e.currentTarget.style.background = 'var(--zeus-gold-dim)'}
                      onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                      <span>{type}</span>
                      {isPT ? (
                        <span style={{ fontSize:'0.65rem', padding:'2px 6px', background:'rgba(59,130,246,0.1)', color:'#3b82f6', borderRadius:'8px', fontWeight:700 }}>+ Order</span>
                      ) : (
                        <Plus size={14} style={{ color:'var(--text-muted)', opacity:0.5 }} />
                      )}
                    </button>
                  );
                })}
              </div>
              <div style={{ padding:'8px 14px', fontSize:'0.65rem', color:'var(--text-muted)', borderTop:'1px solid var(--zeus-border)' }}>
                Manage in Settings → Treatment Types
              </div>
            </div>
          </div>
        )}


        {/* ═══════════════════ DECEASED CASE TAB ═══════════════════ */}
        {activeTab === 'deceased' && (
          <div className="card" style={{ padding:'20px' }}>
            <SectionTitle>Deceased Case Information</SectionTitle>
            <div className="deceased-info-grid" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }}>
              {renderField({ label:"Date of Death", field:"date_of_death", obj:"form", type:"date" })}
              {renderField({ label:"Cause of Death", field:"cause_of_death", obj:"form" })}
              {renderField({ label:"Beneficiary Name", field:"beneficiary_name", obj:"form" })}
              {renderField({ label:"Relationship", field:"beneficiary_relationship", obj:"form" })}
              {renderField({ label:"Phone", field:"beneficiary_phone", obj:"form" })}
              {renderField({ label:"Email", field:"beneficiary_email", obj:"form" })}
              {renderField({ label:"Address", field:"beneficiary_address", obj:"form" })}
            </div>
            <div style={{ marginTop:'12px' }}>
              <label style={{ fontSize:'0.72rem', color:'var(--text-muted)' }}>Notes</label>
              <textarea
                className="form-textarea"
                style={{ minHeight:'100px', fontSize:'0.82rem', padding:'6px 8px' }}
                value={form.deceased_notes || ''}
                onChange={(e) => setForm(prev => ({...prev, deceased_notes: e.target.value}))}
              />
            </div>
          </div>
        )}

        {/* ═══════════════════ NOTES TAB ═══════════════════ */}
        {activeTab === 'notes' && (() => {
          const NOTE_COLORS = [
            { name:'None', value:'' },
            { name:'Red', value:'#ef4444' },
            { name:'Orange', value:'#f97316' },
            { name:'Amber', value:'#f59e0b' },
            { name:'Yellow', value:'#eab308' },
            { name:'Green', value:'#22c55e' },
            { name:'Teal', value:'#14b8a6' },
            { name:'Blue', value:'#3b82f6' },
            { name:'Indigo', value:'#6366f1' },
            { name:'Purple', value:'#a855f7' },
            { name:'Pink', value:'#ec4899' },
          ];
          const noteTypeOptions = customLists.note_type_options?.length
            ? customLists.note_type_options
            : ['General','Phone Call','Email','Meeting','Court','Medical','Billing','Follow-Up','Research','Internal'];

          return (
          <>
          {/* ── Note Modal ── */}
          {showNoteModal && (
            <div style={{ position:'fixed', inset:0, zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center' }}>
              <div style={{ position:'absolute', inset:0, background:'rgba(0,0,0,0.6)' }} onClick={cancelEditNote} />
              <div style={{
                position:'relative', width:'560px', maxHeight:'85vh', display:'flex', flexDirection:'column',
                background:'var(--zeus-navy)', border:'1px solid var(--zeus-border)',
                borderRadius:'var(--radius-lg)', boxShadow:'0 16px 48px rgba(0,0,0,0.5)',
                overflow:'hidden',
              }}>
                {/* Modal header */}
                <div style={{
                  display:'flex', alignItems:'center', justifyContent:'space-between',
                  padding:'14px 20px', borderBottom:'1px solid var(--zeus-border)',
                  background:'var(--zeus-navy-light)',
                }}>
                  <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                    {editingNoteId ? <Pencil size={15} style={{ color:'var(--zeus-gold)' }} /> : <Plus size={15} style={{ color:'var(--zeus-gold)' }} />}
                    <span style={{ fontSize:'0.92rem', fontWeight:700, color:'var(--text-primary)' }}>
                      {editingNoteId ? 'Edit Note' : 'Add Note'}
                    </span>
                  </div>
                  <button onClick={cancelEditNote} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)', padding:'4px' }}>
                    <X size={18} />
                  </button>
                </div>

                {/* Modal body */}
                <div style={{ padding:'18px 20px', overflowY:'auto', flex:1 }}>
                  {/* Type + Color row */}
                  <div style={{ display:'flex', gap:'10px', alignItems:'center', marginBottom:'14px' }}>
                    <div style={{ flex:1 }}>
                      <label style={{ fontSize:'0.72rem', color:'var(--text-muted)', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.04em', display:'block', marginBottom:'4px' }}>Type</label>
                      <select
                        className="form-select"
                        style={{ width:'100%', padding:'6px 10px', fontSize:'0.85rem' }}
                        value={noteType}
                        onChange={(e) => setNoteType(e.target.value)}
                      >
                        {noteTypeOptions.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={{ fontSize:'0.72rem', color:'var(--text-muted)', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.04em', display:'block', marginBottom:'4px' }}>Color</label>
                      <div ref={colorPickerRef} style={{ position:'relative' }}>
                        <button
                          onClick={() => setShowColorPicker(!showColorPicker)}
                          style={{
                            display:'flex', alignItems:'center', gap:'6px',
                            padding:'6px 12px', fontSize:'0.82rem',
                            background:'transparent', border:'1px solid var(--zeus-border)',
                            borderRadius:'var(--radius-sm)', cursor:'pointer',
                            color:'var(--text-secondary)',
                          }}
                        >
                          <span style={{
                            width:'16px', height:'16px', borderRadius:'3px',
                            background: noteColor || 'var(--zeus-border)',
                            border: noteColor ? 'none' : '1px dashed var(--text-muted)',
                            display:'inline-block', flexShrink:0,
                          }} />
                          <Palette size={14} />
                          <span style={{ fontSize:'0.78rem' }}>{noteColor ? NOTE_COLORS.find(c => c.value === noteColor)?.name || 'Custom' : 'None'}</span>
                        </button>
                        {showColorPicker && (
                          <div style={{
                            position:'absolute', top:'100%', right:0, marginTop:'4px',
                            background:'var(--zeus-navy)', border:'1px solid var(--zeus-border)',
                            borderRadius:'var(--radius-md)', padding:'12px', zIndex:70,
                            boxShadow:'0 4px 16px rgba(0,0,0,0.4)', width:'220px',
                          }}>
                            <div style={{ fontSize:'0.72rem', color:'var(--text-muted)', marginBottom:'8px', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.04em' }}>Note Color</div>
                            <div style={{ display:'flex', flexWrap:'wrap', gap:'6px', marginBottom:'10px' }}>
                              {NOTE_COLORS.map(c => (
                                <button
                                  key={c.value}
                                  onClick={() => { setNoteColor(c.value); setShowColorPicker(false); }}
                                  title={c.name}
                                  style={{
                                    width:'28px', height:'28px', borderRadius:'4px', cursor:'pointer',
                                    background: c.value || 'var(--zeus-navy-light)',
                                    border: (noteColor === c.value) ? '2px solid #fff' : c.value ? '1px solid rgba(255,255,255,0.1)' : '1px dashed var(--text-muted)',
                                    outline: (noteColor === c.value) ? '2px solid var(--zeus-gold)' : 'none',
                                  }}
                                />
                              ))}
                            </div>
                            <div style={{ display:'flex', alignItems:'center', gap:'6px' }}>
                              <span style={{ fontSize:'0.75rem', color:'var(--text-muted)' }}>Custom:</span>
                              <input
                                type="color"
                                value={noteColor || '#3b82f6'}
                                onChange={(e) => setNoteColor(e.target.value)}
                                style={{ width:'28px', height:'24px', padding:0, border:'none', cursor:'pointer', background:'transparent' }}
                              />
                              {noteColor && (
                                <button
                                  onClick={() => { setNoteColor(''); setShowColorPicker(false); }}
                                  style={{ fontSize:'0.72rem', color:'var(--text-muted)', background:'none', border:'none', cursor:'pointer', textDecoration:'underline' }}
                                >Clear</button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Formatting toolbar */}
                  <div style={{ display:'flex', gap:'2px', marginBottom:'6px' }}>
                    <button
                      onMouseDown={(e) => { e.preventDefault(); insertFormatting('b'); }}
                      title="Bold (Ctrl+B)"
                      style={{
                        padding:'5px 8px', background:'transparent', border:'1px solid var(--zeus-border)',
                        borderRadius:'var(--radius-sm) 0 0 var(--radius-sm)', cursor:'pointer', color:'var(--text-secondary)',
                        display:'flex', alignItems:'center',
                      }}
                    ><Bold size={14} /></button>
                    <button
                      onMouseDown={(e) => { e.preventDefault(); insertFormatting('i'); }}
                      title="Italic (Ctrl+I)"
                      style={{
                        padding:'5px 8px', background:'transparent', border:'1px solid var(--zeus-border)',
                        borderLeft:'none',
                        borderRadius:'0 var(--radius-sm) var(--radius-sm) 0', cursor:'pointer', color:'var(--text-secondary)',
                        display:'flex', alignItems:'center',
                      }}
                    ><Italic size={14} /></button>
                  </div>

                  {/* Rich text editor */}
                  <div
                    ref={noteRef}
                    contentEditable
                    suppressContentEditableWarning
                    className="form-textarea"
                    style={{
                      minHeight:'180px', width:'100%', fontSize:'0.9rem', padding:'10px 12px',
                      color: noteColor || 'var(--text-primary)', lineHeight:1.6,
                      overflowY:'auto', whiteSpace:'pre-wrap', outline:'none',
                      cursor:'text',
                    }}
                    onInput={(e) => setNoteText(e.currentTarget.innerHTML)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); handleAddNote(); } }}
                    data-placeholder="Enter note content..."
                  />
                  <style>{`[contenteditable][data-placeholder]:empty::before { content: attr(data-placeholder); color: var(--text-muted); opacity: 0.6; pointer-events: none; }`}</style>
                  <div style={{ fontSize:'0.7rem', color:'var(--text-muted)', marginTop:'4px' }}>
                    Ctrl+Enter to save
                  </div>
                </div>

                {/* Modal footer */}
                <div style={{
                  display:'flex', justifyContent:'flex-end', gap:'10px',
                  padding:'12px 20px', borderTop:'1px solid var(--zeus-border)',
                  background:'var(--zeus-navy-light)',
                }}>
                  <button className="btn btn-ghost" onClick={cancelEditNote} style={{ padding:'6px 16px', fontSize:'0.82rem' }}>
                    Cancel
                  </button>
                  <button className="btn btn-primary" onClick={handleAddNote} style={{ padding:'6px 20px', fontSize:'0.82rem', display:'flex', alignItems:'center', gap:'6px' }}>
                    <Save size={14} />
                    {editingNoteId ? 'Save Changes' : 'Add Note'}
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            {/* Toolbar bar */}
            <div style={{ display:'flex', gap:'10px', alignItems:'center', padding:'8px 16px', borderBottom:'1px solid var(--zeus-border)', background:'var(--zeus-navy)' }}>
              <button className="btn btn-primary btn-sm" onClick={openNewNote} style={{ display:'flex', alignItems:'center', gap:'5px', padding:'5px 14px', fontSize:'0.8rem' }}>
                <Plus size={14} /> Add Note
              </button>
              <div style={{ display:'flex', alignItems:'center', gap:'6px', padding:'4px 8px', border:'1px solid var(--zeus-border)', borderRadius:'var(--radius-sm)', background:'var(--zeus-navy-light)', flex:1, maxWidth:'220px' }}>
                <Search size={13} style={{ color:'var(--text-muted)', flexShrink:0 }} />
                <input style={{ border:'none', outline:'none', background:'transparent', fontSize:'0.78rem', color:'var(--text-primary)', width:'100%', padding:0 }}
                  placeholder="Search notes..."
                  value={noteSearch} onChange={(e) => setNoteSearch(e.target.value)} />
                {noteSearch && <button onClick={() => setNoteSearch('')} style={{ background:'none', border:'none', cursor:'pointer', padding:0, color:'var(--text-muted)' }}><X size={12} /></button>}
              </div>
              <span style={{ fontSize:'0.78rem', color:'var(--text-muted)' }}>Dates:</span>
              <select className="form-select" style={{ width:'auto', padding:'4px 8px', fontSize:'0.78rem' }}
                value={noteDateFilter} onChange={(e) => setNoteDateFilter(e.target.value)}>
                <option value="all">All Dates</option>
                <option value="7">Last 7 Days</option>
                <option value="30">Last 30 Days</option>
                <option value="90">Last 90 Days</option>
              </select>
              <span style={{ fontSize:'0.78rem', color:'var(--text-muted)', marginLeft:'auto' }}>
                Notes: {mergedNotes.length}
              </span>
              <button
                onClick={() => setShowNotePreview(p => !p)}
                title={showNotePreview ? 'Hide preview panel' : 'Show preview panel'}
                style={{
                  display:'flex', alignItems:'center', gap:'4px',
                  padding:'3px 8px', fontSize:'0.72rem', fontWeight:600,
                  background: showNotePreview ? 'rgba(212,168,83,0.15)' : 'transparent',
                  border: showNotePreview ? '1px solid rgba(212,168,83,0.3)' : '1px solid var(--zeus-border)',
                  borderRadius:'var(--radius-sm)', cursor:'pointer',
                  color: showNotePreview ? 'var(--zeus-gold)' : 'var(--text-muted)',
                }}
              >
                {showNotePreview ? <EyeOff size={12} /> : <Eye size={12} />}
                Preview
              </button>
            </div>

            {/* Split: table left + preview right */}
            <div style={{ display:'flex', minHeight:'400px' }}>
              {/* Left: Notes table */}
              <div style={{ flex:1, overflowY:'auto', maxHeight:'500px', borderRight: showNotePreview ? '1px solid var(--zeus-border)' : 'none' }}>
                <table style={{ width:'100%', fontSize:'0.82rem' }}>
                  <thead>
                    <tr style={{ position:'sticky', top:0, zIndex:1 }}>
                      <th style={{ width:'90px', padding:'6px 10px', fontSize:'0.72rem' }}>Date</th>
                      <th style={{ width:'65px', padding:'6px 10px', fontSize:'0.72rem' }}>Time</th>
                      <th style={{ width:'90px', padding:'6px 10px', fontSize:'0.72rem' }}>Type</th>
                      <th style={{ padding:'6px 10px', fontSize:'0.72rem' }}>Regarding</th>
                      <th style={{ width:'130px', padding:'6px 10px', fontSize:'0.72rem' }}>Record Manager</th>
                      <th style={{ width:'130px', padding:'6px 10px', fontSize:'0.72rem' }}>Edited By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mergedNotes.length === 0 ? (
                      <tr><td colSpan={6} style={{ textAlign:'center', padding:'24px', color:'var(--text-muted)' }}>No notes yet</td></tr>
                    ) : (
                      mergedNotes
                        .filter(n => {
                          if (noteDateFilter !== 'all') {
                            const days = parseInt(noteDateFilter);
                            const noteDate = new Date(n.createdAt || n.updatedAt);
                            if ((Date.now() - noteDate.getTime()) > days * 86400000) return false;
                          }
                          if (noteSearch.trim()) {
                            const q = noteSearch.toLowerCase();
                            const content = (n.content || '').toLowerCase();
                            const type = (n.note_type || '').toLowerCase();
                            const by = n.created_by ? `${n.created_by.first_name || ''} ${n.created_by.last_name || ''}`.toLowerCase() : '';
                            if (!content.includes(q) && !type.includes(q) && !by.includes(q)) return false;
                          }
                          return true;
                        })
                        .map(note => {
                          const createdBy = note.created_by || note.user;
                          const updatedBy = note.updated_by || null;
                          const dt = new Date(note.createdAt || note.updatedAt);
                          const isSelected = selectedNote?._id === note._id;
                          const rowColor = note.color || '';
                          return (
                            <tr key={note._id}
                              onClick={() => setSelectedNote(note)}
                              onDoubleClick={() => startEditNote(note)}
                              style={{
                                cursor:'pointer',
                                background: isSelected ? 'var(--zeus-gold-dim)' : 'transparent',
                                borderLeft: isSelected ? '4px solid var(--zeus-gold)' : '4px solid transparent',
                              }}
                              onMouseOver={(e) => { if (!isSelected) e.currentTarget.style.background = 'rgba(255,255,255,0.02)'; }}
                              onMouseOut={(e) => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                            >
                              <td style={{ padding:'6px 10px', whiteSpace:'nowrap', fontSize:'0.78rem', color:'var(--text-secondary)' }}>
                                {dt.toLocaleDateString()}
                              </td>
                              <td style={{ padding:'6px 10px', whiteSpace:'nowrap', fontSize:'0.78rem', color:'var(--text-muted)' }}>
                                {dt.toLocaleTimeString([], { hour:'numeric', minute:'2-digit' })}
                              </td>
                              <td style={{ padding:'6px 10px', fontSize:'0.75rem' }}>
                                <span style={{
                                  padding:'1px 6px', borderRadius:'3px', fontSize:'0.72rem', fontWeight:600,
                                  background: rowColor ? `${rowColor}22` : 'rgba(255,255,255,0.05)',
                                  color: rowColor || 'var(--text-secondary)',
                                  border: rowColor ? `1px solid ${rowColor}44` : '1px solid var(--zeus-border)',
                                }}>
                                  {note.note_type || 'General'}
                                </span>
                              </td>
                              <td style={{
                                padding:'6px 10px',
                                color: rowColor || 'var(--text-primary)',
                                maxWidth:'300px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
                              }}>
                                <div
                                  style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:'100%' }}
                                  dangerouslySetInnerHTML={{ __html: (note.content || '').replace(/\n/g, ' ') }}
                                />
                              </td>
                              <td style={{ padding:'6px 10px', fontSize:'0.78rem', color:'var(--text-secondary)' }}>
                                {displayUser(createdBy)}
                              </td>
                              <td style={{ padding:'6px 10px', fontSize:'0.78rem', color:'var(--text-muted)' }}>
                                {updatedBy ? displayUser(updatedBy) : '—'}
                              </td>
                            </tr>
                          );
                        })
                    )}
                  </tbody>
                </table>
              </div>

              {/* Right: Note preview */}
              {showNotePreview && (
              <div style={{ width:'320px', overflowY:'auto', maxHeight:'500px', padding:'14px 16px', background:'var(--zeus-navy-light)', flexShrink:0 }}>
                {selectedNote ? (
                  <>
                    <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'6px' }}>
                      {selectedNote.color && (
                        <span style={{ width:'10px', height:'10px', borderRadius:'50%', background:selectedNote.color, flexShrink:0 }} />
                      )}
                      <span style={{
                        padding:'2px 8px', borderRadius:'3px', fontSize:'0.75rem', fontWeight:600,
                        background: selectedNote.color ? `${selectedNote.color}22` : 'rgba(255,255,255,0.05)',
                        color: selectedNote.color || 'var(--zeus-gold)',
                        border: selectedNote.color ? `1px solid ${selectedNote.color}44` : '1px solid var(--zeus-border)',
                      }}>
                        {selectedNote.note_type || 'General'}
                      </span>
                      <button
                        onClick={() => startEditNote(selectedNote)}
                        title="Edit note"
                        style={{
                          marginLeft:'auto', padding:'3px 8px', fontSize:'0.72rem',
                          background:'transparent', border:'1px solid var(--zeus-border)',
                          borderRadius:'var(--radius-sm)', cursor:'pointer',
                          color:'var(--text-muted)', display:'flex', alignItems:'center', gap:'4px',
                        }}
                      >
                        <Pencil size={11} /> Edit
                      </button>
                    </div>
                    <div style={{ fontSize:'0.78rem', color:'var(--text-muted)', marginBottom:'8px' }}>
                      {selectedNote.createdAt ? new Date(selectedNote.createdAt).toLocaleDateString('en-US', { month:'long', day:'numeric', year:'numeric' }) : ''}
                      {' · '}
                      {selectedNote.createdAt ? new Date(selectedNote.createdAt).toLocaleTimeString([], { hour:'numeric', minute:'2-digit' }) : ''}
                    </div>
                    <div
                      style={{
                        color: selectedNote.color || 'var(--text-primary)',
                        fontSize:'0.88rem', lineHeight:1.7, whiteSpace:'pre-wrap', marginTop:'8px',
                      }}
                      dangerouslySetInnerHTML={{ __html: (selectedNote.content || '—').replace(/\n/g, '<br/>') }}
                    />
                    <div style={{ marginTop:'14px', fontSize:'0.75rem', color:'var(--text-muted)', borderTop:'1px solid var(--zeus-border)', paddingTop:'8px' }}>
                      <div>By: {displayUser(selectedNote.created_by || selectedNote.user)}</div>
                      {selectedNote.updated_by && <div>Edited by: {displayUser(selectedNote.updated_by)}</div>}
                    </div>
                  </>
                ) : (
                  <div style={{ color:'var(--text-muted)', fontSize:'0.85rem', textAlign:'center', paddingTop:'40px' }}>
                    Select a note to preview
                  </div>
                )}
              </div>
              )}
            </div>
          </div>
          </>
          );
        })()}

        {/* ═══════════════════ HISTORY TAB ═══════════════════ */}
        {activeTab === 'history' && (
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            {/* Filters bar */}
            <div style={{ display:'flex', gap:'10px', alignItems:'center', padding:'8px 16px', borderBottom:'1px solid var(--zeus-border)', background:'var(--zeus-navy-light)' }}>
              <span style={{ fontSize:'0.78rem', color:'var(--text-muted)' }}>Dates:</span>
              <select className="form-select" style={{ width:'auto', padding:'4px 8px', fontSize:'0.78rem' }}
                value={historyDateFilter} onChange={(e) => setHistoryDateFilter(e.target.value)}>
                <option value="all">All Dates</option>
                <option value="7">Last 7 Days</option>
                <option value="30">Last 30 Days</option>
                <option value="90">Last 90 Days</option>
              </select>
              <span style={{ fontSize:'0.78rem', color:'var(--text-muted)', marginLeft:'8px' }}>Types:</span>
              <select className="form-select" style={{ width:'auto', padding:'4px 8px', fontSize:'0.78rem' }}
                value={historyTypeFilter} onChange={(e) => setHistoryTypeFilter(e.target.value)}>
                <option value="all">All</option>
                <option value="Field Changed">Field Changed</option>
                <option value="created">Created</option>
                <option value="updated">Updated</option>
                <option value="Letter Sent">Letter Sent</option>
                <option value="Document">Document</option>
                <option value="Note">Note</option>
              </select>
              <span style={{ fontSize:'0.78rem', color:'var(--text-muted)', marginLeft:'auto' }}>
                Histories: {(c.history || []).length}
              </span>
            </div>

            {/* History table */}
            <div style={{ overflowY:'auto', maxHeight:'520px' }}>
              <table style={{ width:'100%', fontSize:'0.82rem' }}>
                <thead>
                  <tr style={{ position:'sticky', top:0, zIndex:1 }}>
                    <th style={{ width:'90px', padding:'6px 10px', fontSize:'0.72rem' }}>Date</th>
                    <th style={{ width:'65px', padding:'6px 10px', fontSize:'0.72rem' }}>Time</th>
                    <th style={{ width:'110px', padding:'6px 10px', fontSize:'0.72rem' }}>Result</th>
                    <th style={{ padding:'6px 10px', fontSize:'0.72rem' }}>Regarding & Details</th>
                    <th style={{ width:'140px', padding:'6px 10px', fontSize:'0.72rem' }}>Record Manager</th>
                  </tr>
                </thead>
                <tbody>
                  {(c.history || []).length === 0 ? (
                    <tr><td colSpan={5} style={{ textAlign:'center', padding:'24px', color:'var(--text-muted)' }}>No history yet</td></tr>
                  ) : (
                    [...(c.history || [])]
                      .sort((a, b) => new Date(b.timestamp || b.createdAt || 0) - new Date(a.timestamp || a.createdAt || 0))
                      .filter(h => {
                        if (historyDateFilter !== 'all') {
                          const days = parseInt(historyDateFilter);
                          const hDate = new Date(h.timestamp || h.createdAt || 0);
                          if ((Date.now() - hDate.getTime()) > days * 86400000) return false;
                        }
                        if (historyTypeFilter !== 'all') {
                          const action = (h.action || '').toLowerCase();
                          const filter = historyTypeFilter.toLowerCase();
                          if (!action.includes(filter)) return false;
                        }
                        return true;
                      })
                      .map((h, i) => {
                        const dt = new Date(h.timestamp || h.createdAt || Date.now());
                        const resultLabel = h.action === 'updated' ? 'Field Changed'
                          : h.action === 'created' ? 'Created'
                          : h.action || 'Field Changed';
                        const resultColor = resultLabel === 'Field Changed' ? 'var(--status-blue)'
                          : resultLabel === 'Letter Sent' ? 'var(--status-green)'
                          : resultLabel === 'Created' ? 'var(--zeus-gold)'
                          : 'var(--text-secondary)';

                        return (
                          <tr key={i} style={{ verticalAlign:'top' }}>
                            <td style={{ padding:'6px 10px', whiteSpace:'nowrap', fontSize:'0.78rem', color:'var(--text-secondary)' }}>
                              {dt.toLocaleDateString()}
                            </td>
                            <td style={{ padding:'6px 10px', whiteSpace:'nowrap', fontSize:'0.78rem', color:'var(--text-muted)' }}>
                              {dt.toLocaleTimeString([], { hour:'numeric', minute:'2-digit' })}
                            </td>
                            <td style={{ padding:'6px 10px' }}>
                              <span style={{
                                fontSize:'0.75rem', fontWeight:600, color: resultColor,
                              }}>
                                {resultLabel}
                              </span>
                            </td>
                            <td style={{ padding:'6px 10px', color:'var(--text-primary)', fontSize:'0.82rem', whiteSpace:'pre-wrap', lineHeight:1.5 }}>
                              {h.description || '—'}
                            </td>
                            <td style={{ padding:'6px 10px', fontSize:'0.78rem', color:'var(--text-secondary)' }}>
                              {h.user ? displayUser(h.user) : '—'}
                            </td>
                          </tr>
                        );
                      })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ═══════════════════ DOCUMENTS TAB ═══════════════════ */}
        {activeTab === 'documents' && (<>
          <div className="card">
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px' }}>
              <div style={{ display:'flex', gap:'8px', alignItems:'center' }}>
                {trashedDocs.length > 0 && (
                  <button
                    className={`btn btn-sm ${showDocTrash ? 'btn-primary' : 'btn-ghost'}`}
                    onClick={() => setShowDocTrash(!showDocTrash)}
                    style={{ display:'flex', alignItems:'center', gap:'5px' }}
                  >
                    <Archive size={14} />
                    Trash ({trashedDocs.length})
                  </button>
                )}
              </div>
              <button className="btn btn-primary btn-sm" onClick={openUpload}><UploadCloud size={14} /> Upload Document</button>
            </div>

            {/* Active Documents */}
            {!showDocTrash && (
              <>
                {activeDocs.length > 0 ? (
                  <table className="table">
                    <thead><tr><th>Name</th><th>Category</th><th>Size</th><th>Date</th><th style={{width:'100px'}}></th></tr></thead>
                    <tbody>
                      {activeDocs.map((doc, i) => (
                        <tr key={doc._id || i}
                          style={{ cursor:'pointer', transition:'background 0.15s' }}
                          onMouseOver={(e) => e.currentTarget.style.background='rgba(255,255,255,0.03)'}
                          onMouseOut={(e) => e.currentTarget.style.background=''}
                        >
                          <td style={{ fontWeight:600, color:'var(--zeus-gold)' }}>
                            {renamingDocId === (doc._id || doc.id) ? (
                              <div style={{ display:'flex', alignItems:'center', gap:'6px' }} onClick={e => e.stopPropagation()}>
                                <FileText size={14} style={{ flexShrink:0 }} />
                                <input
                                  autoFocus
                                  className="form-input"
                                  style={{ padding:'3px 7px', fontSize:'0.85rem', height:'28px', minWidth:0, flex:1 }}
                                  value={renameValue}
                                  onChange={e => setRenameValue(e.target.value)}
                                  onKeyDown={async e => {
                                    if (e.key === 'Enter') {
                                      if (!renameValue.trim()) return;
                                      try {
                                        const updated = await renameDocument(id, doc._id || doc.id, renameValue.trim());
                                        setC(prev => ({ ...prev, documents: (prev.documents||[]).map(d => (d._id||d.id)===(doc._id||doc.id) ? {...d, name: updated.name} : d) }));
                                        toast.success('Document renamed');
                                      } catch { toast.error('Failed to rename'); }
                                      setRenamingDocId(null);
                                    } else if (e.key === 'Escape') {
                                      setRenamingDocId(null);
                                    }
                                  }}
                                />
                                <button className="btn btn-primary btn-sm" style={{ padding:'3px 10px', flexShrink:0 }}
                                  onClick={async e => {
                                    e.stopPropagation();
                                    if (!renameValue.trim()) return;
                                    try {
                                      const updated = await renameDocument(id, doc._id || doc.id, renameValue.trim());
                                      setC(prev => ({ ...prev, documents: (prev.documents||[]).map(d => (d._id||d.id)===(doc._id||doc.id) ? {...d, name: updated.name} : d) }));
                                      toast.success('Document renamed');
                                    } catch { toast.error('Failed to rename'); }
                                    setRenamingDocId(null);
                                  }}>
                                  Save
                                </button>
                                <button className="btn btn-ghost btn-sm" style={{ padding:'3px 8px', flexShrink:0 }}
                                  onClick={e => { e.stopPropagation(); setRenamingDocId(null); }}>
                                  <X size={13} />
                                </button>
                              </div>
                            ) : (
                              <div style={{ display:'flex', alignItems:'center', gap:'6px' }} onClick={() => handleDocClick(doc)}>
                                <FileText size={14} />
                                {doc.name || doc.filename || 'Untitled'}
                              </div>
                            )}
                          </td>
                          <td onClick={() => handleDocClick(doc)}><span className="badge">{(doc.category || 'other').replace(/_/g,' ')}</span></td>
                          <td onClick={() => handleDocClick(doc)} style={{ fontSize:'0.82rem', color:'var(--text-muted)' }}>{doc.size ? `${(doc.size / 1024).toFixed(1)} KB` : '—'}</td>
                          <td onClick={() => handleDocClick(doc)} style={{ fontSize:'0.82rem', color:'var(--text-muted)' }}>{doc.uploaded_at ? formatDate(doc.uploaded_at) : '—'}</td>
                          <td>
                            <div style={{ display:'flex', gap:'4px', alignItems:'center' }}>
                              {doc.url && (
                                <button className="btn btn-ghost btn-sm" style={{ padding:'4px' }}
                                  onClick={(e) => { e.stopPropagation(); handleDocClick(doc); }}
                                  title="Preview">
                                  <Eye size={14} />
                                </button>
                              )}
                              <button className="btn btn-ghost btn-sm" style={{ padding:'4px' }}
                                onClick={(e) => { e.stopPropagation(); setRenameValue(doc.name || doc.filename || ''); setRenamingDocId(doc._id || doc.id); }}
                                title="Rename">
                                <Pencil size={13} />
                              </button>
                              <button className="btn btn-ghost btn-sm" style={{ padding:'4px', color:'var(--status-red)' }}
                                onClick={(e) => { e.stopPropagation(); handleSoftDelete(doc._id); }}
                                title="Move to trash">
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="empty-state"><FileText /><h3>No documents</h3></div>
                )}
              </>
            )}

            {/* Trash View */}
            {showDocTrash && (
              <>
                <div style={{ padding:'10px 14px', background:'rgba(239,68,68,0.06)', border:'1px solid rgba(239,68,68,0.2)', borderRadius:'var(--radius-md)', marginBottom:'14px', display:'flex', alignItems:'center', justifyContent:'space-between', gap:'8px' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                    <Archive size={16} style={{ color:'var(--status-red)' }} />
                    <span style={{ fontSize:'0.85rem', color:'var(--text-secondary)' }}>
                      Trash — documents auto-delete after 30 days
                    </span>
                  </div>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => setShowDocTrash(false)}
                    style={{ display:'flex', alignItems:'center', gap:'5px', flexShrink:0 }}
                  >
                    <ArrowLeft size={14} /> All Documents
                  </button>
                </div>
                {trashedDocs.length > 0 ? (
                  <table className="table">
                    <thead><tr><th>Name</th><th>Category</th><th>Deleted</th><th>Auto-purge</th><th style={{width:'120px'}}></th></tr></thead>
                    <tbody>
                      {trashedDocs.map((doc, i) => {
                        const deletedAt = new Date(doc.deleted_at);
                        const purgeDate = new Date(deletedAt.getTime() + 30 * 24 * 60 * 60 * 1000);
                        const daysLeft = Math.max(0, Math.ceil((purgeDate - Date.now()) / (24 * 60 * 60 * 1000)));
                        return (
                          <tr key={doc._id || i} style={{ opacity: 0.7 }}>
                            <td style={{ color:'var(--text-secondary)' }}>
                              <div style={{ display:'flex', alignItems:'center', gap:'6px' }}>
                                <FileText size={14} style={{ color:'var(--text-muted)' }} />
                                {doc.name || doc.filename || 'Untitled'}
                              </div>
                            </td>
                            <td><span className="badge">{(doc.category || 'other').replace(/_/g,' ')}</span></td>
                            <td style={{ fontSize:'0.82rem', color:'var(--text-muted)' }}>{formatDate(doc.deleted_at)}</td>
                            <td style={{ fontSize:'0.78rem', color: daysLeft <= 7 ? 'var(--status-red)' : 'var(--text-muted)' }}>
                              {daysLeft} day{daysLeft !== 1 ? 's' : ''} left
                            </td>
                            <td>
                              <div style={{ display:'flex', gap:'4px' }}>
                                <button className="btn btn-ghost btn-sm" style={{ padding:'4px', color:'var(--zeus-gold)' }}
                                  onClick={() => handleRestoreDoc(doc._id)} title="Restore">
                                  <RotateCcw size={14} />
                                </button>
                                <button className="btn btn-ghost btn-sm" style={{ padding:'4px', color:'var(--status-red)' }}
                                  onClick={() => handlePermanentDelete(doc._id)} title="Delete permanently">
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : (
                  <div className="empty-state"><Archive /><h3>Trash is empty</h3></div>
                )}
              </>
            )}

            {/* Document Preview Modal */}
            <Modal isOpen={!!previewDoc} onClose={() => { setPreviewDoc(null); setPreviewHtml(''); }} title={previewDoc?.name || 'Document'} preview>
              <div style={{ display:'flex', gap:'8px', marginBottom:'12px' }}>
                {/* "Open File" removed — "Open in New Tab" does the same thing */}
                {previewHtml ? (
                  <button className="btn btn-primary btn-sm" onClick={() => {
                    const win = window.open('', '_blank');
                    win.document.write('<html><head><title>' + (previewDoc && previewDoc.name ? previewDoc.name : 'Document') + '</title><style>body{font-family:Times New Roman,serif;padding:40px;width:8.5in;max-width:8.5in;margin:0 auto;line-height:1.6;}@media print{@page{size:letter;margin:0.5in;}body{padding:0;width:100%;}}</style></head><body>' + previewHtml + '</body></html>');
                    win.document.close();
                    setTimeout(function(){ win.print(); }, 500);
                  }}>
                    <Printer size={14} /> Print / Save PDF
                  </button>
                ) : null}
                {previewDoc && previewDoc.url && !previewDoc.url.includes('/templates/') && (
                  <a href={previewDoc.url} target="_blank" rel="noreferrer" className="btn btn-primary btn-sm" style={{ display:'flex', alignItems:'center', gap:'5px' }}>
                    <Eye size={14} /> Open in New Tab
                  </a>
                )}
              </div>

              {/* Content area — taller to match larger modal */}
              {previewHtml ? (
                <div style={{
                  background:'#fff', color:'#000', padding:'40px 50px',
                  borderRadius:'var(--radius-md)', maxHeight:'70vh', overflowY:'auto',
                  fontFamily:"'Times New Roman', serif", fontSize:'12pt', lineHeight:1.6,
                  width:'8.5in', maxWidth:'100%', margin:'0 auto',
                }}
                  dangerouslySetInnerHTML={{ __html: previewHtml }}
                />
              ) : previewDoc && previewDoc.url ? (
                <div style={{ borderRadius:'var(--radius-md)', overflow:'hidden' }}>
                  {(previewDoc.mimetype && previewDoc.mimetype.startsWith('image/')) || /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(previewDoc.url) ? (
                    <img src={previewDoc.url} alt={previewDoc.name} style={{ maxWidth:'100%', maxHeight:'72vh', display:'block', margin:'0 auto' }} />
                  ) : /\.pdf$/i.test(previewDoc.url || previewDoc.filename || '') || previewDoc.mimetype === 'application/pdf' ? (
                    <iframe src={previewDoc.url} title={previewDoc.name} style={{ width:'100%', height:'72vh', border:'none', borderRadius:'var(--radius-md)', background:'#fff' }} />
                  ) : (
                    <div style={{ padding:'40px', textAlign:'center', background:'var(--zeus-navy-light)', borderRadius:'var(--radius-md)' }}>
                      <FileText size={48} style={{ color:'var(--text-muted)', marginBottom:'12px' }} />
                      <p style={{ color:'var(--text-muted)', fontSize:'0.9rem', marginBottom:'12px' }}>
                        Preview not available for this file type.
                      </p>
                      <a href={previewDoc.url} target="_blank" rel="noreferrer" className="btn btn-primary btn-sm">
                        <FileText size={14} /> Open File
                      </a>
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ padding:'30px', textAlign:'center', color:'var(--text-muted)' }}>
                  No preview available
                </div>
              )}
            </Modal>
          </div>

          {/* ── DocuSeal ── */}
          <div className="card" style={{ marginTop: 16 }}>
            <div className="card-header" style={{ padding: '10px 14px' }}>
              <span style={{ fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--zeus-gold)' }}>
                DocuSeal
              </span>
            </div>
            <div style={{ padding: '14px' }}>
              <Suspense fallback={<div style={{textAlign:'center',padding:'20px'}}><div className="spinner" /></div>}>
                <CaseSigningPanel caseId={id} caseData={c} documents={c?.documents || []} />
              </Suspense>
            </div>
          </div>
        </>)}

        {activeTab === 'communication' && (
          <Suspense fallback={<div style={{textAlign:'center',padding:'30px'}}><div className="spinner" /></div>}>
            <CaseCommunicationTab caseId={id} />
          </Suspense>
        )}

        {/* ═══════════════════ BILLING TAB ═══════════════════ */}
        {activeTab === 'billing' && (() => {
          // ── Civil Billing ─────────────────────────────────────────────────
          if (isCivil) {
            return (
              <Suspense fallback={<div style={{textAlign:'center',padding:'30px'}}><div className="spinner" /></div>}>
                <CaseCivilBillingTab caseId={id} />
              </Suspense>
            );
          }
          // ── WC Billing ────────────────────────────────────────────────────
          if (!isPI) {

            const totalBenefits  = wcBilling.benefit_payments.reduce((s,r) => s + (parseFloat(r.paid)||0), 0);
            const outstandingMed = wcBilling.medical_charges.reduce((s,r) => s + ((parseFloat(r.billed)||0) - (parseFloat(r.paid)||0)), 0);
            const totalExpenses  = wcBilling.case_expenses.reduce((s,r) => s + (parseFloat(r.amount)||0), 0);
            const openLiens      = wcBilling.liens.filter(l => l.status !== 'resolved').length;
            const openFlags      = wcBilling.billing_flags.filter(f => !f.resolved).length;

            const wcStatusColor = (s) => {
              const map = { paid:'#22c55e', underpaid:'#f97316', pending:'#9ca3af', suspended:'#ef4444',
                disputed:'#a855f7', not_started:'#3b82f6', authorized:'#22c55e', requested:'#3b82f6',
                denied:'#ef4444', recoverable:'#22c55e', internal:'#3b82f6', written_off:'#6b7280',
                open:'#ef4444', negotiating:'#f97316', resolved:'#22c55e' };
              return map[s] || '#9ca3af';
            };
            const wcStatusBg = (s) => wcStatusColor(s) + '18';
            const wcStatusLabel = (s) => (s||'').replace(/_/g,' ').replace(/\b\w/g,c=>c.toUpperCase());
            const fmtDate = (d) => d ? new Date(d+'T12:00:00').toLocaleDateString('en-US',{month:'2-digit',day:'2-digit',year:'numeric'}) : '—';

            return (
              <div style={{ display:'flex', flexDirection:'column', gap:'14px' }}>

                {/* ── Header ── */}
                <div className="wc-billing-header" style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', flexWrap:'wrap', gap:'10px' }}>
                  <div>
                    <div style={{ fontWeight:700, fontSize:'1rem', color:'var(--text-primary)' }}>Workers' Comp Billing & Payment Management</div>
                    <div style={{ fontSize:'0.78rem', color:'var(--text-muted)', marginTop:3 }}>Benefits first, then treatment finance, expenses, reimbursement, and resolution.</div>
                  </div>
                  <div className="wc-billing-actions" style={{ display:'flex', gap:'8px', flexWrap:'wrap' }}>
                    {[['benefit_payments','Add Benefit Payment'],['medical_charges','Add Medical Charge'],['case_expenses','Add Expense'],['liens','Add Lien'],['billing_flags','Add Flag']].map(([type, label]) => (
                      <button key={type} onClick={() => openWcModal(type)}
                        style={{
                          fontSize:'0.8rem', fontWeight:600, fontFamily:'inherit',
                          padding:'6px 12px', borderRadius:8, cursor:'pointer',
                          background:'var(--zeus-navy-light)',
                          border:'1px solid var(--zeus-border)',
                          color:'var(--text-primary)',
                          display:'flex', alignItems:'center', gap:5,
                          transition:'background 0.15s',
                        }}
                        onMouseOver={e => e.currentTarget.style.background='rgba(212,168,83,0.12)'}
                        onMouseOut={e => e.currentTarget.style.background='var(--zeus-navy-light)'}
                      >
                        <Plus size={12} />{label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* ── KPI Cards ── */}
                <div className="billing-kpi-grid" style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'10px' }}>
                  {[
                    { label:'Benefits Paid to Date', value:fmtMoney(totalBenefits), color:'#7c3aed', sub:`TTD and TPD across the life of the claim` },
                    { label:'Outstanding Medical',   value:fmtMoney(outstandingMed), color:'#f97316', sub:`${wcBilling.medical_charges.length} provider charge${wcBilling.medical_charges.length!==1?'s':''}` },
                    { label:'Case Expenses',         value:fmtMoney(totalExpenses), color:'#3b82f6', sub:`${wcBilling.case_expenses.length} expense item${wcBilling.case_expenses.length!==1?'s':''}` },
                    { label:'Resolution Stage',      value: c?.status === 'settled' ? 'Disbursement' : 'Pre-Disbursement', color:'#22c55e', sub: openFlags > 0 ? `${openFlags} workflow flag${openFlags!==1?'s':''} open` : 'No open flags' },
                  ].map(({ label, value, color, sub }) => (
                    <div key={label} className="card" style={{ padding:'14px' }}>
                      <div style={{ fontSize:'0.68rem', color:'var(--text-muted)', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:'4px' }}>{label}</div>
                      <div style={{ fontSize: label==='Resolution Stage' ? '1.05rem' : '1.4rem', fontWeight:800, color, lineHeight:1.1, marginBottom:'3px' }}>{value}</div>
                      <div style={{ fontSize:'0.72rem', color:'var(--text-muted)' }}>{sub}</div>
                    </div>
                  ))}
                </div>

                {/* ── Two-column layout ── */}
                <div className="billing-two-col" style={{ display:'grid', gridTemplateColumns:'1fr 300px', gap:'14px', alignItems:'start' }}>

                  {/* LEFT col */}
                  <div style={{ display:'flex', flexDirection:'column', gap:'14px' }}>

                    {/* Benefits Ledger */}
                    <div className="card" style={{ padding:0, overflow:'hidden' }}>
                      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', padding:'14px 16px 10px', borderBottom:'1px solid var(--border)' }}>
                        <div>
                          <div style={{ fontWeight:700, fontSize:'0.95rem', color:'var(--text-primary)' }}>Benefits Ledger</div>
                          <div style={{ fontSize:'0.78rem', color:'var(--text-muted)', marginTop:2 }}>Payment history, periods covered, and dispute status.</div>
                        </div>
                        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                          <span style={{ fontSize:'0.68rem', fontWeight:700, padding:'3px 10px', borderRadius:20, background:'rgba(124,58,237,0.1)', color:'#7c3aed' }}>Benefits First</span>
                        </div>
                      </div>
                      {/* Mini-tabs */}
                      <div className="wc-benefit-tabs" style={{ display:'flex', gap:8, padding:'10px 16px 0', flexWrap:'wrap' }}>
                        {[['payments','Payments'],['suspensions','Suspensions'],['disputes','Disputes'],['rate_changes','Rate Changes']].map(([k,l]) => (
                          <button key={k} onClick={() => setWcBenefitTab(k)} style={{
                            padding:'5px 14px', borderRadius:20, cursor:'pointer', fontSize:'0.8rem',
                            fontWeight: wcBenefitTab===k ? 700 : 500, fontFamily:'inherit',
                            border:`1.5px solid ${wcBenefitTab===k ? '#3b82f6' : 'var(--border)'}`,
                            background: wcBenefitTab===k ? 'rgba(59,130,246,0.1)' : 'transparent',
                            color: wcBenefitTab===k ? '#3b82f6' : 'var(--text-muted)',
                          }}>{l}</button>
                        ))}
                      </div>
                      <div className="wc-table-wrap" style={{ padding:'10px 0 4px', overflowX:'auto' }}>
                        {wcBilling.benefit_payments.length === 0
                          ? <div style={{ textAlign:'center', padding:'24px', color:'var(--text-muted)', fontSize:'0.85rem' }}>No benefit payments recorded yet</div>
                          : <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.82rem' }}>
                              <thead><tr style={{ background:'var(--bg-base)' }}>
                                {['Payment Date','Benefit Type','Period Covered','Rate','Paid','Owed','Status',''].map(h => (
                                  <th key={h} style={{ padding:'7px 10px', textAlign: h==='' ? 'center' : 'left', fontSize:'0.68rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em', color:'var(--text-muted)', whiteSpace:'nowrap' }}>{h}</th>
                                ))}
                              </tr></thead>
                              <tbody>
                                {wcBilling.benefit_payments.map((r,i) => (
                                  <tr key={r._id} style={{ borderTop:'1px solid var(--border)', background:i%2===0?'transparent':'var(--bg-base)' }}>
                                    <td style={{ padding:'9px 10px', whiteSpace:'nowrap' }}>{fmtDate(r.payment_date)}</td>
                                    <td style={{ padding:'9px 10px' }}>
                                      <div style={{ fontWeight:700, color:'var(--text-primary)' }}>{r.benefit_type}</div>
                                      {r.benefit_label && <div style={{ fontSize:'0.72rem', color:'var(--text-muted)' }}>{r.benefit_label}</div>}
                                    </td>
                                    <td style={{ padding:'9px 10px', whiteSpace:'nowrap', fontSize:'0.8rem' }}>
                                      {r.period_start && r.period_end ? `${fmtDate(r.period_start)} – ${fmtDate(r.period_end)}` : r.period_start ? fmtDate(r.period_start) : '—'}
                                    </td>
                                    <td style={{ padding:'9px 10px', fontWeight:700, whiteSpace:'nowrap' }}>{r.rate ? `${fmtMoney(r.rate)}/wk` : '—'}</td>
                                    <td style={{ padding:'9px 10px', fontWeight:700, whiteSpace:'nowrap' }}>{fmtMoney(r.paid)}</td>
                                    <td style={{ padding:'9px 10px', fontWeight:700, whiteSpace:'nowrap', color: (parseFloat(r.owed)||0) > 0 ? '#ef4444' : 'var(--text-primary)' }}>{fmtMoney(r.owed)}</td>
                                    <td style={{ padding:'9px 10px' }}>
                                      <span style={{ fontSize:'0.72rem', fontWeight:700, padding:'3px 10px', borderRadius:20, background:wcStatusBg(r.status), color:wcStatusColor(r.status), whiteSpace:'nowrap' }}>{wcStatusLabel(r.status)}</span>
                                    </td>
                                    <td style={{ padding:'9px 6px', textAlign:'center' }}>
                                      <div style={{ display:'flex', gap:3, justifyContent:'center' }}>
                                        <button onClick={() => openWcModal('benefit_payments', r)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)', padding:2 }}><Pencil size={12} /></button>
                                        <button onClick={() => deleteWc('benefit_payments', r._id)} style={{ background:'none', border:'none', cursor:'pointer', color:'#ef4444', padding:2 }}><Trash2 size={12} /></button>
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                        }
                        <div style={{ padding:'8px 14px', fontSize:'0.75rem', color:'var(--text-muted)', borderTop:'1px solid var(--border)', marginTop:4, lineHeight:1.5 }}>
                          Active WC matters are managed around benefit payments, missed payments, underpayments, suspensions, and disputes long before final disbursement.
                        </div>
                      </div>
                    </div>

                    {/* Medical Authorization & Payment Tracking */}
                    <div className="card" style={{ padding:0, overflow:'hidden' }}>
                      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', padding:'14px 16px 12px', borderBottom:'1px solid var(--border)' }}>
                        <div>
                          <div style={{ fontWeight:700, fontSize:'0.95rem', color:'var(--text-primary)' }}>Medical Authorization & Payment Tracking</div>
                          <div style={{ fontSize:'0.78rem', color:'var(--text-muted)', marginTop:2 }}>Treatment finance for this claim, including authorization, UR, and payment status.</div>
                        </div>
                        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                          <span style={{ fontSize:'0.68rem', fontWeight:700, padding:'3px 10px', borderRadius:20, background:'rgba(59,130,246,0.1)', color:'#3b82f6' }}>Treatment Finance</span>
                        </div>
                      </div>
                      <div className="wc-table-wrap" style={{ overflowX:'auto' }}>
                        {wcBilling.medical_charges.length === 0
                          ? <div style={{ textAlign:'center', padding:'24px', color:'var(--text-muted)', fontSize:'0.85rem' }}>No medical charges recorded yet</div>
                          : <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.82rem' }}>
                              <thead><tr style={{ background:'var(--bg-base)' }}>
                                {['Provider','Specialty','DOS','Billed','Paid','Balance','Authorization','UR / Status',''].map(h => (
                                  <th key={h} style={{ padding:'7px 10px', textAlign:'left', fontSize:'0.68rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em', color:'var(--text-muted)', whiteSpace:'nowrap' }}>{h}</th>
                                ))}
                              </tr></thead>
                              <tbody>
                                {wcBilling.medical_charges.map((r,i) => {
                                  const bal = (parseFloat(r.billed)||0) - (parseFloat(r.paid)||0);
                                  return (
                                    <tr key={r._id} style={{ borderTop:'1px solid var(--border)', background:i%2===0?'transparent':'var(--bg-base)' }}>
                                      <td style={{ padding:'9px 10px', fontWeight:700, color:'var(--text-primary)' }}>{r.provider_name||'—'}</td>
                                      <td style={{ padding:'9px 10px', color:'var(--text-muted)', fontSize:'0.8rem' }}>{r.specialty||'—'}</td>
                                      <td style={{ padding:'9px 10px', whiteSpace:'nowrap', fontSize:'0.8rem' }}>
                                        {r.dos_start && r.dos_end && r.dos_start !== r.dos_end ? `${fmtDate(r.dos_start)} – ${fmtDate(r.dos_end)}` : fmtDate(r.dos_start)}
                                      </td>
                                      <td style={{ padding:'9px 10px', fontWeight:700, whiteSpace:'nowrap' }}>{fmtMoney(r.billed)}</td>
                                      <td style={{ padding:'9px 10px', fontWeight:700, whiteSpace:'nowrap' }}>{fmtMoney(r.paid)}</td>
                                      <td style={{ padding:'9px 10px', fontWeight:700, whiteSpace:'nowrap', color: bal > 0 ? '#ef4444' : '#22c55e' }}>{fmtMoney(bal)}</td>
                                      <td style={{ padding:'9px 10px' }}>
                                        <span style={{ fontSize:'0.72rem', fontWeight:700, padding:'3px 10px', borderRadius:20, background:wcStatusBg(r.auth_status), color:wcStatusColor(r.auth_status), whiteSpace:'nowrap' }}>{wcStatusLabel(r.auth_status)}</span>
                                      </td>
                                      <td style={{ padding:'9px 10px' }}>
                                        {r.ur_status && <span style={{ fontSize:'0.72rem', color:'var(--text-muted)' }}>{r.ur_status}</span>}
                                      </td>
                                      <td style={{ padding:'9px 6px', textAlign:'center' }}>
                                        <div style={{ display:'flex', gap:3, justifyContent:'center' }}>
                                          <button onClick={() => openWcModal('medical_charges', r)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)', padding:2 }}><Pencil size={12} /></button>
                                          <button onClick={() => deleteWc('medical_charges', r._id)} style={{ background:'none', border:'none', cursor:'pointer', color:'#ef4444', padding:2 }}><Trash2 size={12} /></button>
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                        }
                      </div>
                    </div>

                    {/* Case Expenses */}
                    <div className="card" style={{ padding:0, overflow:'hidden' }}>
                      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', padding:'14px 16px 12px', borderBottom:'1px solid var(--border)' }}>
                        <div>
                          <div style={{ fontWeight:700, fontSize:'0.95rem', color:'var(--text-primary)' }}>Case Expenses</div>
                          <div style={{ fontSize:'0.78rem', color:'var(--text-muted)', marginTop:2 }}>Firm-side expenses advanced on the file, separate from medical treatment payments.</div>
                        </div>
                        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                          <span style={{ fontSize:'0.68rem', fontWeight:700, padding:'3px 10px', borderRadius:20, background:'rgba(249,115,22,0.1)', color:'#f97316' }}>Matter Expenses</span>
                        </div>
                      </div>
                      <div className="wc-table-wrap" style={{ overflowX:'auto' }}>
                        {wcBilling.case_expenses.length === 0
                          ? <div style={{ textAlign:'center', padding:'24px', color:'var(--text-muted)', fontSize:'0.85rem' }}>No case expenses recorded yet</div>
                          : <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.82rem' }}>
                              <thead><tr style={{ background:'var(--bg-base)' }}>
                                {['Date','Expense Type','Vendor','Amount','Recovery Status',''].map(h => (
                                  <th key={h} style={{ padding:'7px 10px', textAlign:'left', fontSize:'0.68rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em', color:'var(--text-muted)' }}>{h}</th>
                                ))}
                              </tr></thead>
                              <tbody>
                                {wcBilling.case_expenses.map((r,i) => (
                                  <tr key={r._id} style={{ borderTop:'1px solid var(--border)', background:i%2===0?'transparent':'var(--bg-base)' }}>
                                    <td style={{ padding:'9px 10px', whiteSpace:'nowrap', fontSize:'0.8rem' }}>{fmtDate(r.expense_date)}</td>
                                    <td style={{ padding:'9px 10px', fontWeight:700 }}>{r.expense_type||'—'}</td>
                                    <td style={{ padding:'9px 10px', color:'var(--text-muted)', fontSize:'0.8rem' }}>{r.vendor||'—'}</td>
                                    <td style={{ padding:'9px 10px', fontWeight:700 }}>{fmtMoney(r.amount)}</td>
                                    <td style={{ padding:'9px 10px' }}>
                                      <span style={{ fontSize:'0.72rem', fontWeight:700, padding:'3px 10px', borderRadius:20, background:wcStatusBg(r.recovery_status), color:wcStatusColor(r.recovery_status), whiteSpace:'nowrap' }}>{wcStatusLabel(r.recovery_status)}</span>
                                    </td>
                                    <td style={{ padding:'9px 6px', textAlign:'center' }}>
                                      <div style={{ display:'flex', gap:3, justifyContent:'center' }}>
                                        <button onClick={() => openWcModal('case_expenses', r)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)', padding:2 }}><Pencil size={12} /></button>
                                        <button onClick={() => deleteWc('case_expenses', r._id)} style={{ background:'none', border:'none', cursor:'pointer', color:'#ef4444', padding:2 }}><Trash2 size={12} /></button>
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                        }
                      </div>
                    </div>
                  </div>

                  {/* RIGHT col */}
                  <div className="billing-right-col" style={{ display:'flex', flexDirection:'column', gap:'14px' }}>

                    {/* Quick Totals */}
                    <div className="card" style={{ padding:'14px' }}>
                      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'12px' }}>
                        <div style={{ fontWeight:700, fontSize:'0.9rem', color:'var(--text-primary)' }}>Quick Totals</div>
                        <span style={{ fontSize:'0.68rem', fontWeight:700, padding:'2px 8px', borderRadius:10, background:'rgba(59,130,246,0.1)', color:'#3b82f6' }}>Snapshot</span>
                      </div>
                      <div style={{ background:'var(--bg-base)', borderRadius:8, padding:'10px 12px', border:'1px solid var(--border)' }}>
                        {[
                          ['Total Benefits Paid',       fmtMoney(totalBenefits)],
                          ['Total Medical Billed',      fmtMoney(wcBilling.medical_charges.reduce((s,r)=>s+(parseFloat(r.billed)||0),0))],
                          ['Total Medical Paid',        fmtMoney(wcBilling.medical_charges.reduce((s,r)=>s+(parseFloat(r.paid)||0),0))],
                          ['Outstanding Medical',       fmtMoney(outstandingMed)],
                          ['Total Case Expenses',       fmtMoney(totalExpenses)],
                          ['Open Reimbursements/Offsets', fmtMoney(wcBilling.liens.filter(l=>l.status!=='resolved').reduce((s,r)=>s+(parseFloat(r.amount)||0),0))],
                        ].map(([k,v]) => (
                          <div key={k} style={{ display:'flex', justifyContent:'space-between', fontSize:'0.82rem', padding:'6px 0', borderBottom:'1px solid var(--border)' }}>
                            <span style={{ color:'var(--text-muted)' }}>{k}</span>
                            <strong>{v}</strong>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Liens / Reimbursements */}
                    <div className="card" style={{ padding:'14px' }}>
                      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'10px' }}>
                        <div style={{ fontWeight:700, fontSize:'0.9rem', color:'var(--text-primary)' }}>Liens / Reimbursements / Offsets</div>
                        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                          {openLiens > 0 && <span style={{ fontSize:'0.68rem', fontWeight:700, padding:'2px 8px', borderRadius:10, background:'rgba(239,68,68,0.1)', color:'#ef4444' }}>{openLiens} Open</span>}
                          <button className="btn btn-ghost btn-sm" style={{ padding:'2px 8px', fontSize:'0.75rem' }} onClick={() => openWcModal('liens')}><Plus size={12} /></button>
                        </div>
                      </div>
                      {wcBilling.liens.length === 0
                        ? <div style={{ fontSize:'0.82rem', color:'var(--text-muted)', textAlign:'center', padding:'12px 0' }}>No liens or reimbursements yet</div>
                        : wcBilling.liens.map(r => (
                            <div key={r._id} style={{ padding:'9px 0', borderBottom:'1px solid var(--border)' }}>
                              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:6 }}>
                                <div style={{ flex:1, minWidth:0 }}>
                                  <div style={{ fontWeight:600, fontSize:'0.85rem', color:'var(--text-primary)' }}>{r.title||'—'}</div>
                                  {r.notes && <div style={{ fontSize:'0.72rem', color:'var(--text-muted)', marginTop:2 }}>{r.notes}</div>}
                                </div>
                                <div style={{ display:'flex', alignItems:'center', gap:6, flexShrink:0 }}>
                                  <strong style={{ fontSize:'0.9rem' }}>{fmtMoney(r.amount)}</strong>
                                  <span style={{ fontSize:'0.68rem', fontWeight:700, padding:'2px 7px', borderRadius:10, background:wcStatusBg(r.status), color:wcStatusColor(r.status) }}>{wcStatusLabel(r.status)}</span>
                                  <button onClick={() => openWcModal('liens', r)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)', padding:2 }}><Pencil size={12} /></button>
                                  <button onClick={() => deleteWc('liens', r._id)} style={{ background:'none', border:'none', cursor:'pointer', color:'#ef4444', padding:2 }}><Trash2 size={12} /></button>
                                </div>
                              </div>
                            </div>
                          ))
                      }
                    </div>

                    {/* Resolution Projection */}
                    <div className="card" style={{ padding:'14px' }}>
                      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'12px' }}>
                        <div>
                          <div style={{ fontWeight:700, fontSize:'0.9rem', color:'var(--text-primary)' }}>Resolution / Disbursement</div>
                          <div style={{ fontSize:'0.72rem', color:'var(--text-muted)', marginTop:2 }}>Later-stage settlement projection.</div>
                        </div>
                        <span style={{ fontSize:'0.68rem', fontWeight:700, padding:'2px 8px', borderRadius:10, background:'rgba(34,197,94,0.1)', color:'#22c55e' }}>Later-Stage</span>
                      </div>
                      <div style={{ background:'var(--bg-base)', borderRadius:8, padding:'10px 12px', border:'1px solid var(--border)' }}>
                        <div style={{ fontSize:'0.75rem', fontWeight:700, color:'var(--text-primary)', marginBottom:'10px' }}>
                          {form.name || c?.client_name || 'Client'} · Resolution Projection
                        </div>
                        {(() => {
                          const settlement = parseFloat(form.settlement_amount) || 0;
                          const feeRaw     = parseFloat(form.af_fee) || 0;
                          const attyFee    = settlement * (feeRaw / 100);
                          const lienTotal  = wcBilling.liens.reduce((s,r)=>s+(parseFloat(r.negotiated||r.amount)||0),0);
                          const clientNet  = settlement - attyFee - totalExpenses - lienTotal;
                          return [
                            ['Projected Settlement / Award', fmtMoney(settlement), null],
                            [`Attorney Fee (${feeRaw}%)`,    `(${fmtMoney(attyFee)})`, '#ef4444'],
                            ['Case Expenses',                `(${fmtMoney(totalExpenses)})`, '#ef4444'],
                            ['Negotiated Reimbursements',    `(${fmtMoney(lienTotal)})`, '#ef4444'],
                            ['Trust Received',               fmtMoney(0), null],
                          ].map(([k,v,color]) => (
                            <div key={k} style={{ display:'flex', justifyContent:'space-between', fontSize:'0.82rem', padding:'6px 0', borderBottom:'1px solid var(--border)' }}>
                              <span style={{ color:'var(--text-muted)' }}>{k}</span>
                              <strong style={{ color:color||'var(--text-primary)' }}>{v}</strong>
                            </div>
                          )).concat([
                            <div key="net" style={{ display:'flex', justifyContent:'space-between', fontSize:'0.9rem', padding:'8px 0 0' }}>
                              <strong>Estimated Net to Client</strong>
                              <strong style={{ color: clientNet >= 0 ? '#22c55e' : '#ef4444', fontSize:'1rem' }}>{fmtMoney(clientNet)}</strong>
                            </div>
                          ]);
                        })()}
                      </div>
                    </div>

                    {/* Billing Workflow Flags */}
                    <div className="card" style={{ padding:'14px' }}>
                      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'10px' }}>
                        <div>
                          <div style={{ fontWeight:700, fontSize:'0.9rem', color:'var(--text-primary)' }}>Billing Workflow Flags</div>
                          <div style={{ fontSize:'0.72rem', color:'var(--text-muted)', marginTop:2 }}>Common WC finance checkpoints.</div>
                        </div>
                        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                          {openFlags > 0 && <span style={{ fontSize:'0.68rem', fontWeight:700, padding:'2px 8px', borderRadius:10, background:'rgba(249,115,22,0.1)', color:'#f97316' }}>Action Needed</span>}
                          <button className="btn btn-ghost btn-sm" style={{ padding:'2px 8px', fontSize:'0.75rem' }} onClick={() => openWcModal('billing_flags')}><Plus size={12} /></button>
                        </div>
                      </div>
                      {wcBilling.billing_flags.length === 0
                        ? <div style={{ fontSize:'0.82rem', color:'var(--text-muted)', textAlign:'center', padding:'12px 0' }}>No flags yet</div>
                        : wcBilling.billing_flags.map(r => (
                            <div key={r._id} style={{ padding:'9px 0', borderBottom:'1px solid var(--border)', opacity: r.resolved ? 0.5 : 1 }}>
                              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:6 }}>
                                <div style={{ flex:1, minWidth:0 }}>
                                  <div style={{ fontWeight:600, fontSize:'0.85rem', color:'var(--text-primary)', display:'flex', alignItems:'center', gap:6 }}>
                                    {r.title||'—'}
                                    {r.flag_type && <span style={{ fontSize:'0.68rem', fontWeight:700, padding:'1px 6px', borderRadius:8, background:'rgba(249,115,22,0.1)', color:'#f97316' }}>{r.flag_type}</span>}
                                  </div>
                                  {r.description && <div style={{ fontSize:'0.72rem', color:'var(--text-muted)', marginTop:2 }}>{r.description}</div>}
                                </div>
                                <div style={{ display:'flex', alignItems:'center', gap:4, flexShrink:0 }}>
                                  <button onClick={() => openWcModal('billing_flags', r)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)', padding:2 }}><Pencil size={12} /></button>
                                  <button onClick={() => deleteWc('billing_flags', r._id)} style={{ background:'none', border:'none', cursor:'pointer', color:'#ef4444', padding:2 }}><Trash2 size={12} /></button>
                                </div>
                              </div>
                            </div>
                          ))
                      }
                    </div>

                    {/* Case Financials quick edit */}
                    <div className="card" style={{ padding:'14px' }}>
                      <div style={{ fontWeight:700, fontSize:'0.9rem', color:'var(--text-primary)', marginBottom:'10px' }}>Case Financials</div>
                      {renderField({ label:'Settlement/Award', field:'settlement_amount', obj:'form', type:'number' })}
                      {/* AF Fee — read-only, set in Legal Information */}
                      <div style={{ display:'flex', alignItems:'center', gap:'6px', marginBottom:'6px' }}>
                        <label style={{ fontSize:'0.75rem', color:'var(--text-muted)', minWidth:'90px', textAlign:'right', flexShrink:0 }}>AF Fee %</label>
                        <div style={{ flex:1, display:'flex', alignItems:'center', gap:'8px' }}>
                          <span style={{
                            padding:'5px 10px', fontSize:'0.85rem', fontWeight:700,
                            background:'var(--zeus-navy-light)', border:'1px solid var(--zeus-border)',
                            borderRadius:'var(--radius-sm)', color:'var(--text-primary)',
                          }}>
                            {form.af_fee ? `${form.af_fee}%` : '—'}
                          </span>
                          <span style={{ fontSize:'0.7rem', color:'var(--text-muted)' }}>Set in Legal Information</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          }

          // ── PI Billing ────────────────────────────────────────────────────

          const settlement = parseFloat(form.settlement_amount) || 0;
          const demand     = parseFloat(form.demand_amount)     || 0;
          const feeRaw     = parseFloat(form.af_fee)            || 0;
          const attyFee    = settlement * (feeRaw / 100);
          const medTotal   = billing.medical_bills.reduce((s,r) => s + (parseFloat(r.billed) - parseFloat(r.adjusted||0) - parseFloat(r.paid||0)), 0);
          const lienTotal  = billing.liens.reduce((s,r) => s + parseFloat(r.amount||0), 0);
          const costTotal  = billing.costs.reduce((s,r) => s + parseFloat(r.amount||0), 0);
          const clientNet  = settlement - attyFee - medTotal - lienTotal - costTotal;

          const BILL_STATUS = ['open','verified','negotiation_needed','records_received','paid','waived'];
          const LIEN_STATUS = ['open','verified','negotiation_pending','resolved'];
          const DISB_CAT    = ['attorney_fee','medical','lien','client','cost'];
          const DISB_STATUS = ['pending','paid','cancelled'];

          const statusColor = (s) => {
            if (!s) return 'var(--text-muted)';
            const map = {
              open:'#7c3aed', verified:'#1d4ed8', negotiation_needed:'#c2410c',
              negotiation_pending:'#c2410c', records_received:'#15803d',
              paid:'#15803d', waived:'#6b7280', resolved:'#15803d',
              pending:'#9ca3af', cancelled:'#dc2626'
            };
            return map[s] || '#6b7280';
          };
          const statusBg = (s) => statusColor(s) + '15';
          const statusLabel = (s) => (s||'').replace(/_/g,' ').replace(/\w/g,c=>c.toUpperCase());

          const miniTabs = [
            { key:'medical_bills', label:'Medical Bills',  count: billing.medical_bills.length },
            { key:'liens',         label:'Liens',          count: billing.liens.length },
            { key:'costs',         label:'Case Costs',     count: billing.costs.length },
            { key:'disbursements', label:'Disbursements',  count: billing.disbursements.length },
          ];

          return (
            <div style={{ display:'flex', flexDirection:'column', gap:'14px' }}>

              {/* ── Summary KPI strip ── */}
              <div className="billing-kpi-grid" style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'10px' }}>
                {[
                  { label:'Settlement', value: fmtMoney(settlement), color:'#3b82f6', sub:`Demand: ${fmtMoney(demand)}` },
                  { label:'Attorney Fee', value: fmtMoney(attyFee), color:'#f97316', sub:`${feeRaw}% contingency` },
                  { label:'Medical + Liens', value: fmtMoney(medTotal + lienTotal), color:'#ef4444', sub:`${billing.medical_bills.length} bills · ${billing.liens.length} liens` },
                  { label:'Client Net', value: fmtMoney(clientNet), color: clientNet >= 0 ? '#22c55e' : '#ef4444', sub:'Est. payable to client' },
                ].map(({ label, value, color, sub }) => (
                  <div key={label} className="card" style={{ padding:'14px' }}>
                    <div style={{ fontSize:'0.68rem', color:'var(--text-muted)', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:'4px' }}>{label}</div>
                    <div style={{ fontSize:'1.4rem', fontWeight:800, color, lineHeight:1, marginBottom:'3px' }}>{value}</div>
                    <div style={{ fontSize:'0.72rem', color:'var(--text-muted)' }}>{sub}</div>
                  </div>
                ))}
              </div>

              {/* ── Two-column layout ── */}
              <div className="billing-two-col" style={{ display:'grid', gridTemplateColumns:'1fr 320px', gap:'14px', alignItems:'start' }}>

                {/* LEFT: Billing Detail + Activity */}
                <div style={{ display:'flex', flexDirection:'column', gap:'14px' }}>

                  {/* Settlement numbers */}
                  <div className="card" style={{ padding:'14px' }}>
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'12px' }}>
                      <div style={{ fontWeight:700, fontSize:'0.9rem', color:'var(--text-primary)' }}>Settlement Summary</div>
                      <span style={{ fontSize:'0.68rem', fontWeight:700, padding:'2px 8px', borderRadius:10, background:'rgba(59,130,246,0.1)', color:'#3b82f6' }}>Case Finance</span>
                    </div>
                    <div className="settlement-summary-grid" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px' }}>
                      <div style={{ background:'var(--bg-base)', borderRadius:8, padding:'10px 14px', border:'1px solid var(--border)' }}>
                        <div style={{ fontSize:'0.65rem', color:'var(--text-muted)', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:'8px' }}>Recovery & Fee</div>
                        {[['Demand Amount', fmtMoney(demand)],['Settlement Amount', fmtMoney(settlement)],[`Fee % (${feeRaw}%)`, fmtMoney(attyFee)]].map(([k,v]) => (
                          <div key={k} style={{ display:'flex', justifyContent:'space-between', fontSize:'0.82rem', marginBottom:'5px' }}>
                            <span style={{ color:'var(--text-muted)' }}>{k}</span>
                            <strong>{v}</strong>
                          </div>
                        ))}
                      </div>
                      <div style={{ background:'var(--bg-base)', borderRadius:8, padding:'10px 14px', border:'1px solid var(--border)' }}>
                        <div style={{ fontSize:'0.65rem', color:'var(--text-muted)', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:'8px' }}>Net Sheet Snapshot</div>
                        {[['Case Costs', fmtMoney(costTotal)],['Medical Bills', fmtMoney(medTotal)],['Liens', fmtMoney(lienTotal)]].map(([k,v]) => (
                          <div key={k} style={{ display:'flex', justifyContent:'space-between', fontSize:'0.82rem', marginBottom:'5px' }}>
                            <span style={{ color:'var(--text-muted)' }}>{k}</span>
                            <strong>{v}</strong>
                          </div>
                        ))}
                        <div style={{ display:'flex', justifyContent:'space-between', fontSize:'0.85rem', marginTop:'8px', paddingTop:'8px', borderTop:'1px solid var(--border)' }}>
                          <span style={{ fontWeight:700 }}>Client Net</span>
                          <strong style={{ color: clientNet >= 0 ? '#22c55e' : '#ef4444' }}>{fmtMoney(clientNet)}</strong>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Billing Detail with mini-tabs */}
                  <div className="card billing-detail-card" style={{ padding:0, overflow:'hidden' }}>
                    {/* Card header — matches mockup */}
                    <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', padding:'14px 16px 12px', borderBottom:'1px solid var(--border)' }}>
                      <div>
                        <div style={{ fontWeight:700, fontSize:'0.95rem', color:'var(--text-primary)' }}>Billing Detail</div>
                        <div style={{ fontSize:'0.78rem', color:'var(--text-muted)', marginTop:2 }}>Mini-tabs inside Billing keep the tab compact and easier to use.</div>
                      </div>
                      <div style={{ display:'flex', alignItems:'center', gap:8, flexShrink:0 }}>
                        <span style={{ fontSize:'0.72rem', fontWeight:700, padding:'3px 10px', borderRadius:20, background:'rgba(249,115,22,0.12)', color:'#f97316' }}>PI Billing</span>
                        <button className="btn btn-primary btn-sm"
                          onClick={() => openBillingModal(billingTab)}
                          style={{ display:'flex', alignItems:'center', gap:'4px' }}>
                          <Plus size={13} /> Add
                        </button>
                      </div>
                    </div>
                    {/* Pill-shaped mini-tabs */}
                    <div className="billing-mini-tabs" style={{ display:'flex', gap:8, padding:'12px 16px 0', flexWrap:'wrap' }}>
                      {miniTabs.map(t => (
                        <button key={t.key} onClick={() => setBillingTab(t.key)}
                          style={{
                            padding:'6px 16px', borderRadius:20, cursor:'pointer',
                            fontWeight: billingTab === t.key ? 700 : 500,
                            fontSize:'0.82rem', fontFamily:'inherit',
                            border: `1.5px solid ${billingTab === t.key ? '#3b82f6' : 'var(--border)'}`,
                            background: billingTab === t.key ? 'rgba(59,130,246,0.1)' : 'transparent',
                            color: billingTab === t.key ? '#3b82f6' : 'var(--text-muted)',
                            transition:'all 0.12s',
                          }}>
                          {t.label}{t.count > 0 && ` (${t.count})`}
                        </button>
                      ))}
                    </div>
                    <div style={{ padding:'12px 0 4px' }}>
                    {/* Medical Bills table */}
                    {billingTab === 'medical_bills' && (
                      billing.medical_bills.length === 0
                        ? <div style={{ textAlign:'center', padding:'24px', color:'var(--text-muted)', fontSize:'0.85rem' }}>No medical bills added yet</div>
                        : <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.82rem' }}>
                            <thead><tr style={{ background:'var(--bg-base)' }}>
                              {['Provider','Billed','Adjusted','Paid','Balance','Status',''].map(h => (
                                <th key={h} style={{ padding:'7px 10px', textAlign: h === '' ? 'center' : h === 'Provider' ? 'left' : 'right', fontSize:'0.68rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em', color:'var(--text-muted)' }}>{h}</th>
                              ))}
                            </tr></thead>
                            <tbody>
                              {billing.medical_bills.map((r, i) => {
                                const bal = (parseFloat(r.billed)||0) - (parseFloat(r.adjusted)||0) - (parseFloat(r.paid)||0);
                                return (
                                  <tr key={r._id} style={{ borderTop:'1px solid var(--border)', background: i%2===0 ? 'transparent' : 'var(--bg-base)' }}>
                                    <td style={{ padding:'9px 10px' }}>
                                      <div style={{ fontWeight:600, color:'var(--text-primary)' }}>{r.provider_name||'—'}</div>
                                      {r.provider_type && <div style={{ fontSize:'0.72rem', color:'var(--text-muted)' }}>{r.provider_type}</div>}
                                    </td>
                                    <td style={{ padding:'9px 10px', textAlign:'right' }}>{fmtMoney(r.billed)}</td>
                                    <td style={{ padding:'9px 10px', textAlign:'right' }}>{fmtMoney(r.adjusted)}</td>
                                    <td style={{ padding:'9px 10px', textAlign:'right' }}>{fmtMoney(r.paid)}</td>
                                    <td style={{ padding:'9px 10px', textAlign:'right', fontWeight:700 }}>{fmtMoney(bal)}</td>
                                    <td style={{ padding:'9px 10px', textAlign:'right' }}>
                                      <span style={{ fontSize:'0.75rem', fontWeight:600, padding:'4px 12px', borderRadius:20, background:statusBg(r.status), color:statusColor(r.status), whiteSpace:'nowrap' }}>{statusLabel(r.status)}</span>
                                    </td>
                                    <td style={{ padding:'9px 6px', textAlign:'center' }}>
                                      <div style={{ display:'flex', gap:4, justifyContent:'center' }}>
                                        <button onClick={() => openBillingModal('medical_bills', r)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)', padding:2 }}><Pencil size={12} /></button>
                                        <button onClick={() => deleteBilling('medical_bills', r._id)} style={{ background:'none', border:'none', cursor:'pointer', color:'#ef4444', padding:2 }}><Trash2 size={12} /></button>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                    )}

                    {/* Liens */}
                    {billingTab === 'liens' && (
                      billing.liens.length === 0
                        ? <div style={{ textAlign:'center', padding:'24px', color:'var(--text-muted)', fontSize:'0.85rem' }}>No liens added yet</div>
                        : <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
                            {billing.liens.map(r => (
                              <div key={r._id} style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', padding:'10px 14px', background:'var(--bg-base)', borderRadius:8, border:'1px solid var(--border)' }}>
                                <div>
                                  <div style={{ fontWeight:600, color:'var(--text-primary)', fontSize:'0.85rem' }}>{r.title||'—'}</div>
                                  {r.lienholder && <div style={{ fontSize:'0.75rem', color:'var(--text-muted)' }}>{r.lienholder}</div>}
                                  {r.notes && <div style={{ fontSize:'0.73rem', color:'var(--text-muted)', marginTop:3 }}>{r.notes}</div>}
                                </div>
                                <div style={{ display:'flex', alignItems:'center', gap:'8px', flexShrink:0, marginLeft:12 }}>
                                  <span style={{ fontWeight:700, fontSize:'0.9rem' }}>{fmtMoney(r.amount)}</span>
                                  <span style={{ fontSize:'0.68rem', fontWeight:700, padding:'2px 7px', borderRadius:8, background:statusBg(r.status), color:statusColor(r.status) }}>{statusLabel(r.status)}</span>
                                  <button onClick={() => openBillingModal('liens', r)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)', padding:2 }}><Pencil size={12} /></button>
                                  <button onClick={() => deleteBilling('liens', r._id)} style={{ background:'none', border:'none', cursor:'pointer', color:'#ef4444', padding:2 }}><Trash2 size={12} /></button>
                                </div>
                              </div>
                            ))}
                          </div>
                    )}

                    {/* Case Costs */}
                    {billingTab === 'costs' && (
                      billing.costs.length === 0
                        ? <div style={{ textAlign:'center', padding:'24px', color:'var(--text-muted)', fontSize:'0.85rem' }}>No case costs added yet</div>
                        : <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
                            {billing.costs.map(r => (
                              <div key={r._id} style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', padding:'10px 14px', background:'var(--bg-base)', borderRadius:8, border:'1px solid var(--border)' }}>
                                <div>
                                  <div style={{ fontWeight:600, color:'var(--text-primary)', fontSize:'0.85rem' }}>{r.description||'—'}</div>
                                  {r.notes && <div style={{ fontSize:'0.73rem', color:'var(--text-muted)', marginTop:3 }}>{r.notes}</div>}
                                  {r.recoverable && <span style={{ fontSize:'0.65rem', fontWeight:700, padding:'1px 6px', borderRadius:8, background:'rgba(34,197,94,0.1)', color:'#22c55e', marginTop:4, display:'inline-block' }}>Recoverable</span>}
                                </div>
                                <div style={{ display:'flex', alignItems:'center', gap:'8px', flexShrink:0, marginLeft:12 }}>
                                  <span style={{ fontWeight:700, fontSize:'0.9rem' }}>{fmtMoney(r.amount)}</span>
                                  <button onClick={() => openBillingModal('costs', r)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)', padding:2 }}><Pencil size={12} /></button>
                                  <button onClick={() => deleteBilling('costs', r._id)} style={{ background:'none', border:'none', cursor:'pointer', color:'#ef4444', padding:2 }}><Trash2 size={12} /></button>
                                </div>
                              </div>
                            ))}
                          </div>
                    )}

                    {/* Disbursements */}
                    {billingTab === 'disbursements' && (
                      billing.disbursements.length === 0
                        ? <div style={{ textAlign:'center', padding:'24px', color:'var(--text-muted)', fontSize:'0.85rem' }}>No disbursements added yet</div>
                        : <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.82rem' }}>
                            <thead><tr style={{ background:'var(--bg-base)' }}>
                              {['Payee','Category','Amount','Date','Status',''].map(h => (
                                <th key={h} style={{ padding:'7px 10px', textAlign: h==='' ? 'center' : h==='Payee' ? 'left' : 'right', fontSize:'0.68rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em', color:'var(--text-muted)' }}>{h}</th>
                              ))}
                            </tr></thead>
                            <tbody>
                              {billing.disbursements.map((r, i) => (
                                <tr key={r._id} style={{ borderTop:'1px solid var(--border)', background: i%2===0 ? 'transparent' : 'var(--bg-base)' }}>
                                  <td style={{ padding:'9px 10px', fontWeight:600 }}>{r.payee||'—'}</td>
                                  <td style={{ padding:'9px 10px', textAlign:'right', textTransform:'capitalize' }}>{(r.category||'').replace(/_/g,' ')}</td>
                                  <td style={{ padding:'9px 10px', textAlign:'right', fontWeight:700 }}>{fmtMoney(r.amount)}</td>
                                  <td style={{ padding:'9px 10px', textAlign:'right', color:'var(--text-muted)' }}>{r.paid_date ? new Date(r.paid_date).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'}) : '—'}</td>
                                  <td style={{ padding:'9px 10px', textAlign:'right' }}>
                                    <span style={{ fontSize:'0.75rem', fontWeight:600, padding:'4px 12px', borderRadius:20, background:statusBg(r.status), color:statusColor(r.status), whiteSpace:'nowrap' }}>{statusLabel(r.status)}</span>
                                  </td>
                                  <td style={{ padding:'9px 6px', textAlign:'center' }}>
                                    <div style={{ display:'flex', gap:4, justifyContent:'center' }}>
                                      <button onClick={() => openBillingModal('disbursements', r)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)', padding:2 }}><Pencil size={12} /></button>
                                      <button onClick={() => deleteBilling('disbursements', r._id)} style={{ background:'none', border:'none', cursor:'pointer', color:'#ef4444', padding:2 }}><Trash2 size={12} /></button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                    )}
                    </div>{/* end pill-tabs content wrapper */}
                  </div>
                </div>

                {/* RIGHT: Net Sheet + Case Costs + Liens summary */}
                <div className="billing-right-col" style={{ display:'flex', flexDirection:'column', gap:'14px' }}>

                  {/* Net Sheet */}
                  <div className="card billing-net-sheet" style={{ padding:'14px' }}>
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'12px' }}>
                      <div style={{ fontWeight:700, fontSize:'0.9rem', color:'var(--text-primary)' }}>Net Sheet</div>
                      <span style={{ fontSize:'0.68rem', fontWeight:700, padding:'2px 8px', borderRadius:10, background:'rgba(34,197,94,0.1)', color:'#22c55e' }}>Snapshot</span>
                    </div>
                    <div style={{ background:'var(--bg-base)', borderRadius:8, padding:'12px 14px', border:'1px solid var(--border)' }}>
                      <div style={{ fontSize:'0.75rem', fontWeight:700, color:'var(--text-primary)', marginBottom:'10px' }}>
                        {form.name || c?.client_name || 'Client'} · Settlement Breakdown
                      </div>
                      {[
                        ['Gross Settlement',   fmtMoney(settlement), null],
                        ['Minus Attorney Fee', `(${fmtMoney(attyFee)})`, '#ef4444'],
                        ['Minus Case Costs',   `(${fmtMoney(costTotal)})`, '#ef4444'],
                        ['Minus Medical Bills',`(${fmtMoney(medTotal)})`, '#ef4444'],
                        ['Minus Liens',        `(${fmtMoney(lienTotal)})`, '#ef4444'],
                      ].map(([k,v,color]) => (
                        <div key={k} style={{ display:'flex', justifyContent:'space-between', fontSize:'0.82rem', marginBottom:'6px' }}>
                          <span style={{ color:'var(--text-muted)' }}>{k}</span>
                          <strong style={{ color: color||'var(--text-primary)' }}>{v}</strong>
                        </div>
                      ))}
                      <div style={{ display:'flex', justifyContent:'space-between', fontSize:'0.9rem', marginTop:'10px', paddingTop:'10px', borderTop:'2px solid var(--border)' }}>
                        <strong>Net to Client</strong>
                        <strong style={{ color: clientNet >= 0 ? '#22c55e' : '#ef4444', fontSize:'1rem' }}>{fmtMoney(clientNet)}</strong>
                      </div>
                    </div>
                  </div>

                  {/* Case Costs Summary */}
                  <div className="card" style={{ padding:'14px' }}>
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'10px' }}>
                      <div style={{ fontWeight:700, fontSize:'0.9rem', color:'var(--text-primary)' }}>Case Costs</div>
                      <span style={{ fontSize:'0.68rem', fontWeight:700, padding:'2px 8px', borderRadius:10, background:'rgba(59,130,246,0.1)', color:'#3b82f6' }}>Recoverable</span>
                    </div>
                    {billing.costs.length === 0
                      ? <div style={{ fontSize:'0.82rem', color:'var(--text-muted)', textAlign:'center', padding:'12px 0' }}>No costs yet</div>
                      : billing.costs.map(r => (
                          <div key={r._id} style={{ display:'flex', justifyContent:'space-between', padding:'6px 0', borderBottom:'1px solid var(--border)', fontSize:'0.82rem' }}>
                            <span style={{ color:'var(--text-secondary)' }}>{r.description||'—'}</span>
                            <strong>{fmtMoney(r.amount)}</strong>
                          </div>
                        ))
                    }
                    {billing.costs.length > 0 && (
                      <div style={{ display:'flex', justifyContent:'space-between', fontSize:'0.85rem', marginTop:'8px', fontWeight:700 }}>
                        <span>Total</span><span>{fmtMoney(costTotal)}</span>
                      </div>
                    )}
                  </div>

                  {/* Liens Summary */}
                  <div className="card" style={{ padding:'14px' }}>
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'10px' }}>
                      <div style={{ fontWeight:700, fontSize:'0.9rem', color:'var(--text-primary)' }}>Liens</div>
                      {billing.liens.filter(l=>l.status==='open').length > 0 && (
                        <span style={{ fontSize:'0.68rem', fontWeight:700, padding:'2px 8px', borderRadius:10, background:'rgba(239,68,68,0.1)', color:'#ef4444' }}>
                          {billing.liens.filter(l=>l.status==='open').length} Open
                        </span>
                      )}
                    </div>
                    {billing.liens.length === 0
                      ? <div style={{ fontSize:'0.82rem', color:'var(--text-muted)', textAlign:'center', padding:'12px 0' }}>No liens yet</div>
                      : billing.liens.map(r => (
                          <div key={r._id} style={{ padding:'8px 0', borderBottom:'1px solid var(--border)' }}>
                            <div style={{ display:'flex', justifyContent:'space-between', fontSize:'0.82rem' }}>
                              <strong style={{ color:'var(--text-primary)' }}>{r.title||'—'}</strong>
                              <strong>{fmtMoney(r.amount)}</strong>
                            </div>
                            {r.lienholder && <div style={{ fontSize:'0.72rem', color:'var(--text-muted)', marginTop:2 }}>{r.lienholder}</div>}
                          </div>
                        ))
                    }
                    {billing.liens.length > 0 && (
                      <div style={{ display:'flex', justifyContent:'space-between', fontSize:'0.85rem', marginTop:'8px', fontWeight:700 }}>
                        <span>Total</span><span>{fmtMoney(lienTotal)}</span>
                      </div>
                    )}
                  </div>

                  {/* Demand / Settlement quick edit */}
                  <div className="card" style={{ padding:'14px' }}>
                    <div style={{ fontWeight:700, fontSize:'0.9rem', color:'var(--text-primary)', marginBottom:'10px' }}>Case Financials</div>
                    {renderField({ label:'Demand', field:'demand_amount', obj:'form', type:'number' })}
                    {renderField({ label:'Settlement', field:'settlement_amount', obj:'form', type:'number' })}
                    {/* AF Fee — read-only, set in Legal Information */}
                      <div style={{ display:'flex', alignItems:'center', gap:'6px', marginBottom:'6px' }}>
                      <label style={{ fontSize:'0.75rem', color:'var(--text-muted)', minWidth:'90px', textAlign:'right', flexShrink:0 }}>AF Fee %</label>
                      <div style={{ flex:1, display:'flex', alignItems:'center', gap:'8px' }}>
                        <span style={{
                    padding:'5px 10px', fontSize:'0.85rem', fontWeight:700,
                    background:'var(--zeus-navy-light)', border:'1px solid var(--zeus-border)',
                    borderRadius:'var(--radius-sm)', color:'var(--text-primary)',
                        }}>
                        {form.af_fee ? `${form.af_fee}%` : '—'}
                        </span>
                        <span style={{ fontSize:'0.7rem', color:'var(--text-muted)' }}>Set in Legal Information</span>
                      </div>
                      </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* ── WC Billing Add/Edit Modal ── */}
        {wcModal && (() => {
          const { type, item } = wcModal;
          const labels = { benefit_payments:'Benefit Payment', medical_charges:'Medical Charge', case_expenses:'Case Expense', liens:'Lien / Reimbursement', billing_flags:'Workflow Flag' };
          const fmtDate = (d) => d ? new Date(d+'T12:00:00').toLocaleDateString('en-US',{month:'2-digit',day:'2-digit',year:'numeric'}) : '—';
          return (
            <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center' }}
              onClick={() => setWcModal(null)}>
              <div style={{ background:'var(--zeus-navy)', border:'1px solid var(--zeus-border)', borderRadius:'var(--radius-md)', padding:'22px', width:'480px', maxWidth:'94vw', maxHeight:'88vh', overflowY:'auto' }}
                onClick={e => e.stopPropagation()}>
                <div style={{ fontWeight:700, fontSize:'1rem', color:'var(--text-primary)', marginBottom:'16px' }}>
                  {item ? 'Edit' : 'Add'} {labels[type]}
                </div>

                {type === 'benefit_payments' && (<>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px' }}>
                    <div><label className="form-label">Payment Date</label><input className="form-input" type="date" value={wcForm.payment_date||''} onChange={e=>setWcForm(p=>({...p,payment_date:e.target.value}))} /></div>
                    <div><label className="form-label">Benefit Type</label>
                      <select className="form-select" value={wcForm.benefit_type||'TTD'} onChange={e=>{
                        const labels={TTD:'Temporary Total Disability',TPD:'Temporary Partial Disability',IIBs:'Impairment Income Benefits',SIBs:'Supplemental Income Benefits',LIBs:'Lifetime Income Benefits',Death:'Death Benefits'};
                        setWcForm(p=>({...p,benefit_type:e.target.value,benefit_label:labels[e.target.value]||''}));
                      }}>
                        {['TTD','TPD','IIBs','SIBs','LIBs','Death'].map(t=><option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div><label className="form-label">Period Start</label><input className="form-input" type="date" value={wcForm.period_start||''} onChange={e=>setWcForm(p=>({...p,period_start:e.target.value}))} /></div>
                    <div><label className="form-label">Period End</label><input className="form-input" type="date" value={wcForm.period_end||''} onChange={e=>setWcForm(p=>({...p,period_end:e.target.value}))} /></div>
                    <div><label className="form-label">Weekly Rate ($)</label><input className="form-input" type="number" value={wcForm.rate||''} onChange={e=>setWcForm(p=>({...p,rate:e.target.value}))} /></div>
                    <div><label className="form-label">Amount Paid ($)</label><input className="form-input" type="number" value={wcForm.paid||''} onChange={e=>setWcForm(p=>({...p,paid:e.target.value}))} /></div>
                    <div><label className="form-label">Amount Owed ($)</label><input className="form-input" type="number" value={wcForm.owed||''} onChange={e=>setWcForm(p=>({...p,owed:e.target.value}))} /></div>
                    <div><label className="form-label">Status</label>
                      <select className="form-select" value={wcForm.status||'paid'} onChange={e=>setWcForm(p=>({...p,status:e.target.value}))}>
                        {['paid','underpaid','pending','suspended','disputed','not_started'].map(s=><option key={s} value={s}>{s.replace(/_/g,' ')}</option>)}
                      </select>
                    </div>
                  </div>
                  <div style={{ marginTop:'8px' }}><label className="form-label">Notes</label><textarea className="form-textarea" value={wcForm.notes||''} onChange={e=>setWcForm(p=>({...p,notes:e.target.value}))} style={{ minHeight:60 }} /></div>
                </>)}

                {type === 'medical_charges' && (<>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px' }}>
                    <div style={{ gridColumn:'1/-1' }} ref={wcProvRef}>
                      <label className="form-label">Provider Name</label>
                      <div style={{ position:'relative' }}>
                        <div
                          onClick={() => { setWcProvOpen(true); setWcProvSearch(''); setTimeout(() => wcProvInputRef.current?.focus(), 50); }}
                          style={{
                            display:'flex', alignItems:'center', gap:'6px', padding:'7px 10px',
                            background:'var(--zeus-navy-light)', border:'1px solid var(--zeus-border)',
                            borderRadius:'var(--radius-sm)', cursor:'pointer', minHeight:'36px',
                          }}
                        >
                          {wcProvOpen ? (
                            <input
                              ref={wcProvInputRef}
                              style={{ border:'none', outline:'none', background:'transparent', padding:0, fontSize:'0.85rem', flex:1, color:'var(--text-primary)', fontFamily:'inherit' }}
                              placeholder="Search provider network..."
                              value={wcProvSearch}
                              onChange={(e) => setWcProvSearch(e.target.value)}
                              onKeyDown={(e) => { if (e.key === 'Escape') setWcProvOpen(false); }}
                            />
                          ) : (
                            <span style={{ flex:1, color: wcForm.provider_name ? 'var(--text-primary)' : 'var(--text-muted)', fontSize:'0.85rem' }}>
                              {wcForm.provider_name || 'Search or type provider name...'}
                            </span>
                          )}
                          <ChevronDown size={14} style={{ color:'var(--text-muted)', flexShrink:0, transform: wcProvOpen ? 'rotate(180deg)' : 'none', transition:'transform 0.2s' }} />
                        </div>

                        {wcProvOpen && (
                          <div style={{
                            position:'absolute', top:'100%', left:0, right:0, marginTop:'2px',
                            background:'var(--zeus-navy)', border:'1px solid var(--zeus-border)',
                            borderRadius:'var(--radius-md)', boxShadow:'0 8px 24px rgba(0,0,0,0.5)',
                            zIndex:100, maxHeight:'220px', overflowY:'auto',
                          }}>
                            {/* Type custom name */}
                            {wcProvSearch.trim() && ![...providers, ...(c?.treatments||[])].some(p => (p.name||p.provider_name||'').toLowerCase() === wcProvSearch.trim().toLowerCase()) && (
                              <div
                                onClick={() => { setWcForm(p => ({ ...p, provider_name: wcProvSearch.trim() })); setWcProvOpen(false); setWcProvSearch(''); }}
                                style={{ padding:'7px 12px', cursor:'pointer', fontSize:'0.82rem', borderBottom:'1px solid var(--zeus-border)', color:'var(--text-primary)', fontStyle:'italic' }}
                                onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                                onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                              >
                                Use "<strong>{wcProvSearch.trim()}</strong>" as typed
                              </div>
                            )}
                            {/* Treating providers on this case */}
                            {(() => {
                              const treatingOnCase = (c?.treatments || []).filter(t => t.provider_name?.trim());
                              const q = wcProvSearch.toLowerCase();
                              const filtered = treatingOnCase.filter(t => !q || t.provider_name.toLowerCase().includes(q) || (t.provider_type||'').toLowerCase().includes(q));
                              if (filtered.length === 0) return null;
                              return (<>
                                <div style={{ padding:'5px 12px', fontSize:'0.68rem', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.06em', background:'var(--bg-base)' }}>Treating Providers on This Case</div>
                                {filtered.map((t,i) => (
                                  <div key={`tp-${i}`}
                                    onClick={() => {
                                      setWcForm(p => ({ ...p, provider_name: t.provider_name, specialty: t.provider_type || p.specialty }));
                                      setWcProvOpen(false); setWcProvSearch('');
                                    }}
                                    style={{ padding:'7px 12px', cursor:'pointer', fontSize:'0.82rem', borderBottom:'1px solid rgba(255,255,255,0.03)', background: wcForm.provider_name === t.provider_name ? 'var(--zeus-gold-dim)' : 'transparent' }}
                                    onMouseOver={(e) => { if (wcForm.provider_name !== t.provider_name) e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
                                    onMouseOut={(e) => { if (wcForm.provider_name !== t.provider_name) e.currentTarget.style.background = 'transparent'; }}
                                  >
                                    <div style={{ color:'var(--text-primary)', fontWeight: wcForm.provider_name === t.provider_name ? 600 : 400 }}>{t.provider_name}</div>
                                    {t.provider_type && <div style={{ fontSize:'0.72rem', color:'var(--text-muted)', marginTop:'1px' }}>{t.provider_type}</div>}
                                  </div>
                                ))}
                              </>);
                            })()}
                            {/* Full Provider Network */}
                            {(() => {
                              const treatingNames = new Set((c?.treatments || []).map(t => t.provider_name));
                              const q = wcProvSearch.toLowerCase();
                              const networkOnly = providers.filter(p => !treatingNames.has(p.name) && (!q || p.name?.toLowerCase().includes(q) || (p.provider_type||'').toLowerCase().includes(q) || (p.specialty||'').toLowerCase().includes(q)));
                              if (networkOnly.length === 0 && !wcProvSearch) return null;
                              return (<>
                                <div style={{ padding:'5px 12px', fontSize:'0.68rem', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.06em', background:'var(--bg-base)' }}>Provider Network</div>
                                {networkOnly.map(p => (
                                  <div key={p._id}
                                    onClick={() => {
                                      setWcForm(prev => ({ ...prev, provider_name: p.name, specialty: p.provider_type || p.specialty || prev.specialty }));
                                      setWcProvOpen(false); setWcProvSearch('');
                                    }}
                                    style={{ padding:'7px 12px', cursor:'pointer', fontSize:'0.82rem', borderBottom:'1px solid rgba(255,255,255,0.03)', background: wcForm.provider_name === p.name ? 'var(--zeus-gold-dim)' : 'transparent' }}
                                    onMouseOver={(e) => { if (wcForm.provider_name !== p.name) e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
                                    onMouseOut={(e) => { if (wcForm.provider_name !== p.name) e.currentTarget.style.background = 'transparent'; }}
                                  >
                                    <div style={{ color:'var(--text-primary)', fontWeight: wcForm.provider_name === p.name ? 600 : 400 }}>{p.name}</div>
                                    {(p.provider_type || p.specialty || p.phone) && (
                                      <div style={{ fontSize:'0.72rem', color:'var(--text-muted)', marginTop:'1px' }}>
                                        {p.provider_type}{p.specialty ? ` · ${p.specialty}` : ''}{p.phone ? ` · ${p.phone}` : ''}
                                      </div>
                                    )}
                                  </div>
                                ))}
                                {networkOnly.length === 0 && wcProvSearch && (
                                  <div style={{ padding:'10px 12px', fontSize:'0.82rem', color:'var(--text-muted)', textAlign:'center' }}>No matching providers</div>
                                )}
                              </>);
                            })()}
                          </div>
                        )}
                      </div>
                    </div>
                    <div><label className="form-label">Specialty</label><input className="form-input" value={wcForm.specialty||''} onChange={e=>setWcForm(p=>({...p,specialty:e.target.value}))} /></div>
                    <div><label className="form-label">DOS Start</label><input className="form-input" type="date" value={wcForm.dos_start||''} onChange={e=>setWcForm(p=>({...p,dos_start:e.target.value}))} /></div>
                    <div><label className="form-label">DOS End</label><input className="form-input" type="date" value={wcForm.dos_end||''} onChange={e=>setWcForm(p=>({...p,dos_end:e.target.value}))} /></div>
                    <div><label className="form-label">Billed ($)</label><input className="form-input" type="number" value={wcForm.billed||''} onChange={e=>setWcForm(p=>({...p,billed:e.target.value}))} /></div>
                    <div><label className="form-label">Paid ($)</label><input className="form-input" type="number" value={wcForm.paid||''} onChange={e=>setWcForm(p=>({...p,paid:e.target.value}))} /></div>
                    <div><label className="form-label">Authorization</label>
                      <select className="form-select" value={wcForm.auth_status||'pending'} onChange={e=>setWcForm(p=>({...p,auth_status:e.target.value}))}>
                        {['authorized','requested','pending','denied'].map(s=><option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                    <div><label className="form-label">UR / Carrier Status</label><input className="form-input" value={wcForm.ur_status||''} onChange={e=>setWcForm(p=>({...p,ur_status:e.target.value}))} placeholder="e.g. Pending UR, Partial payment review" /></div>
                  </div>
                  <div style={{ marginTop:'8px' }}><label className="form-label">Notes</label><textarea className="form-textarea" value={wcForm.notes||''} onChange={e=>setWcForm(p=>({...p,notes:e.target.value}))} style={{ minHeight:60 }} /></div>
                </>)}

                {type === 'case_expenses' && (<>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px' }}>
                    <div><label className="form-label">Expense Date</label><input className="form-input" type="date" value={wcForm.expense_date||''} onChange={e=>setWcForm(p=>({...p,expense_date:e.target.value}))} /></div>
                    <div><label className="form-label">Expense Type</label><input className="form-input" value={wcForm.expense_type||''} onChange={e=>setWcForm(p=>({...p,expense_type:e.target.value}))} placeholder="e.g. IME Cost, Medical Records" /></div>
                    <div><label className="form-label">Vendor</label><input className="form-input" value={wcForm.vendor||''} onChange={e=>setWcForm(p=>({...p,vendor:e.target.value}))} /></div>
                    <div><label className="form-label">Amount ($)</label><input className="form-input" type="number" value={wcForm.amount||''} onChange={e=>setWcForm(p=>({...p,amount:e.target.value}))} /></div>
                    <div><label className="form-label">Recovery Status</label>
                      <select className="form-select" value={wcForm.recovery_status||'recoverable'} onChange={e=>setWcForm(p=>({...p,recovery_status:e.target.value}))}>
                        {['recoverable','internal','written_off'].map(s=><option key={s} value={s}>{s.replace(/_/g,' ')}</option>)}
                      </select>
                    </div>
                  </div>
                  <div style={{ marginTop:'8px' }}><label className="form-label">Notes</label><textarea className="form-textarea" value={wcForm.notes||''} onChange={e=>setWcForm(p=>({...p,notes:e.target.value}))} style={{ minHeight:60 }} /></div>
                </>)}

                {type === 'liens' && (<>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px' }}>
                    <div style={{ gridColumn:'1/-1' }}><label className="form-label">Title</label><input className="form-input" value={wcForm.title||''} onChange={e=>setWcForm(p=>({...p,title:e.target.value}))} placeholder="e.g. Employer Health Plan Reimbursement" /></div>
                    <div><label className="form-label">Amount ($)</label><input className="form-input" type="number" value={wcForm.amount||''} onChange={e=>setWcForm(p=>({...p,amount:e.target.value}))} /></div>
                    <div><label className="form-label">Negotiated ($)</label><input className="form-input" type="number" value={wcForm.negotiated||''} onChange={e=>setWcForm(p=>({...p,negotiated:e.target.value}))} /></div>
                    <div><label className="form-label">Status</label>
                      <select className="form-select" value={wcForm.status||'open'} onChange={e=>setWcForm(p=>({...p,status:e.target.value}))}>
                        {['open','negotiating','resolved'].map(s=><option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                  </div>
                  <div style={{ marginTop:'8px' }}><label className="form-label">Notes</label><textarea className="form-textarea" value={wcForm.notes||''} onChange={e=>setWcForm(p=>({...p,notes:e.target.value}))} style={{ minHeight:60 }} /></div>
                </>)}

                {type === 'billing_flags' && (<>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px' }}>
                    <div style={{ gridColumn:'1/-1' }}><label className="form-label">Flag Title</label><input className="form-input" value={wcForm.title||''} onChange={e=>setWcForm(p=>({...p,title:e.target.value}))} placeholder="e.g. Benefit underpayment needs follow-up" /></div>
                    <div><label className="form-label">Flag Type</label><input className="form-input" value={wcForm.flag_type||''} onChange={e=>setWcForm(p=>({...p,flag_type:e.target.value}))} placeholder="e.g. TPD, MRI, Lien" /></div>
                    <div style={{ display:'flex', alignItems:'center', gap:8, paddingTop:'20px' }}>
                      <input type="checkbox" id="wc_resolved" checked={!!wcForm.resolved} onChange={e=>setWcForm(p=>({...p,resolved:e.target.checked}))} />
                      <label htmlFor="wc_resolved" style={{ fontSize:'0.85rem', color:'var(--text-secondary)' }}>Resolved</label>
                    </div>
                  </div>
                  <div style={{ marginTop:'8px' }}><label className="form-label">Description</label><textarea className="form-textarea" value={wcForm.description||''} onChange={e=>setWcForm(p=>({...p,description:e.target.value}))} style={{ minHeight:80 }} placeholder="Describe the action needed or issue to track..." /></div>
                </>)}

                <div style={{ display:'flex', justifyContent:'flex-end', gap:'8px', marginTop:'18px' }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => setWcModal(null)}>Cancel</button>
                  <button className="btn btn-primary btn-sm" onClick={saveWcItem} disabled={wcSaving}>
                    {wcSaving ? 'Saving…' : item ? 'Update' : 'Add'}
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* ── PI Billing Add/Edit Modal ── */}
        {billingModal && (() => {
          const { type, item } = billingModal;
          const labels = { medical_bills:'Medical Bill', liens:'Lien', costs:'Case Cost', disbursements:'Disbursement' };
          return (
            <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center' }}
              onClick={() => setBillingModal(null)}>
              <div style={{ background:'var(--zeus-navy)', border:'1px solid var(--zeus-border)', borderRadius:'var(--radius-md)', padding:'22px', width:'460px', maxWidth:'94vw', maxHeight:'88vh', overflowY:'auto' }}
                onClick={e => e.stopPropagation()}>
                <div style={{ fontWeight:700, fontSize:'1rem', color:'var(--text-primary)', marginBottom:'16px' }}>
                  {item ? 'Edit' : 'Add'} {labels[type]}
                </div>

                {type === 'medical_bills' && (<>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px' }}>
                    <div style={{ gridColumn:'1/-1' }}>
                      <label className="form-label">Provider <span style={{ fontSize:'0.72rem', color:'var(--text-muted)', fontWeight:400 }}>(from Medical Info — Treating Providers)</span></label>
                      <select className="form-select"
                        value={billingForm.provider_name||''}
                        onChange={e => {
                          const allTP = (c?.treatments || []).filter(t => t.provider_name);
                          const selected = allTP.find(t => t.provider_name === e.target.value);
                          setBillingForm(p => ({
                            ...p,
                            provider_name: e.target.value,
                            provider_type: selected?.provider_type || p.provider_type || '',
                          }));
                        }}>
                        <option value="">Select provider…</option>
                        {(c?.treatments || []).filter(t => t.provider_name && (t.treating_status||'current') === 'current').length > 0 && (
                          <optgroup label="Currently Treating">
                            {(c?.treatments || []).filter(t => t.provider_name && (t.treating_status||'current') === 'current').map((t,i) => (
                              <option key={`cur-${i}`} value={t.provider_name}>{t.provider_name}{t.provider_type ? ` — ${t.provider_type}` : ''}</option>
                            ))}
                          </optgroup>
                        )}
                        {(c?.treatments || []).filter(t => t.provider_name && t.treating_status === 'former').length > 0 && (
                          <optgroup label="No Longer Treating">
                            {(c?.treatments || []).filter(t => t.provider_name && t.treating_status === 'former').map((t,i) => (
                              <option key={`fmr-${i}`} value={t.provider_name}>{t.provider_name}{t.provider_type ? ` — ${t.provider_type}` : ''}</option>
                            ))}
                          </optgroup>
                        )}
                        {!(c?.treatments||[]).some(t=>t.provider_name) && (
                          <option disabled>No treating providers on this case yet</option>
                        )}
                      </select>
                      {!(c?.treatments||[]).some(t=>t.provider_name) && (
                        <div style={{ fontSize:'0.75rem', color:'var(--text-muted)', marginTop:4 }}>Add providers in the Medical Info tab first.</div>
                      )}
                    </div>
                    <div><label className="form-label">Provider Type</label><input className="form-input" value={billingForm.provider_type||''} readOnly style={{ opacity:0.7, cursor:'default' }} /></div>
                    <div><label className="form-label">Billed Amount</label><input className="form-input" type="number" value={billingForm.billed||''} onChange={e=>setBillingForm(p=>({...p,billed:e.target.value}))} /></div>
                    <div><label className="form-label">Adjusted</label><input className="form-input" type="number" value={billingForm.adjusted||''} onChange={e=>setBillingForm(p=>({...p,adjusted:e.target.value}))} /></div>
                    <div><label className="form-label">Paid</label><input className="form-input" type="number" value={billingForm.paid||''} onChange={e=>setBillingForm(p=>({...p,paid:e.target.value}))} /></div>
                    <div><label className="form-label">Status</label>
                      <select className="form-select" value={billingForm.status||'open'} onChange={e=>setBillingForm(p=>({...p,status:e.target.value}))}>
                        {['open','verified','negotiation_needed','records_received','paid','waived'].map(s=><option key={s} value={s}>{statusLabel(s)}</option>)}
                      </select>
                    </div>
                  </div>
                  <div style={{ marginTop:'8px' }}><label className="form-label">Notes</label><textarea className="form-textarea" value={billingForm.notes||''} onChange={e=>setBillingForm(p=>({...p,notes:e.target.value}))} style={{ minHeight:60 }} /></div>
                </>)}

                {type === 'liens' && (<>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px' }}>
                    <div><label className="form-label">Lien Title</label><input className="form-input" value={billingForm.title||''} onChange={e=>setBillingForm(p=>({...p,title:e.target.value}))} /></div>
                    <div><label className="form-label">Lienholder</label><input className="form-input" value={billingForm.lienholder||''} onChange={e=>setBillingForm(p=>({...p,lienholder:e.target.value}))} /></div>
                    <div><label className="form-label">Amount</label><input className="form-input" type="number" value={billingForm.amount||''} onChange={e=>setBillingForm(p=>({...p,amount:e.target.value}))} /></div>
                    <div><label className="form-label">Negotiated</label><input className="form-input" type="number" value={billingForm.negotiated||''} onChange={e=>setBillingForm(p=>({...p,negotiated:e.target.value}))} /></div>
                    <div><label className="form-label">Status</label>
                      <select className="form-select" value={billingForm.status||'open'} onChange={e=>setBillingForm(p=>({...p,status:e.target.value}))}>
                        {['open','verified','negotiation_pending','resolved'].map(s=><option key={s} value={s}>{statusLabel(s)}</option>)}
                      </select>
                    </div>
                  </div>
                  <div style={{ marginTop:'8px' }}><label className="form-label">Notes</label><textarea className="form-textarea" value={billingForm.notes||''} onChange={e=>setBillingForm(p=>({...p,notes:e.target.value}))} style={{ minHeight:60 }} /></div>
                </>)}

                {type === 'costs' && (<>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px' }}>
                    <div style={{ gridColumn:'1/-1' }}><label className="form-label">Description</label><input className="form-input" value={billingForm.description||''} onChange={e=>setBillingForm(p=>({...p,description:e.target.value}))} /></div>
                    <div><label className="form-label">Amount</label><input className="form-input" type="number" value={billingForm.amount||''} onChange={e=>setBillingForm(p=>({...p,amount:e.target.value}))} /></div>
                    <div style={{ display:'flex', alignItems:'center', gap:'8px', paddingTop:'20px' }}>
                      <input type="checkbox" id="recoverable" checked={!!billingForm.recoverable} onChange={e=>setBillingForm(p=>({...p,recoverable:e.target.checked}))} />
                      <label htmlFor="recoverable" style={{ fontSize:'0.85rem', color:'var(--text-secondary)' }}>Recoverable</label>
                    </div>
                  </div>
                  <div style={{ marginTop:'8px' }}><label className="form-label">Notes</label><textarea className="form-textarea" value={billingForm.notes||''} onChange={e=>setBillingForm(p=>({...p,notes:e.target.value}))} style={{ minHeight:60 }} /></div>
                </>)}

                {type === 'disbursements' && (<>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px' }}>
                    <div><label className="form-label">Payee</label><input className="form-input" value={billingForm.payee||''} onChange={e=>setBillingForm(p=>({...p,payee:e.target.value}))} /></div>
                    <div><label className="form-label">Category</label>
                      <select className="form-select" value={billingForm.category||'client'} onChange={e=>setBillingForm(p=>({...p,category:e.target.value}))}>
                        {['attorney_fee','medical','lien','client','cost'].map(s=><option key={s} value={s}>{s.replace(/_/g,' ')}</option>)}
                      </select>
                    </div>
                    <div><label className="form-label">Amount</label><input className="form-input" type="number" value={billingForm.amount||''} onChange={e=>setBillingForm(p=>({...p,amount:e.target.value}))} /></div>
                    <div><label className="form-label">Paid Date</label><input className="form-input" type="date" value={billingForm.paid_date||''} onChange={e=>setBillingForm(p=>({...p,paid_date:e.target.value}))} /></div>
                    <div><label className="form-label">Status</label>
                      <select className="form-select" value={billingForm.status||'pending'} onChange={e=>setBillingForm(p=>({...p,status:e.target.value}))}>
                        {['pending','paid','cancelled'].map(s=><option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                  </div>
                  <div style={{ marginTop:'8px' }}><label className="form-label">Notes</label><textarea className="form-textarea" value={billingForm.notes||''} onChange={e=>setBillingForm(p=>({...p,notes:e.target.value}))} style={{ minHeight:60 }} /></div>
                </>)}

                <div style={{ display:'flex', justifyContent:'flex-end', gap:'8px', marginTop:'18px' }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => setBillingModal(null)}>Cancel</button>
                  <button className="btn btn-primary btn-sm" onClick={saveBillingItem} disabled={billingSaving}>
                    {billingSaving ? 'Saving…' : item ? 'Update' : 'Add'}
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* ═══════════════════ TREATMENTS TAB ═══════════════════ */}
        {activeTab === 'treatments' && (
          <div style={{ display:'flex', flexDirection:'column', gap:'16px' }}>

            {/* ── Injury Summary Card ── */}
            <div className="card" style={{ padding:'16px' }}>
              <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'12px' }}>
                <ClipboardList size={16} style={{ color:'var(--zeus-gold)' }} />
                <span style={{ fontWeight:700, fontSize:'0.88rem', color:'var(--text-primary)' }}>Injury Summary</span>
              </div>

              <div className="injury-summary-grid" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'16px', alignItems:'start' }}>

                {/* ── COLUMN 1: Body Parts ── */}
                <div style={{ display:'flex', flexDirection:'column', gap:'10px' }}>

                  {/* Body Parts Injured */}
                  <div>
                    <label style={{ fontSize:'0.72rem', color:'var(--text-muted)', fontWeight:600, display:'block', marginBottom:'4px', textTransform:'uppercase', letterSpacing:'0.04em' }}>Body Parts Injured</label>
                    <div ref={bodyPartsRef} style={{ position:'relative' }}>
                      <div
                        onClick={() => setShowBodyParts(!showBodyParts)}
                        style={{
                          padding:'5px 8px', fontSize:'0.82rem', minHeight:'32px',
                          background:'var(--zeus-navy-light)', border:'1px solid var(--zeus-border)',
                          borderRadius:'var(--radius-sm)', cursor:'pointer',
                          display:'flex', flexWrap:'wrap', gap:'4px', alignItems:'center',
                        }}
                      >
                        {(form.body_parts || []).length === 0 ? (
                          <span style={{ color:'var(--text-muted)', fontSize:'0.82rem' }}>Select body parts...</span>
                        ) : (
                          (form.body_parts || []).map(bp => (
                            <span key={bp} style={{
                              background:'var(--zeus-gold-dim)', color:'var(--zeus-gold)', padding:'1px 8px',
                              borderRadius:'10px', fontSize:'0.72rem', fontWeight:600,
                              display:'inline-flex', alignItems:'center', gap:'4px',
                            }}>
                              {bp}
                              <X size={10} style={{ cursor:'pointer' }} onClick={(e) => {
                                e.stopPropagation();
                                setForm(prev => ({...prev, body_parts: (prev.body_parts || []).filter(x => x !== bp)}));
                              }} />
                            </span>
                          ))
                        )}
                        <ChevronDown size={14} style={{ marginLeft:'auto', color:'var(--text-muted)', flexShrink:0 }} />
                      </div>
                      {showBodyParts && (
                        <div style={{
                          position:'absolute', top:'100%', left:0, right:0, zIndex:20,
                          background:'var(--zeus-navy)', border:'1px solid var(--zeus-border)',
                          borderRadius:'var(--radius-sm)', maxHeight:'200px', overflowY:'auto',
                          boxShadow:'0 4px 20px rgba(0,0,0,0.3)',
                        }}>
                          {(customLists.body_parts || []).map(bp => {
                            const isSelected = (form.body_parts || []).includes(bp);
                            return (
                              <div key={bp} onClick={() => {
                                setForm(prev => ({
                                  ...prev,
                                  body_parts: isSelected
                                    ? (prev.body_parts || []).filter(x => x !== bp)
                                    : [...(prev.body_parts || []), bp],
                                }));
                              }} style={{
                                padding:'5px 10px', cursor:'pointer', fontSize:'0.82rem',
                                background: isSelected ? 'var(--zeus-gold-dim)' : 'transparent',
                                color: isSelected ? 'var(--zeus-gold)' : 'var(--text-primary)',
                                display:'flex', alignItems:'center', gap:'6px',
                              }}>
                                <input type="checkbox" checked={isSelected} readOnly style={{ accentColor:'var(--zeus-gold)' }} />
                                {bp}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Compensable Body Parts */}
                  <div>
                    <label style={{ fontSize:'0.72rem', color:'var(--text-muted)', fontWeight:600, display:'block', marginBottom:'4px', textTransform:'uppercase', letterSpacing:'0.04em' }}>Compensable Body Parts</label>
                    <div ref={compensableRef} style={{ position:'relative' }}>
                      <div
                        onClick={() => setShowCompensable(!showCompensable)}
                        style={{
                          padding:'5px 8px', fontSize:'0.82rem', minHeight:'32px',
                          background:'var(--zeus-navy-light)', border:'1px solid var(--zeus-border)',
                          borderRadius:'var(--radius-sm)', cursor:'pointer',
                          display:'flex', flexWrap:'wrap', gap:'4px', alignItems:'center',
                        }}
                      >
                        {(form.compensable || []).length === 0 ? (
                          <span style={{ color:'var(--text-muted)', fontSize:'0.82rem' }}>Select compensable body parts...</span>
                        ) : (
                          (form.compensable || []).map(bp => (
                            <span key={bp} style={{
                              background:'var(--zeus-gold-dim)', color:'var(--zeus-gold)', padding:'1px 8px',
                              borderRadius:'10px', fontSize:'0.72rem', fontWeight:600,
                              display:'inline-flex', alignItems:'center', gap:'4px',
                            }}>
                              {bp}
                              <X size={10} style={{ cursor:'pointer' }} onClick={(e) => {
                                e.stopPropagation();
                                setForm(prev => ({...prev, compensable: (prev.compensable || []).filter(x => x !== bp)}));
                              }} />
                            </span>
                          ))
                        )}
                        <ChevronDown size={14} style={{ marginLeft:'auto', color:'var(--text-muted)', flexShrink:0 }} />
                      </div>
                      {showCompensable && (
                        <div style={{
                          position:'absolute', top:'100%', left:0, right:0, zIndex:20,
                          background:'var(--zeus-navy)', border:'1px solid var(--zeus-border)',
                          borderRadius:'var(--radius-sm)', maxHeight:'200px', overflowY:'auto',
                          boxShadow:'0 4px 20px rgba(0,0,0,0.3)',
                        }}>
                          {(customLists.body_parts || []).map(bp => {
                            const isSelected = (form.compensable || []).includes(bp);
                            return (
                              <div key={bp} onClick={() => {
                                setForm(prev => ({
                                  ...prev,
                                  compensable: isSelected
                                    ? (prev.compensable || []).filter(x => x !== bp)
                                    : [...(prev.compensable || []), bp],
                                }));
                              }} style={{
                                padding:'5px 10px', cursor:'pointer', fontSize:'0.82rem',
                                background: isSelected ? 'var(--zeus-gold-dim)' : 'transparent',
                                color: isSelected ? 'var(--zeus-gold)' : 'var(--text-primary)',
                                display:'flex', alignItems:'center', gap:'6px',
                              }}>
                                <input type="checkbox" checked={isSelected} readOnly style={{ accentColor:'var(--zeus-gold)' }} />
                                {bp}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Disputed Body Parts */}
                  <div>
                    <label style={{ fontSize:'0.72rem', color:'var(--text-muted)', fontWeight:600, display:'block', marginBottom:'4px', textTransform:'uppercase', letterSpacing:'0.04em' }}>Disputed Body Parts</label>
                    <div ref={disputesRef} style={{ position:'relative' }}>
                      <div
                        onClick={() => setShowDisputes(!showDisputes)}
                        style={{
                          padding:'5px 8px', fontSize:'0.82rem', minHeight:'32px',
                          background:'var(--zeus-navy-light)', border:'1px solid var(--zeus-border)',
                          borderRadius:'var(--radius-sm)', cursor:'pointer',
                          display:'flex', flexWrap:'wrap', gap:'4px', alignItems:'center',
                        }}
                      >
                        {(form.disputes || []).length === 0 ? (
                          <span style={{ color:'var(--text-muted)', fontSize:'0.82rem' }}>Select disputed body parts...</span>
                        ) : (
                          (form.disputes || []).map(bp => (
                            <span key={bp} style={{
                              background:'var(--zeus-gold-dim)', color:'var(--zeus-gold)', padding:'1px 8px',
                              borderRadius:'10px', fontSize:'0.72rem', fontWeight:600,
                              display:'inline-flex', alignItems:'center', gap:'4px',
                            }}>
                              {bp}
                              <X size={10} style={{ cursor:'pointer' }} onClick={(e) => {
                                e.stopPropagation();
                                setForm(prev => ({...prev, disputes: (prev.disputes || []).filter(x => x !== bp)}));
                              }} />
                            </span>
                          ))
                        )}
                        <ChevronDown size={14} style={{ marginLeft:'auto', color:'var(--text-muted)', flexShrink:0 }} />
                      </div>
                      {showDisputes && (
                        <div style={{
                          position:'absolute', top:'100%', left:0, right:0, zIndex:20,
                          background:'var(--zeus-navy)', border:'1px solid var(--zeus-border)',
                          borderRadius:'var(--radius-sm)', maxHeight:'200px', overflowY:'auto',
                          boxShadow:'0 4px 20px rgba(0,0,0,0.3)',
                        }}>
                          {(customLists.body_parts || []).map(bp => {
                            const isSelected = (form.disputes || []).includes(bp);
                            return (
                              <div key={bp} onClick={() => {
                                setForm(prev => ({
                                  ...prev,
                                  disputes: isSelected
                                    ? (prev.disputes || []).filter(x => x !== bp)
                                    : [...(prev.disputes || []), bp],
                                }));
                              }} style={{
                                padding:'5px 10px', cursor:'pointer', fontSize:'0.82rem',
                                background: isSelected ? 'var(--zeus-gold-dim)' : 'transparent',
                                color: isSelected ? 'var(--zeus-gold)' : 'var(--text-primary)',
                                display:'flex', alignItems:'center', gap:'6px',
                              }}>
                                <input type="checkbox" checked={isSelected} readOnly style={{ accentColor:'var(--zeus-gold)' }} />
                                {bp}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* ── COLUMN 2: Mechanism, Symptoms, Diagnosis ── */}
                <div style={{ display:'flex', flexDirection:'column', gap:'10px' }}>

                  {/* Mechanism of Injury */}
                  <div>
                    <label style={{ fontSize:'0.72rem', color:'var(--text-muted)', fontWeight:600, display:'block', marginBottom:'4px', textTransform:'uppercase', letterSpacing:'0.04em' }}>Mechanism of Injury (MOI)</label>
                    <textarea className="form-textarea" style={{ minHeight:'70px', fontSize:'0.85rem', padding:'6px 10px' }}
                      value={form.mechanism_of_injury || ''} placeholder="Describe how the injury occurred..."
                      onChange={(e) => setForm(prev => ({ ...prev, mechanism_of_injury: e.target.value }))}
                    />
                  </div>

                  {/* Symptoms */}
                  <div>
                    <label style={{ fontSize:'0.72rem', color:'var(--text-muted)', fontWeight:600, display:'block', marginBottom:'4px', textTransform:'uppercase', letterSpacing:'0.04em' }}>Symptoms</label>
                    <textarea className="form-textarea" style={{ minHeight:'70px', fontSize:'0.85rem', padding:'6px 10px' }}
                      value={form.symptoms || ''} placeholder="Describe current symptoms..."
                      onChange={(e) => setForm(prev => ({ ...prev, symptoms: e.target.value }))}
                    />
                  </div>

                  {/* Injury Summary / Diagnosis */}
                  <div>
                    <label style={{ fontSize:'0.72rem', color:'var(--text-muted)', fontWeight:600, display:'block', marginBottom:'4px', textTransform:'uppercase', letterSpacing:'0.04em' }}>Injury Summary / Diagnosis</label>
                    <textarea className="form-textarea" style={{ minHeight:'70px', fontSize:'0.85rem', padding:'6px 10px' }}
                      value={form.injury_summary || ''} placeholder="Summary of injuries, diagnoses, and clinical findings..."
                      onChange={(e) => setForm(prev => ({ ...prev, injury_summary: e.target.value }))}
                    />
                  </div>
                </div>

              </div>
            </div>

            {/* ── Treating Providers Table ── */}
            <div className="card" style={{ padding:'16px' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'12px' }}>
                <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                  <Stethoscope size={16} style={{ color:'var(--zeus-gold)' }} />
                  <span style={{ fontWeight:700, fontSize:'0.88rem', color:'var(--text-primary)' }}>Treating Providers</span>
                  <span style={{ fontSize:'0.75rem', color:'var(--text-muted)', background:'var(--zeus-navy-light)', padding:'2px 8px', borderRadius:'10px' }}>
                    {(c.treatments || []).length}
                  </span>
                </div>
                <button className="btn btn-primary btn-sm" onClick={() => { setTreatForm({ ...emptyTreatForm }); setEditingTreatIdx(null); setTreatProvOpen(false); setTreatProvSearch(''); setShowProviderDetails(false); setShowTreatment(true); }}>
                  <Plus size={14} /> Add Provider
                </button>
              </div>

              {/* Status Filter Tabs */}
              {(c.treatments || []).length > 0 && (
                <div style={{ display:'flex', gap:'4px', marginBottom:'14px' }}>
                  {[
                    { key:'all', label:'All', count: (c.treatments || []).length },
                    { key:'current', label:'Current', count: (c.treatments || []).filter(t => (t.treating_status || 'current') === 'current').length },
                    { key:'former', label:'Former', count: (c.treatments || []).filter(t => t.treating_status === 'former').length },
                  ].map(f => (
                    <button key={f.key}
                      onClick={() => setTreatFilter(f.key)}
                      style={{
                        padding:'4px 12px', borderRadius:'14px', border:'none', cursor:'pointer',
                        fontSize:'0.78rem', fontWeight:600, transition:'all 0.15s',
                        background: treatFilter === f.key ? (f.key === 'current' ? 'rgba(34,197,94,0.15)' : f.key === 'former' ? 'rgba(239,68,68,0.12)' : 'var(--zeus-gold-dim)') : 'rgba(255,255,255,0.04)',
                        color: treatFilter === f.key ? (f.key === 'current' ? '#22c55e' : f.key === 'former' ? '#ef4444' : 'var(--zeus-gold)') : 'var(--text-muted)',
                      }}
                    >
                      {f.label} ({f.count})
                    </button>
                  ))}
                </div>
              )}

              {(() => {
                const filtered = (c.treatments || []).filter(t => {
                  if (treatFilter === 'current') return (t.treating_status || 'current') === 'current';
                  if (treatFilter === 'former') return t.treating_status === 'former';
                  return true;
                });
                // Keep original indices for editing
                const indexedFiltered = (c.treatments || []).map((t, i) => ({ ...t, _origIdx: i })).filter(t => {
                  if (treatFilter === 'current') return (t.treating_status || 'current') === 'current';
                  if (treatFilter === 'former') return t.treating_status === 'former';
                  return true;
                });

                return indexedFiltered.length > 0 ? (
                <div style={{ overflowX:'auto' }}>
                  <table className="table" style={{ fontSize:'0.82rem' }}>
                    <thead>
                      <tr>
                        <th>Provider</th>
                        <th>Status</th>
                        <th>Type</th>
                        <th>Contact</th>
                        <th>Dates</th>
                        <th>Next Appt</th>
                        <th>Records</th>
                        <th>Bills</th>
                        <th style={{ textAlign:'right' }}>Cost</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {indexedFiltered.map((t) => {
                        const i = t._origIdx;
                        const isCurrent = (t.treating_status || 'current') === 'current';
                        const hasGap = t.gap_notes;
                        const recordsStatus = t.records_received_date ? 'received' : t.records_requested ? 'pending' : 'none';
                        const billsStatus = t.bills_received_date ? 'received' : t.bills_requested ? 'pending' : 'none';
                        return (
                          <tr key={i} style={{ cursor:'pointer', opacity: isCurrent ? 1 : 0.65 }} onClick={() => openEditTreatment(i)}
                            onMouseOver={(e) => e.currentTarget.style.background = 'rgba(212,168,83,0.04)'}
                            onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                          >
                            <td>
                              <div style={{ fontWeight:600, color:'var(--text-primary)' }}>{t.provider_name || '—'}</div>
                              {t.address && <div style={{ fontSize:'0.72rem', color:'var(--text-muted)' }}>{t.address}</div>}
                            </td>
                            <td>
                              <span style={{
                                padding:'2px 10px', borderRadius:'10px', fontSize:'0.7rem', fontWeight:700,
                                background: isCurrent ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.1)',
                                color: isCurrent ? '#22c55e' : '#ef4444',
                                display:'inline-flex', alignItems:'center', gap:'4px',
                                whiteSpace:'nowrap',
                              }}>
                                <span style={{ width:'6px', height:'6px', borderRadius:'50%', background: isCurrent ? '#22c55e' : '#ef4444', flexShrink:0 }} />
                                {isCurrent ? 'Current' : 'Former'}
                              </span>
                            </td>
                            <td><span className="badge">{t.provider_type || '—'}</span></td>
                            <td style={{ fontSize:'0.78rem' }}>
                              {t.phone && <div style={{ display:'flex', alignItems:'center', gap:'3px' }}><Phone size={11} style={{ color:'var(--text-muted)' }} />{t.phone}</div>}
                              {t.fax && <div style={{ display:'flex', alignItems:'center', gap:'3px', color:'var(--text-muted)' }}>F: {t.fax}</div>}
                              {t.email && <div style={{ display:'flex', alignItems:'center', gap:'3px' }}><Mail size={11} style={{ color:'var(--text-muted)' }} />{t.email}</div>}
                              {!t.phone && !t.fax && !t.email && '—'}
                            </td>
                            <td style={{ fontSize:'0.78rem', whiteSpace:'nowrap' }}>
                              {t.treatment_start ? formatDate(t.treatment_start) : '—'}
                              {t.treatment_end ? ` → ${formatDate(t.treatment_end)}` : ''}
                              {hasGap && <div style={{ color:'var(--status-red)', fontSize:'0.7rem', display:'flex', alignItems:'center', gap:'3px', marginTop:'2px' }}><AlertTriangle size={11} />Gap noted</div>}
                            </td>
                            <td style={{ fontSize:'0.78rem', whiteSpace:'nowrap' }}>
                              {t.next_appointment ? (
                                <span style={{ color: new Date(t.next_appointment) < new Date() ? 'var(--status-red)' : 'var(--zeus-gold)', fontWeight:600 }}>
                                  {formatDate(t.next_appointment)}
                                </span>
                              ) : '—'}
                            </td>
                            <td>
                              <span style={{
                                padding:'2px 8px', borderRadius:'10px', fontSize:'0.7rem', fontWeight:600,
                                background: recordsStatus === 'received' ? 'rgba(34,197,94,0.12)' : recordsStatus === 'pending' ? 'rgba(234,179,8,0.12)' : 'rgba(255,255,255,0.04)',
                                color: recordsStatus === 'received' ? '#22c55e' : recordsStatus === 'pending' ? '#eab308' : 'var(--text-muted)',
                              }}>
                                {recordsStatus === 'received' ? ' Received' : recordsStatus === 'pending' ? ' Requested' : '—'}
                              </span>
                            </td>
                            <td>
                              <span style={{
                                padding:'2px 8px', borderRadius:'10px', fontSize:'0.7rem', fontWeight:600,
                                background: billsStatus === 'received' ? 'rgba(34,197,94,0.12)' : billsStatus === 'pending' ? 'rgba(234,179,8,0.12)' : 'rgba(255,255,255,0.04)',
                                color: billsStatus === 'received' ? '#22c55e' : billsStatus === 'pending' ? '#eab308' : 'var(--text-muted)',
                              }}>
                                {billsStatus === 'received' ? ' Received' : billsStatus === 'pending' ? ' Requested' : '—'}
                              </span>
                            </td>
                            <td style={{ textAlign:'right', fontWeight:600 }}>{t.cost ? formatCurrency(t.cost) : '—'}</td>
                            <td onClick={(e) => e.stopPropagation()}>
                              <button className="btn btn-ghost btn-sm" onClick={() => handleRemoveTreatment(i)} title="Remove" style={{ color:'var(--status-red)', padding:'4px' }}>
                                <Trash2 size={14} />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (c.treatments || []).length > 0 ? (
                <div className="empty-state" style={{ padding:'24px' }}>
                  <Stethoscope />
                  <h3>No {treatFilter === 'current' ? 'current' : 'former'} providers</h3>
                  <p style={{ fontSize:'0.85rem', color:'var(--text-muted)' }}>
                    {treatFilter === 'current' ? 'All providers are marked as former.' : 'No providers have been marked as former yet.'}
                  </p>
                </div>
              ) : (
                <div className="empty-state" style={{ padding:'32px' }}>
                  <Stethoscope />
                  <h3>No treating providers</h3>
                  <p style={{ fontSize:'0.85rem', color:'var(--text-muted)' }}>Add providers to track treatment, records, and billing.</p>
                </div>
              );
              })()}
            </div>

            {/* ── Treatment Gaps Warning ── */}
            {treatmentGaps.length > 0 && (
              <div className="card" style={{ padding:'14px', border:'1px solid rgba(239,68,68,0.3)', background:'rgba(239,68,68,0.04)' }}>
                <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'8px' }}>
                  <AlertTriangle size={16} style={{ color:'var(--status-red)' }} />
                  <span style={{ fontWeight:700, fontSize:'0.85rem', color:'var(--status-red)' }}>Treatment Gaps Detected</span>
                </div>
                {treatmentGaps.map((g, i) => (
                  <div key={i} style={{ padding:'6px 0', fontSize:'0.82rem', color:'var(--text-secondary)', borderBottom: i < treatmentGaps.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none' }}>
                    <strong style={{ color:'var(--status-red)' }}>{g.days} days</strong> gap between{' '}
                    <strong>{g.from}</strong> ({formatDate(g.fromDate)}) and{' '}
                    <strong>{g.to}</strong> ({formatDate(g.toDate)})
                  </div>
                ))}
              </div>
            )}

            {/* ── Outstanding Records/Bills Summary ── */}
            {(c.treatments || []).some(t => t.outstanding_records || t.outstanding_bills || (t.records_requested && !t.records_received_date) || (t.bills_requested && !t.bills_received_date)) && (
              <div className="card" style={{ padding:'16px' }}>
                <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'10px' }}>
                  <FileText size={16} style={{ color:'var(--zeus-gold)' }} />
                  <span style={{ fontWeight:700, fontSize:'0.88rem', color:'var(--text-primary)' }}>Outstanding Records & Bills</span>
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px' }}>
                  {(c.treatments || []).filter(t => t.outstanding_records || (t.records_requested && !t.records_received_date)).map((t, i) => (
                    <div key={'r'+i} style={{ padding:'8px 12px', background:'rgba(234,179,8,0.06)', border:'1px solid rgba(234,179,8,0.15)', borderRadius:'var(--radius-sm)' }}>
                      <div style={{ fontWeight:600, fontSize:'0.82rem', color:'var(--text-primary)' }}>{t.provider_name}</div>
                      <div style={{ fontSize:'0.75rem', color:'#eab308' }}>
                         Records: {t.records_received_date ? `Received ${formatDate(t.records_received_date)}` : t.records_requested ? `Requested ${t.records_requested_date ? formatDate(t.records_requested_date) : ''}` : 'Not requested'}
                      </div>
                      {t.outstanding_records && <div style={{ fontSize:'0.75rem', color:'var(--text-muted)', marginTop:'2px' }}>{t.outstanding_records}</div>}
                    </div>
                  ))}
                  {(c.treatments || []).filter(t => t.outstanding_bills || (t.bills_requested && !t.bills_received_date)).map((t, i) => (
                    <div key={'b'+i} style={{ padding:'8px 12px', background:'rgba(234,179,8,0.06)', border:'1px solid rgba(234,179,8,0.15)', borderRadius:'var(--radius-sm)' }}>
                      <div style={{ fontWeight:600, fontSize:'0.82rem', color:'var(--text-primary)' }}>{t.provider_name}</div>
                      <div style={{ fontSize:'0.75rem', color:'#eab308' }}>
                         Bills: {t.bills_received_date ? `Received ${formatDate(t.bills_received_date)}` : t.bills_requested ? `Requested ${t.bills_requested_date ? formatDate(t.bills_requested_date) : ''}` : 'Not requested'}
                      </div>
                      {t.outstanding_bills && <div style={{ fontSize:'0.75rem', color:'var(--text-muted)', marginTop:'2px' }}>{t.outstanding_bills}</div>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ═══════════════════ RELATIONSHIPS TAB ═══════════════════ */}

      </div>
      </div>{/* end scrollable container */}

      {/* ═══════════════════ UPLOAD MODAL ═══════════════════ */}
      {showUpload && (
        <Modal title="Upload Document" onClose={closeUpload} wider>
          <div style={{ display:'flex', flexDirection:'column', gap:'14px' }}>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }}>
              <div>
                <label className="form-label">Document Name</label>
                <input className="form-input" value={docForm.name} onChange={(e) => setDocForm(prev => ({...prev, name: e.target.value}))} />
              </div>
              <div>
                <label className="form-label">Category</label>
                <select className="form-select" value={docForm.category} onChange={(e) => setDocForm(prev => ({...prev, category: e.target.value}))}>
                  {DOCUMENT_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
            </div>

            {/* Drop zone */}
            <div
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={onDrop}
              onClick={() => fileInputRef.current?.click()}
              style={{
                border: `2px dashed ${isDragging ? 'var(--zeus-gold)' : docFile ? 'rgba(212,175,55,0.5)' : 'var(--zeus-border)'}`,
                borderRadius:'var(--radius-md)',
                padding: docFile ? '16px 20px' : '28px 20px',
                textAlign:'center',
                cursor:'pointer',
                background: isDragging ? 'rgba(212,175,55,0.06)' : docFile ? 'rgba(212,175,55,0.04)' : 'transparent',
                transition:'all 0.2s',
                display:'flex', alignItems:'center', justifyContent:'center', gap:'10px',
              }}
            >
              <UploadCloud size={20} style={{ color: docFile ? 'var(--zeus-gold)' : 'var(--text-muted)', flexShrink:0 }} />
              <div style={{ textAlign:'left' }}>
                <div style={{ color: docFile ? 'var(--text-primary)' : 'var(--text-muted)', fontSize:'0.85rem', fontWeight: docFile ? 600 : 400 }}>
                  {docFile ? docFile.name : 'Drop file here or click to browse'}
                </div>
                {docFile && (
                  <div style={{ fontSize:'0.72rem', color:'var(--text-muted)', marginTop:'2px' }}>
                    {(docFile.size / 1024).toFixed(1)} KB · Click to change file
                  </div>
                )}
                {ocrEnabled && !docFile && (
                  <div style={{ color:'var(--text-muted)', fontSize:'0.72rem', marginTop:'3px', display:'flex', alignItems:'center', gap:'4px' }}>
                    <Scan size={11} /> OCR analysis runs automatically on images &amp; PDFs
                  </div>
                )}
              </div>
              <input ref={fileInputRef} type="file" style={{ display:'none' }} onChange={(e) => onPickFile(e.target.files?.[0])} />
            </div>

            {/* ── OCR Panel ── */}
            {ocrEnabled && docFile && !ocrDismissed && (
              <div style={{ border:'1px solid rgba(212,175,55,0.25)', borderRadius:'var(--radius-md)', overflow:'hidden' }}>

                {/* Scanning state */}
                {(ocr.status === 'loading' || ocr.status === 'scanning') && (
                  <div style={{ padding:'14px 16px', background:'rgba(212,175,55,0.05)' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'8px' }}>
                      <Scan size={14} style={{ color:'var(--zeus-gold)' }} />
                      <span style={{ fontSize:'0.8rem', fontWeight:600, color:'var(--zeus-gold)' }}>
                        {ocr.status === 'loading' ? 'Loading OCR engine…' : `Scanning document… ${ocr.progress}%`}
                      </span>
                    </div>
                    <div style={{ height:'4px', borderRadius:'2px', background:'var(--zeus-border)', overflow:'hidden' }}>
                      <div style={{ height:'100%', borderRadius:'2px', background:'var(--zeus-gold)', width:`${ocr.progress}%`, transition:'width 0.3s' }} />
                    </div>
                  </div>
                )}

                {/* Error state */}
                {ocr.status === 'error' && (
                  <div style={{ padding:'12px 16px', background:'rgba(239,68,68,0.06)', display:'flex', alignItems:'center', justifyContent:'space-between', gap:'8px' }}>
                    <span style={{ fontSize:'0.8rem', color:'#ef4444' }}>OCR scan failed — document will still upload normally.</span>
                    <button onClick={() => setOcrDismissed(true)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)', padding:'2px' }}><X size={13} /></button>
                  </div>
                )}

                {/* Done — suggestions */}
                {ocr.status === 'done' && ocr.result && (
                  <>
                    {ocr.result.suggestions.length === 0 ? (
                      <div style={{ padding:'12px 16px', background:'rgba(212,175,55,0.04)', display:'flex', alignItems:'center', justifyContent:'space-between', gap:'8px' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:'7px' }}>
                          <Scan size={13} style={{ color:'var(--text-muted)' }} />
                          <span style={{ fontSize:'0.8rem', color:'var(--text-muted)' }}>
                            OCR complete — no matching appointments or providers found in this document.
                          </span>
                        </div>
                        <button onClick={() => setOcrDismissed(true)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)', padding:'2px', flexShrink:0 }}><X size={13} /></button>
                      </div>
                    ) : (
                      <div>
                        {/* Header */}
                        <div style={{ padding:'10px 14px', background:'rgba(212,175,55,0.08)', borderBottom:'1px solid rgba(212,175,55,0.15)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                          <div style={{ display:'flex', alignItems:'center', gap:'7px' }}>
                            <Sparkles size={13} style={{ color:'var(--zeus-gold)' }} />
                            <span style={{ fontSize:'0.8rem', fontWeight:700, color:'var(--zeus-gold)' }}>
                              {ocr.result.suggestions.filter(s => !appliedOcrIds.has(s.id)).length} suggestion{ocr.result.suggestions.filter(s => !appliedOcrIds.has(s.id)).length !== 1 ? 's' : ''} found
                            </span>
                            {ocr.result.clientInDoc && (
                              <span style={{ fontSize:'0.68rem', background:'rgba(34,197,94,0.15)', color:'#22c55e', padding:'1px 7px', borderRadius:'10px', fontWeight:600 }}>Client name detected</span>
                            )}
                          </div>
                          <button onClick={() => setOcrDismissed(true)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)', padding:'2px' }}><X size={13} /></button>
                        </div>

                        {/* Suggestion cards */}
                        {(() => {
                          const pending = ocr.result.suggestions.filter(s => !appliedOcrIds.has(s.id));
                          if (pending.length === 0) return (
                            <div style={{ padding:'12px 16px', display:'flex', alignItems:'center', gap:'7px' }}>
                              <CheckCircle size={14} style={{ color:'#22c55e' }} />
                              <span style={{ fontSize:'0.8rem', color:'var(--text-muted)' }}>All suggestions applied.</span>
                            </div>
                          );
                          return (
                            <div style={{ display:'flex', flexDirection:'column', gap:0 }}>
                              {pending.map((s, si) => (
                                <div key={s.id} style={{
                                  padding:'10px 14px',
                                  borderBottom: si < pending.length - 1 ? '1px solid var(--zeus-border)' : 'none',
                                  display:'flex', alignItems:'flex-start', gap:'10px',
                                }}>
                                  {/* Confidence dot */}
                                  <div style={{
                                    width:8, height:8, borderRadius:'50%', flexShrink:0, marginTop:5,
                                    background: s.confidence === 'high' ? '#22c55e' : '#f59e0b',
                                  }} title={s.confidence === 'high' ? 'High confidence' : 'Medium confidence'} />
                                  <div style={{ flex:1, minWidth:0 }}>
                                    <div style={{ fontSize:'0.82rem', fontWeight:600, color:'var(--text-primary)', marginBottom:'2px' }}>{s.title}</div>
                                    <div style={{ fontSize:'0.75rem', color:'var(--text-muted)', lineHeight:1.4 }}>{s.description}</div>
                                  </div>
                                  <button
                                    onClick={() => applyOcrSuggestion(s)}
                                    style={{
                                      flexShrink:0, display:'flex', alignItems:'center', gap:'4px',
                                      padding:'5px 10px', borderRadius:'6px', border:'1px solid rgba(212,175,55,0.4)',
                                      background:'rgba(212,175,55,0.1)', color:'var(--zeus-gold)',
                                      fontSize:'0.75rem', fontWeight:700, cursor:'pointer', transition:'all 0.15s',
                                    }}
                                    onMouseOver={e => e.currentTarget.style.background='rgba(212,175,55,0.2)'}
                                    onMouseOut={e => e.currentTarget.style.background='rgba(212,175,55,0.1)'}
                                  >
                                    Apply <ChevronRight size={11} />
                                  </button>
                                </div>
                              ))}
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </>
                )}

              {ocr.status === 'done' && ocr.result && ocr.result.entities && (
                <OcrDetectedInfo entities={ocr.result.entities} />
              )}
              </div>
            )}

            <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
              <button className="btn btn-primary" onClick={doUpload}
                disabled={docUploading || ocr.status === 'scanning' || ocr.status === 'loading'}
                style={{ flex:1, justifyContent:'center' }}>
                {docUploading
                  ? <><span className="spinner" style={{ width:13, height:13 }} /> Uploading…</>
                  : (ocr.status === 'scanning' || ocr.status === 'loading')
                    ? <><Scan size={13} /> Scanning…</>
                    : <><UploadCloud size={13} /> Upload Document</>}
              </button>
              {(ocr.status === 'scanning' || ocr.status === 'loading') && (
                <span style={{ fontSize:'0.72rem', color:'var(--text-muted)', whiteSpace:'nowrap' }}>
                  Apply suggestions first, then upload.
                </span>
              )}
            </div>
          </div>
        </Modal>
      )}

      {/* ═══════════════════ RECORD VIEWER MODAL ═══════════════════ */}
      {viewRecordEntry && (() => {
        const matchDocs = findConsultDocs(activeDocs, viewRecordEntry);
        const provider  = viewRecordEntry.provider_name || viewRecordEntry.appointment_type || 'Appointment';
        const apptDate  = viewRecordEntry.appointment_date
          ? new Date(viewRecordEntry.appointment_date).toLocaleDateString('en-US', { month:'short', day:'numeric', year:'numeric' })
          : '';
        return (
          <div style={{ position:'fixed', inset:0, zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center' }}>
            <div style={{ position:'absolute', inset:0, background:'rgba(0,0,0,0.55)' }}
              onClick={() => setViewRecordEntry(null)} />
            <div style={{
              position:'relative', width:'520px', maxWidth:'96vw', maxHeight:'88vh',
              background:'var(--zeus-navy)', border:'1px solid var(--zeus-border)',
              borderRadius:'var(--radius-lg)', boxShadow:'0 16px 48px rgba(0,0,0,0.5)',
              display:'flex', flexDirection:'column', overflow:'hidden',
            }}>
              {/* Header */}
              <div style={{
                padding:'14px 18px', borderBottom:'1px solid var(--zeus-border)',
                display:'flex', alignItems:'center', justifyContent:'space-between',
                background:'rgba(34,197,94,0.06)',
              }}>
                <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                  <CheckCircle size={16} style={{ color:'#22c55e' }} />
                  <div>
                    <div style={{ fontSize:'0.9rem', fontWeight:700, color:'var(--text-primary)' }}>
                      {provider}
                    </div>
                    {apptDate && (
                      <div style={{ fontSize:'0.75rem', color:'var(--text-muted)' }}>
                        {viewRecordEntry.appointment_type || 'Appointment'} · {apptDate}
                      </div>
                    )}
                  </div>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                  <span style={{ fontSize:'0.65rem', fontWeight:700, color:'#22c55e', padding:'2px 8px', background:'rgba(34,197,94,0.12)', borderRadius:'10px' }}>
                    TREATMENT COMPLETE
                  </span>
                  <button onClick={() => setViewRecordEntry(null)}
                    style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)', padding:'4px', display:'flex', alignItems:'center' }}>
                    <X size={16} />
                  </button>
                </div>
              </div>

              {/* Body */}
              <div style={{ padding:'18px', overflowY:'auto', flex:1 }}>
                {matchDocs.length > 0 ? (
                  <>
                    <div style={{ fontSize:'0.78rem', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'10px' }}>
                      Associated Records ({matchDocs.length})
                    </div>
                    <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
                      {matchDocs.map((doc) => (
                        <div key={doc._id || doc.id} style={{
                          display:'flex', alignItems:'center', gap:'12px',
                          padding:'10px 14px', borderRadius:'var(--radius-sm)',
                          background:'var(--zeus-navy-light)', border:'1px solid var(--zeus-border)',
                          cursor:'pointer', transition:'border-color 0.15s',
                        }}
                          onClick={async () => {
                            // Build the serve URL and open directly in new tab
                            // (avoids z-index conflict between viewRecord modal and previewDoc modal)
                            const caseDocId = doc.id || doc._id;
                            const token = localStorage.getItem('zeus_token') || '';
                            if (caseDocId) {
                              const serveUrl = getDirectApiUrl('/cases/' + id + '/documents/' + caseDocId + '/file?token=' + encodeURIComponent(token));
                              window.open(serveUrl, '_blank');
                            } else if (doc.url) {
                              window.open(doc.url, '_blank');
                            }
                          }}
                          onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--zeus-gold)'}
                          onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--zeus-border)'}
                        >
                          <FileText size={18} style={{ color:'var(--zeus-gold)', flexShrink:0 }} />
                          <div style={{ flex:1, minWidth:0 }}>
                            <div style={{ fontSize:'0.85rem', fontWeight:600, color:'var(--text-primary)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                              {doc.name || doc.filename || 'Untitled'}
                            </div>
                            <div style={{ fontSize:'0.72rem', color:'var(--text-muted)', marginTop:'2px', display:'flex', gap:'10px' }}>
                              <span>{(doc.category || 'other').replace(/_/g, ' ')}</span>
                              {doc.size && <span>{(doc.size / 1024).toFixed(1)} KB</span>}
                              {doc.uploaded_at && <span>{new Date(doc.uploaded_at).toLocaleDateString('en-US', { month:'short', day:'numeric', year:'numeric' })}</span>}
                            </div>
                          </div>
                          <Eye size={14} style={{ color:'var(--text-muted)', flexShrink:0 }} />
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div style={{ textAlign:'center', padding:'28px 16px' }}>
                    <FileText size={36} style={{ color:'var(--text-muted)', marginBottom:'10px', opacity:0.5 }} />
                    <div style={{ fontSize:'0.88rem', fontWeight:600, color:'var(--text-primary)', marginBottom:'6px' }}>
                      No matching documents found
                    </div>
                    <div style={{ fontSize:'0.8rem', color:'var(--text-muted)', marginBottom:'16px' }}>
                      Upload a record from the Documents tab and name it to match the provider or appointment type.
                    </div>
                    <button className="btn btn-primary btn-sm"
                      onClick={() => { setViewRecordEntry(null); setActiveTab('documents'); }}>
                      <UploadCloud size={13} /> Go to Documents
                    </button>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div style={{ padding:'12px 18px', borderTop:'1px solid var(--zeus-border)', display:'flex', gap:'8px', justifyContent:'flex-end' }}>
                <button className="btn btn-ghost btn-sm"
                  onClick={() => { setViewRecordEntry(null); setActiveTab('documents'); }}>
                  <FileText size={13} /> All Documents
                </button>
                <button className="btn btn-primary btn-sm"
                  onClick={() => { setViewRecordEntry(null); setShowUpload(true); }}>
                  <UploadCloud size={13} /> Upload New Record
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ═══════════════════ TREATMENT / PROVIDER MODAL ═══════════════════ */}
      {showTreatment && (
        <div style={{ position:'fixed', inset:0, zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <div style={{ position:'absolute', inset:0, background:'rgba(0,0,0,0.6)' }} onClick={() => { setShowTreatment(false); setEditingTreatIdx(null); }} />
          <div style={{
            position:'relative', width:'75%', maxWidth:'1100px', maxHeight:'90vh',
            background:'var(--zeus-navy)', border:'1px solid var(--zeus-border)',
            borderRadius:'var(--radius-lg)', boxShadow:'0 16px 48px rgba(0,0,0,0.5)',
            display:'flex', flexDirection:'column',
          }}>
            {/* Header */}
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'14px 20px', borderBottom:'1px solid var(--zeus-border)', background:'var(--zeus-navy-light)', flexShrink:0 }}>
              <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                <Stethoscope size={16} style={{ color:'var(--zeus-gold)' }} />
                <span style={{ fontSize:'0.92rem', fontWeight:700, color:'var(--text-primary)' }}>
                  Provider / Treatment
                </span>
              </div>
              <button onClick={() => { setShowTreatment(false); setEditingTreatIdx(null); }} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)', padding:'4px' }}><X size={18} /></button>
            </div>

            {/* Body — two columns */}
            <form onSubmit={handleAddTreatment} style={{ flex:1, overflowY:'auto', padding:'20px', display:'grid', gridTemplateColumns:'1fr 1fr', gap:'24px', alignContent:'start' }}>

              {/* ── COLUMN 1: Provider Information + Treatment Dates ── */}
              <div style={{ display:'flex', flexDirection:'column', gap:'14px' }}>

                {/* Provider Information */}
                <div style={{ fontSize:'0.7rem', fontWeight:700, color:'var(--zeus-gold)', textTransform:'uppercase', letterSpacing:'0.07em', paddingBottom:'6px', borderBottom:'1px solid var(--zeus-border)' }}>
                  Provider Information
                </div>

                <div>
                  <label className="form-label" style={{ fontSize:'0.75rem' }}>Provider Name *</label>
                  {editingTreatIdx !== null ? (
                    <div className="form-input" style={{ background:'var(--zeus-navy)', opacity:0.7, cursor:'not-allowed', fontSize:'0.85rem', display:'flex', alignItems:'center', gap:'6px' }}>
                      <Stethoscope size={13} style={{ color:'var(--zeus-gold)', flexShrink:0 }} />
                      {treatForm.provider_name || '—'}
                    </div>
                  ) : (
                  <div ref={treatProvRef} style={{ position:'relative' }}>
                    <div
                      onClick={() => { setTreatProvOpen(true); setTreatProvSearch(''); setTimeout(() => treatProvInputRef.current?.focus(), 50); }}
                      style={{
                        display:'flex', alignItems:'center', gap:'6px',
                        padding:'5px 8px', fontSize:'0.85rem',
                        border:'1px solid var(--zeus-border)', borderRadius:'var(--radius-sm)',
                        background:'var(--zeus-navy-light)', cursor:'pointer', minHeight:'32px',
                      }}
                    >
                      <Search size={13} style={{ color:'var(--text-muted)', flexShrink:0 }} />
                      {treatProvOpen ? (
                        <input
                          ref={treatProvInputRef}
                          style={{ border:'none', outline:'none', background:'transparent', padding:0, fontSize:'0.85rem', flex:1, color:'var(--text-primary)', fontFamily:'inherit' }}
                          placeholder="Search provider network..."
                          value={treatProvSearch}
                          onChange={(e) => setTreatProvSearch(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Escape') setTreatProvOpen(false); }}
                        />
                      ) : (
                        <span style={{ flex:1, color: treatForm.provider_name ? 'var(--text-primary)' : 'var(--text-muted)', fontSize:'0.85rem' }}>
                          {treatForm.provider_name || 'Search or add provider...'}
                        </span>
                      )}
                      <ChevronDown size={14} style={{ color:'var(--text-muted)', flexShrink:0, transform: treatProvOpen ? 'rotate(180deg)' : 'none', transition:'transform 0.2s' }} />
                    </div>

                    {treatProvOpen && (
                      <div style={{
                        position:'absolute', top:'100%', left:0, right:0, marginTop:'2px',
                        background:'var(--zeus-navy)', border:'1px solid var(--zeus-border)',
                        borderRadius:'var(--radius-md)', boxShadow:'0 8px 24px rgba(0,0,0,0.5)',
                        zIndex:100, maxHeight:'220px', overflowY:'auto',
                      }}>
                        {treatProvSearch.trim() && !providers.some(p => p.name?.toLowerCase() === treatProvSearch.trim().toLowerCase()) && (
                          <div
                            onClick={() => { setTreatForm(prev => ({ ...prev, provider_name: treatProvSearch.trim() })); setTreatProvOpen(false); setTreatProvSearch(''); }}
                            style={{ padding:'7px 12px', cursor:'pointer', fontSize:'0.82rem', borderBottom:'1px solid var(--zeus-border)', color:'var(--text-primary)', fontStyle:'italic' }}
                            onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                            onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                          >
                            Use "<strong>{treatProvSearch.trim()}</strong>" as typed
                          </div>
                        )}
                        {providers
                          .filter(p => { const q = treatProvSearch.toLowerCase(); if (!q) return true; return p.name?.toLowerCase().includes(q) || (p.provider_type||'').toLowerCase().includes(q) || (p.specialty||'').toLowerCase().includes(q); })
                          .map(p => (
                            <div key={p._id}
                              onClick={() => {
                                setTreatForm(prev => ({ ...prev, provider_name: p.name, provider_type: p.provider_type || prev.provider_type, phone: p.phone || prev.phone, fax: p.fax || prev.fax, email: p.email || prev.email, address: [p.address, p.city, p.state, p.zip].filter(Boolean).join(', ') || prev.address }));
                                setTreatProvOpen(false); setTreatProvSearch('');
                              }}
                              style={{ padding:'7px 12px', cursor:'pointer', fontSize:'0.82rem', borderBottom:'1px solid rgba(255,255,255,0.03)', background: treatForm.provider_name === p.name ? 'var(--zeus-gold-dim)' : 'transparent' }}
                              onMouseOver={(e) => { if (treatForm.provider_name !== p.name) e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
                              onMouseOut={(e) => { if (treatForm.provider_name !== p.name) e.currentTarget.style.background = 'transparent'; }}
                            >
                              <div style={{ color:'var(--text-primary)', fontWeight: treatForm.provider_name === p.name ? 600 : 400 }}>{p.name}</div>
                              {(p.provider_type || p.specialty || p.phone) && (
                                <div style={{ fontSize:'0.72rem', color:'var(--text-muted)', marginTop:'1px' }}>
                                  {p.provider_type}{p.specialty ? ` · ${p.specialty}` : ''}{p.phone ? ` · ${p.phone}` : ''}
                                </div>
                              )}
                            </div>
                          ))}
                        {providers.length === 0 && !treatProvSearch && (
                          <div style={{ padding:'12px', fontSize:'0.82rem', color:'var(--text-muted)', textAlign:'center' }}>No providers in network yet</div>
                        )}
                      </div>
                    )}
                  </div>
                  )}
                </div>

                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px' }}>
                  <div>
                    <label className="form-label" style={{ fontSize:'0.75rem' }}>Provider Type</label>
                    {editingTreatIdx !== null ? (
                      <div className="form-input" style={{ background:'var(--zeus-navy)', opacity:0.7, cursor:'not-allowed', fontSize:'0.85rem' }}>
                        {treatForm.provider_type || '—'}
                      </div>
                    ) : (
                      <input className="form-input" value={treatForm.provider_type} onChange={(e) => setTreatForm(prev => ({...prev, provider_type: e.target.value}))} placeholder="e.g. Chiropractor" />
                    )}
                  </div>
                  <div>
                    <label className="form-label" style={{ fontSize:'0.75rem' }}>Treating Status</label>
                    <select className="form-select" value={treatForm.treating_status} onChange={(e) => setTreatForm(prev => ({...prev, treating_status: e.target.value}))}>
                      <option value="current">Currently Treating</option>
                      <option value="former">No Longer Treating</option>
                    </select>
                  </div>
                </div>

                {/* Collapsible Provider Contact Details */}
                <div
                  onClick={() => setShowProviderDetails(!showProviderDetails)}
                  style={{
                    display:'flex', alignItems:'center', gap:'6px', cursor:'pointer',
                    padding:'8px 10px', borderRadius:'var(--radius-sm)',
                    background: showProviderDetails ? 'rgba(212,168,83,0.08)' : 'rgba(255,255,255,0.03)',
                    border:'1px solid ' + (showProviderDetails ? 'rgba(212,168,83,0.2)' : 'var(--zeus-border)'),
                    transition:'all 0.2s',
                  }}
                >
                  <Eye size={13} style={{ color:'var(--zeus-gold)' }} />
                  <span style={{ fontSize:'0.78rem', fontWeight:600, color:'var(--zeus-gold)' }}>
                    {showProviderDetails ? 'Hide provider contact info' : 'Click here to see provider full information'}
                  </span>
                  <ChevronDown size={13} style={{ color:'var(--zeus-gold)', marginLeft:'auto', transform: showProviderDetails ? 'rotate(180deg)' : 'none', transition:'transform 0.2s' }} />
                </div>

                {showProviderDetails && (
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px' }}>
                    <div>
                      <label className="form-label" style={{ fontSize:'0.75rem' }}>Phone</label>
                      <input className="form-input" value={treatForm.phone} onChange={(e) => setTreatForm(prev => ({...prev, phone: formatPhoneValue(e.target.value)}))} placeholder="(___) ___-____" />
                    </div>
                    <div>
                      <label className="form-label" style={{ fontSize:'0.75rem' }}>Fax</label>
                      <input className="form-input" value={treatForm.fax} onChange={(e) => setTreatForm(prev => ({...prev, fax: e.target.value}))} placeholder="(___) ___-____" />
                    </div>
                    <div>
                      <label className="form-label" style={{ fontSize:'0.75rem' }}>Email</label>
                      <input className="form-input" value={treatForm.email} onChange={(e) => setTreatForm(prev => ({...prev, email: e.target.value}))} placeholder="provider@clinic.com" />
                    </div>
                    <div>
                      <label className="form-label" style={{ fontSize:'0.75rem' }}>Address</label>
                      <AddressAutocomplete
                        value={treatForm.address}
                        onChange={(v) => setTreatForm(prev => ({ ...prev, address: v }))}
                        onPlaceSelect={({ street, city, state, zip }) => setTreatForm(prev => ({ ...prev, address: [street, city, state, zip].filter(Boolean).join(', ') }))}
                        placeholder="123 Main St, Houston, TX"
                      />
                    </div>
                  </div>
                )}

                {/* Treatment Dates */}
                <div style={{ fontSize:'0.7rem', fontWeight:700, color:'var(--zeus-gold)', textTransform:'uppercase', letterSpacing:'0.07em', paddingBottom:'6px', borderBottom:'1px solid var(--zeus-border)', marginTop:'4px' }}>
                  Treatment Dates
                </div>

                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'10px' }}>
                  <div>
                    <label className="form-label" style={{ fontSize:'0.75rem' }}>First Visit</label>
                    <input className="form-input" type="date" value={treatForm.treatment_start} onChange={(e) => setTreatForm(prev => ({...prev, treatment_start: e.target.value}))} />
                  </div>
                  <div>
                    <label className="form-label" style={{ fontSize:'0.75rem' }}>Last Visit</label>
                    <input className="form-input" type="date" value={treatForm.treatment_end} onChange={(e) => setTreatForm(prev => ({...prev, treatment_end: e.target.value}))} />
                  </div>
                  <div>
                    <label className="form-label" style={{ fontSize:'0.75rem' }}>Next Appointment</label>
                    <input className="form-input" type="date" value={treatForm.next_appointment} onChange={(e) => setTreatForm(prev => ({...prev, next_appointment: e.target.value}))} />
                  </div>
                </div>

                <div>
                  <label className="form-label" style={{ fontSize:'0.75rem' }}>Gap in Treatment Notes</label>
                  <input className="form-input" value={treatForm.gap_notes} onChange={(e) => setTreatForm(prev => ({...prev, gap_notes: e.target.value}))} placeholder="e.g. Client missed 3 weeks due to work schedule" />
                </div>
              </div>

              {/* ── COLUMN 2: Records & Bills + Cost & Notes ── */}
              <div style={{ display:'flex', flexDirection:'column', gap:'14px' }}>

                {/* Records & Bills */}
                <div style={{ fontSize:'0.7rem', fontWeight:700, color:'var(--zeus-gold)', textTransform:'uppercase', letterSpacing:'0.07em', paddingBottom:'6px', borderBottom:'1px solid var(--zeus-border)' }}>
                  Records & Bills
                </div>

                {/* Medical Records */}
                <div style={{ background:'var(--zeus-navy-light)', borderRadius:'var(--radius-sm)', padding:'12px', border:'1px solid var(--zeus-border)' }}>
                  <div style={{ fontSize:'0.72rem', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'10px' }}>Medical Records</div>
                  <div style={{ display:'grid', gridTemplateColumns:'auto 1fr 1fr', gap:'10px', alignItems:'end' }}>
                    <div>
                      <label className="form-label" style={{ fontSize:'0.75rem' }}>Requested?</label>
                      <div style={{ display:'flex', gap:'10px', alignItems:'center', padding:'6px 0' }}>
                        <label style={{ display:'flex', alignItems:'center', gap:'4px', cursor:'pointer', fontSize:'0.85rem' }}><input type="radio" name="records_req" checked={treatForm.records_requested === true} onChange={() => setTreatForm(prev => ({...prev, records_requested: true}))} /> Yes</label>
                        <label style={{ display:'flex', alignItems:'center', gap:'4px', cursor:'pointer', fontSize:'0.85rem' }}><input type="radio" name="records_req" checked={treatForm.records_requested === false} onChange={() => setTreatForm(prev => ({...prev, records_requested: false}))} /> No</label>
                      </div>
                    </div>
                    <div>
                      <label className="form-label" style={{ fontSize:'0.75rem' }}>Date Requested</label>
                      <input className="form-input" type="date" value={treatForm.records_requested_date} onChange={(e) => setTreatForm(prev => ({...prev, records_requested_date: e.target.value}))} disabled={!treatForm.records_requested} style={{ opacity: treatForm.records_requested ? 1 : 0.4 }} />
                    </div>
                    <div>
                      <label className="form-label" style={{ fontSize:'0.75rem' }}>Date Received</label>
                      <input className="form-input" type="date" value={treatForm.records_received_date} onChange={(e) => setTreatForm(prev => ({...prev, records_received_date: e.target.value}))} />
                    </div>
                  </div>
                  <div style={{ marginTop:'10px' }}>
                    <label className="form-label" style={{ fontSize:'0.75rem' }}>Outstanding Records</label>
                    <input className="form-input" value={treatForm.outstanding_records} onChange={(e) => setTreatForm(prev => ({...prev, outstanding_records: e.target.value}))} placeholder="e.g. MRI report still pending" />
                  </div>
                </div>

                {/* Medical Bills */}
                <div style={{ background:'var(--zeus-navy-light)', borderRadius:'var(--radius-sm)', padding:'12px', border:'1px solid var(--zeus-border)' }}>
                  <div style={{ fontSize:'0.72rem', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'10px' }}>Medical Bills</div>
                  <div style={{ display:'grid', gridTemplateColumns:'auto 1fr 1fr', gap:'10px', alignItems:'end' }}>
                    <div>
                      <label className="form-label" style={{ fontSize:'0.75rem' }}>Requested?</label>
                      <div style={{ display:'flex', gap:'10px', alignItems:'center', padding:'6px 0' }}>
                        <label style={{ display:'flex', alignItems:'center', gap:'4px', cursor:'pointer', fontSize:'0.85rem' }}><input type="radio" name="bills_req" checked={treatForm.bills_requested === true} onChange={() => setTreatForm(prev => ({...prev, bills_requested: true}))} /> Yes</label>
                        <label style={{ display:'flex', alignItems:'center', gap:'4px', cursor:'pointer', fontSize:'0.85rem' }}><input type="radio" name="bills_req" checked={treatForm.bills_requested === false} onChange={() => setTreatForm(prev => ({...prev, bills_requested: false}))} /> No</label>
                      </div>
                    </div>
                    <div>
                      <label className="form-label" style={{ fontSize:'0.75rem' }}>Date Requested</label>
                      <input className="form-input" type="date" value={treatForm.bills_requested_date} onChange={(e) => setTreatForm(prev => ({...prev, bills_requested_date: e.target.value}))} disabled={!treatForm.bills_requested} style={{ opacity: treatForm.bills_requested ? 1 : 0.4 }} />
                    </div>
                    <div>
                      <label className="form-label" style={{ fontSize:'0.75rem' }}>Date Received</label>
                      <input className="form-input" type="date" value={treatForm.bills_received_date} onChange={(e) => setTreatForm(prev => ({...prev, bills_received_date: e.target.value}))} />
                    </div>
                  </div>
                  <div style={{ marginTop:'10px' }}>
                    <label className="form-label" style={{ fontSize:'0.75rem' }}>Outstanding Bills</label>
                    <input className="form-input" value={treatForm.outstanding_bills} onChange={(e) => setTreatForm(prev => ({...prev, outstanding_bills: e.target.value}))} placeholder="e.g. Awaiting itemized bill" />
                  </div>
                </div>

                {/* Cost & Notes */}
                <div style={{ fontSize:'0.7rem', fontWeight:700, color:'var(--zeus-gold)', textTransform:'uppercase', letterSpacing:'0.07em', paddingBottom:'6px', borderBottom:'1px solid var(--zeus-border)', marginTop:'4px' }}>
                  Cost & Notes
                </div>

                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px' }}>
                  <div>
                    <label className="form-label" style={{ fontSize:'0.75rem' }}>Cost</label>
                    <input className="form-input" type="number" step="0.01" value={treatForm.cost} onChange={(e) => setTreatForm(prev => ({...prev, cost: e.target.value}))} placeholder="0.00" />
                  </div>
                  <div>
                    <label className="form-label" style={{ fontSize:'0.75rem' }}>Treatment Description</label>
                    <input className="form-input" value={treatForm.description} onChange={(e) => setTreatForm(prev => ({...prev, description: e.target.value}))} placeholder="e.g. Chiropractic adjustments, 3x weekly" />
                  </div>
                </div>
                <div>
                  <label className="form-label" style={{ fontSize:'0.75rem' }}>Notes</label>
                  <textarea className="form-textarea" style={{ minHeight:'80px' }} value={treatForm.notes} onChange={(e) => setTreatForm(prev => ({...prev, notes: e.target.value}))} placeholder="Additional notes about this provider or treatment..." />
                </div>
              </div>

            </form>

            {/* Footer */}
            <div style={{ display:'flex', justifyContent:'flex-end', gap:'10px', padding:'12px 20px', borderTop:'1px solid var(--zeus-border)', background:'var(--zeus-navy-light)', flexShrink:0 }}>
              <button className="btn btn-ghost" onClick={() => { setShowTreatment(false); setEditingTreatIdx(null); }} style={{ padding:'6px 16px', fontSize:'0.82rem' }}>Cancel</button>
              <button className="btn btn-primary" onClick={handleAddTreatment} style={{ padding:'6px 20px', fontSize:'0.82rem', display:'flex', alignItems:'center', gap:'6px' }}>
                <Save size={14} /> {editingTreatIdx !== null ? 'Save Changes' : 'Add Provider'}
              </button>
            </div>
          </div>
        </div>
      )}



      {/* Delete Confirmation Modal */}
      <Modal isOpen={showDeleteConfirm} onClose={() => { setShowDeleteConfirm(false); setDeleteConfirm(''); }} title="Delete Case">
        <div style={{ marginBottom:'16px' }}>
          {/* Case info card */}
          <div style={{
            padding:'12px', background:'var(--zeus-navy-light)', borderRadius:'var(--radius-md)',
            border:'1px solid var(--zeus-border)', marginBottom:'14px',
          }}>
            <div style={{ fontWeight:600, color:'var(--zeus-gold)', fontSize:'0.9rem' }}>{c?.case_number || '—'}</div>
            <div style={{ fontSize:'0.85rem', color:'var(--text-secondary)' }}>
              {form.name || c?.client_name || 'Unnamed'} · {form.claim_type || '—'}
            </div>
          </div>

          {isSuperAdmin ? (
            <>
              <p style={{ fontSize:'0.85rem', color:'var(--status-red)', marginBottom:10, fontWeight:600 }}>
                 This will permanently delete the case and all associated records. This cannot be undone.
              </p>
              <label style={{ fontSize:'0.82rem', color:'var(--text-muted)', display:'block', marginBottom:4 }}>
                Type the client name to confirm:{' '}
                <strong style={{ color:'var(--text-primary)' }}>
                  {form.name || c?.client_name || c?.case_number}
                </strong>
              </label>
              <input
                className="form-input" autoFocus
                placeholder={form.name || c?.client_name || c?.case_number}
                value={deleteConfirm}
                onChange={e => setDeleteConfirm(e.target.value)}
                style={{
                  marginBottom:10,
                  borderColor: deleteConfirm && deleteConfirm.toLowerCase() !== (form.name || c?.client_name || c?.case_number || '').toLowerCase()
                    ? 'var(--status-red)' : undefined,
                }}
              />
              <label style={{ fontSize:'0.82rem', color:'var(--text-muted)', display:'block', marginBottom:4 }}>Reason (optional)</label>
              <textarea className="form-textarea" style={{ minHeight:'60px', fontSize:'0.85rem' }}
                placeholder="Reason for deletion..." value={deleteReason} onChange={e => setDeleteReason(e.target.value)} />
            </>
          ) : isAdmin ? (
            <>
              <p style={{ fontSize:'0.85rem', color:'var(--text-secondary)', marginBottom:10 }}>
                This will send a <strong style={{ color:'var(--zeus-gold)' }}>deletion request</strong> to a Super Admin for approval.
              </p>
              <label style={{ fontSize:'0.82rem', color:'var(--text-muted)', display:'block', marginBottom:4 }}>
                Reason <span style={{ color:'var(--status-red)' }}>*</span>
              </label>
              <textarea className="form-textarea" style={{ minHeight:'70px', fontSize:'0.85rem' }} autoFocus
                placeholder="Enter reason for deleting this case..." value={deleteReason} onChange={e => setDeleteReason(e.target.value)} />
            </>
          ) : (
            <p style={{ fontSize:'0.88rem', color:'var(--text-muted)', textAlign:'center', padding:'8px 0' }}>
              Only Super Admins and Admins can delete or request deletion of cases.
            </p>
          )}
        </div>

        <div style={{ display:'flex', gap:'8px', justifyContent:'flex-end' }}>
          <button className="btn btn-ghost btn-sm" onClick={() => setShowDeleteConfirm(false)}>Cancel</button>
          <button
            className="btn btn-sm"
            disabled={
              deleting ||
              (isSuperAdmin && deleteConfirm.trim().toLowerCase() !== (form.name || c?.client_name || c?.case_number || '').toLowerCase()) ||
              (!isSuperAdmin && isAdmin && !deleteReason.trim())
            }
            onClick={handleDeleteCase}
            style={{
              background:'var(--status-red)', color:'#fff', border:'none',
              opacity: (
                deleting ||
                (isSuperAdmin && deleteConfirm.trim().toLowerCase() !== (form.name || c?.client_name || c?.case_number || '').toLowerCase()) ||
                (!isSuperAdmin && isAdmin && !deleteReason.trim())
              ) ? 0.5 : 1,
            }}
          >
            <Trash2 size={14} /> {deleting ? 'Processing...' : isSuperAdmin ? 'Delete Permanently' : isAdmin ? 'Submit Request' : 'Not Allowed'}
          </button>
        </div>
      </Modal>

      <AddProviderModal
        isOpen={showQuickProvider}
        onClose={() => { setShowQuickProvider(false); setQuickProviderForm({ name:'', provider_type:'', phone:'', fax:'', email:'', address:'', city:'', state:'', zip:'' }); }}
        initialName={quickProviderForm.name}
        providerTypes={customLists.provider_type_options || ['Doctor','Specialist','Imaging','Pharmacy','Therapy','Other']}
        specialties={customLists.specialty_options || []}
        onCreated={(newP) => {
          setAllProviders(prev => [...prev, newP]);
          // Apply to whichever field triggered the modal
          if (quickProviderContext === 'referred_by') {
            setForm(prev => ({ ...prev, referred_by: newP.name }));
          } else if (quickProviderContext === 'treatment') {
            setC(prev => ({
              ...prev,
              treatments: [...(prev.treatments || []), {
                provider_name: newP.name, provider_type: newP.provider_type || '',
                phone: newP.phone || '', fax: newP.fax || '',
                email: newP.email || '', address: [newP.address, newP.city, newP.state, newP.zip].filter(Boolean).join(', '),
                treating_status: 'current', _id: `new_${Date.now()}`,
              }],
            }));
          }
          setShowQuickProvider(false);
        }}
      />

    </div>
      {/* ── Confirm Modal (reusable) ── */}
      <ConfirmModal
        isOpen={!!confirmState}
        onClose={() => setConfirmState(null)}
        onConfirm={confirmState?.onConfirm}
        title={confirmState?.title || 'Confirm'}
        message={confirmState?.message}
        detail={confirmState?.detail}
        confirmLabel={confirmState?.label || 'Delete'}
      />

      {/* ── Unsaved Changes Banner ── */}
      {isDirty && (
        <div className="unsaved-banner" style={{
          position:'fixed', bottom:0, left:0, right:0, zIndex:9990,
          background:'var(--zeus-navy-light)',
          borderTop:'2px solid var(--zeus-gold)',
          padding:'12px 24px',
          display:'flex', alignItems:'center', justifyContent:'space-between',
          boxShadow:'0 -4px 20px rgba(0,0,0,0.35)',
        }}>
          <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
            <span style={{
              width:8, height:8, borderRadius:'50%',
              background:'var(--zeus-gold)', display:'inline-block', flexShrink:0,
            }} />
            <span style={{ fontSize:'0.88rem', fontWeight:600, color:'var(--text-primary)' }}>
              You have unsaved changes
            </span>
          </div>
          <div className="unsaved-banner-actions" style={{ display:'flex', gap:'10px' }}>
            <button
              className="btn btn-ghost btn-sm"
              onClick={handleDiscard}
              style={{ padding:'7px 18px', fontSize:'0.85rem' }}
            >
              Discard
            </button>
            <button
              className="btn btn-primary btn-sm"
              onClick={handleSave}
              disabled={saving}
              style={{ padding:'7px 22px', fontSize:'0.85rem', fontWeight:700 }}
            >
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
